import React, { useState, useEffect, useRef, useMemo } from 'react';
import html2canvas from 'html2canvas';
import { gradingMachine } from '../utils/gradingMachine';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, 
  faUpload, 
  faDownload, 
  faEdit, 
  faTrash, 
  faImage,
  faSpinner,
  faPrint,
  faEye,
  faTimes,
  faChevronLeft,
  faChevronRight,
  faExclamationTriangle,
  faSearch,
  faTag,
  faList,
  faClock,
  faTableCells,
  faComments,
  faAlignLeft,
  faRotateRight,
  faPaperclip,
  faPaste,
  faMicrophone,
  faStop,
  faSort,
  faSortUp,
  faSortDown,
  faCamera,
  faCommentDots
} from '@fortawesome/free-solid-svg-icons';
import { Document, Packer, Paragraph, TextRun, BorderStyle, LineRuleType, Table, TableRow, TableCell, WidthType, TableLayoutType, TabStopType } from 'docx';
import { saveAs } from 'file-saver';
import './ScansTab.css';
import ConfirmDialog from './ConfirmDialog';
import AnnotatableImage from './AnnotatableImage';
import ZoomableImage from './ZoomableImage';
import { folderDisplayTitle } from '../utils/folderDisplay';
import { stripQuestionPaperText, restorePaperNotes } from '../utils/questionPaper';
import { tableToOneLineComments, splitRawMarkdownCells, splitMarkdownRowCells, normalizeQuestionId } from '../utils/tableToOneLine';
import { findQuestionSpan, findTranscriptSpan, rowDeduction, transcriptPageIndex, revealTextareaSpan, MarkedTextarea } from '../utils/questionFocus';
import { compareStudentsByRoll } from '../utils/rollOrder';

const API_BASE_URL = window.API_BASE_URL;

// ── Saved-results comment display helpers ─────────────────────────────────
// A saved checked-result comment usually carries the markdown Verification
// Table (| Question | Marks | Deduction | ... |) plus a trailing
// "[Image_x.jpg] [N sheets]" footer. These helpers split/re-render it for the
// on-screen viewers ONLY — the stored comment (and everything the Preview /
// Print to DOCX flow reads) is untouched.
function splitSavedComment(raw) {
  const lines = String(raw || '').split('\n');
  const tableLines = [];
  const otherLines = [];
  for (const line of lines) {
    if (line.trim().startsWith('|')) tableLines.push(line.trim());
    else otherLines.push(line);
  }
  return {
    tableMd: tableLines.length >= 2 ? tableLines.join('\n') : '',
    afterText: otherLines.join('\n').replace(/\n{3,}/g, '\n\n').trim()
  };
}

// Image filenames referenced by a saved comment's footer — the finalize flow
// appends "[Image_18456.jpg] [1 sheet]" (or "[a.jpg, b.jpg] [2 sheets]") to
// every saved comment, so the images can be recovered from the text alone.
function imageNamesFromComment(raw) {
  const matches = String(raw || '').match(/\[([^\][]*\.(?:jpe?g|png|gif|webp)[^\][]*)\]/gi) || [];
  const names = [];
  for (const m of matches) {
    m.slice(1, -1).split(',').forEach(n => {
      const t = n.trim();
      if (t && !names.includes(t)) names.push(t);
    });
  }
  return names;
}

// Parse "[N sheets]" (or "( N sheet )") from comment text.
function sheetCountFromText(raw) {
  const s = String(raw || '');
  const m = s.match(/\[\s*(\d+)\s*sheets?\s*\]/i) || s.match(/\(\s*(\d+)\s*sheets?\s*\)/i);
  return m ? parseInt(m[1], 10) : null;
}

// Rewrite a wrong "[1 sheet]" footer using the real count (e.g. from Evaluate).
function rewriteSheetCountInText(raw, count) {
  if (!raw || !count || count < 1) return raw;
  const label = `${count} sheet${count === 1 ? '' : 's'}`;
  return String(raw)
    .replace(/\[\s*\d+\s*sheets?\s*\]/gi, `[${label}]`)
    .replace(/\(\s*\d+\s*sheets?\s*\)/gi, `( ${label} )`);
}

// Increment the trailing number in a filename (Image_18456.jpg →
// Image_18457.jpg) to find the BACK side of a double-sided scan. Returns
// null when the name carries no trailing number.
function nextImageFilename(name) {
  const m = String(name).match(/^(.*?)(\d+)(\.[^.]+)?$/);
  if (!m) return null;
  const nextNum = String(parseInt(m[2], 10) + 1).padStart(m[2].length, '0');
  return `${m[1]}${nextNum}${m[3] || ''}`;
}

// One-line view of a saved comment — same conversion the Check tab uses
// (tableToOneLineComments). Falls back to the raw text when there's no
// parseable table (hand-edited comments, absent rows, etc.).
function savedCommentOneLine(raw) {
  const { tableMd, afterText } = splitSavedComment(raw);
  if (tableMd) {
    try {
      const line = tableToOneLineComments({ tableMd, studentName: '', existingComments: '' });
      if (line) return afterText ? `${line} ${afterText}` : line;
    } catch (_) { /* fall through to raw text */ }
  }
  return String(raw || '').trim();
}

// Render a markdown deduction table as a real <table>. Copied from the Check
// tab's renderMarkdownTable so both viewers look identical, including the
// short display-only header labels (Qs / T.M / Mistakes / -ve / Obt.M).
function renderSavedMarkdownTable(md, opts = {}) {
  const text = String(md || '').trim();
  if (!text || !text.includes('|')) return null;
  const lines = text.split('\n').map(l => l.trim()).filter(l => l.startsWith('|'));
  if (lines.length < 2) return null;
  const isSep = (line) => /^\|\s*:?-+:?\s*(\|\s*:?-+:?\s*)+\|?\s*$/.test(line);
  let header = null;
  const bodyRows = [];
  for (const line of lines) {
    if (isSep(line)) continue;
    if (!header) { header = splitRawMarkdownCells(line); continue; }
    bodyRows.push(splitMarkdownRowCells(line, header.length));
  }
  if (!header) return null;
  const isBoldRow = (cells) => cells.some(c => /^\*\*.+\*\*$/.test(c));
  const stripBold = (s) => String(s || '').replace(/\*\*/g, '').trim();
  const HEADER_LABELS = {
    'question': 'Qs',
    'marks': 'T.M',
    'deduction': 'Mistakes',
    'deduction marks': '-ve',
    'obtained': 'Obt.M',
    'obtained marks': 'Obt.M'
  };
  const renameHeader = (h) => {
    const plain = stripBold(h);
    const key = plain.toLowerCase().replace(/\s+/g, ' ').trim();
    return HEADER_LABELS[key] || plain;
  };
  return (
    <table style={{
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: 13,
      fontFamily: 'inherit',
      border: '1px solid var(--border-color, #e2e8f0)'
    }}>
      <thead>
        <tr style={{ background: 'var(--surface-alt, #f1f5f9)' }}>
          {header.map((h, i) => (
            <th key={i} style={{
              padding: '8px 10px',
              textAlign: 'left',
              fontWeight: 700,
              color: 'var(--text-primary, #0f172a)',
              borderBottom: '2px solid var(--border-color, #cbd5e1)'
            }}>{renameHeader(h)}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {bodyRows.map((cells, ri) => {
          const bold = isBoldRow(cells);
          const rowLabel = stripBold(cells[0]);
          const rowQuestionId = /total/i.test(rowLabel) ? null : normalizeQuestionId(rowLabel);
          const linked = !!(rowQuestionId && opts.linkedId && rowQuestionId === opts.linkedId);
          return (
            <tr key={ri} style={{
              background: linked
                ? 'rgba(245, 158, 11, 0.28)'
                : bold ? 'rgba(139, 92, 246, 0.08)' : 'transparent',
              fontWeight: bold ? 700 : 400,
              outline: linked ? '2px solid #d97706' : 'none'
            }}>
              {cells.map((c, ci) => (
                <td key={ci} style={{
                  padding: '6px 10px',
                  borderBottom: '1px solid var(--border-color, #e2e8f0)',
                  color: 'var(--text-secondary, #334155)',
                  verticalAlign: 'top',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}>
                  {ci === 0 && rowQuestionId && opts.onPick ? (
                    <button
                      type="button"
                      onClick={() => opts.onPick(rowQuestionId, rowDeduction(header, cells))}
                      title="Highlight this question in the transcript and the rubric"
                      style={{
                        background: linked ? '#fde68a' : 'transparent',
                        border: linked ? '1px solid #d97706' : '1px solid transparent',
                        borderRadius: 6,
                        padding: '1px 6px',
                        font: 'inherit',
                        fontWeight: 700,
                        color: 'inherit',
                        cursor: 'pointer'
                      }}
                    >
                      {stripBold(c)}
                    </button>
                  ) : stripBold(c)}
                </td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// Lazy-loading thumbnail for one scan. Only when the tile scrolls into view does
// it fetch that image's presigned S3 URL (so a folder of 30+ scans doesn't fire
// 30+ requests at once) and render it. Shows a spinner while loading and a
// generic image icon if the fetch/render fails.
function ScanThumbnail({ scan }) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let cancelled = false;
    const io = new IntersectionObserver((entries) => {
      if (!entries[0] || !entries[0].isIntersecting) return;
      io.disconnect(); // load once
      (async () => {
        try {
          const token = localStorage.getItem('authToken');
          const res = await fetch(`${API_BASE_URL}/scans/file/${scan.id}/download`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const data = await res.json().catch(() => ({}));
          if (cancelled) return;
          if (res.ok && data.downloadUrl) setUrl(data.downloadUrl);
          else setFailed(true);
        } catch (_) { if (!cancelled) setFailed(true); }
      })();
    }, { rootMargin: '300px' });
    io.observe(el);
    return () => { cancelled = true; io.disconnect(); };
  }, [scan.id]);
  return (
    <div ref={ref} style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--panel-bg, #f3f4f6)', overflow: 'hidden' }}>
      {url ? (
        <img
          src={url}
          alt={scan.file_name}
          loading="lazy"
          onError={() => { setUrl(null); setFailed(true); }}
          style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top', display: 'block' }}
        />
      ) : (
        <FontAwesomeIcon
          icon={failed ? faImage : faSpinner}
          spin={!failed}
          style={{ fontSize: 22, color: 'var(--text-muted, #9ca3af)' }}
        />
      )}
    </div>
  );
}

// A4 full-size at 96 CSS px/inch, then scaled for display
const A4_FULL_W = 794;   // 210mm ≈ 8.27" × 96
const A4_FULL_H = 1123;  // 297mm ≈ 11.69" × 96
const A4_MARGIN_PX = 19; // 0.2 inch × 96 (matches DOCX 288 twips)
// Usable preview height = full A4 minus margins, calibrated to Word's TRUE
// text area (Word uses 288-twip ≈ 19px margins top+bottom). With the line
// spacing + per-student gaps now shared between preview and export, the
// preview's "Exceeds A4" warning should fire at the SAME point Word actually
// breaks a page — not ~20px early as the old 0.982 safety trim did.
// Tuning: if the preview STILL over-warns while Word fits, nudge
// OVERFLOW_FUDGE up a hair (e.g. 1.01); if .docx output starts spilling off
// the page, nudge it down (e.g. 0.99).
const OVERFLOW_FUDGE = 1.0;
const A4_CONTENT_H = Math.floor((A4_FULL_H - A4_MARGIN_PX * 2) * OVERFLOW_FUDGE);
// Printable width inside the same margins (794 − 38 = 756). Offscreen
// measurement uses this so wrapping matches the preview page.
const A4_CONTENT_W = A4_FULL_W - A4_MARGIN_PX * 2;
const A4_SCALE = 0.8;
const A4_DISP_W = Math.round(A4_FULL_W * A4_SCALE);
const A4_DISP_H = Math.round(A4_FULL_H * A4_SCALE);
// Single source of truth for line height, shared by BOTH the HTML preview
// (CSS lineHeight = sizePt × this) and the DOCX export (Word line spacing =
// sizePt × this × 20 twips). Keeping one constant guarantees a Word row is
// exactly as tall as a preview row, so the two layouts stay in lockstep.
const PREVIEW_LINE_MULT = 17.2 / 12;

function printStudentId(sc) {
  return String(sc?.imageList || sc?.image_no || sc?.unique_code || sc?.student_name || sc?.name || '');
}

// Print-header stamp for when this student was last graded or approved.
// Karachi clock, like Last checked / Last approved on Sort Papers.
// Shape: (Oct 23, 14:15)
function formatGradedStamp(value) {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const datePart = d.toLocaleDateString('en-US', {
    timeZone: 'Asia/Karachi',
    month: 'short',
    day: 'numeric'
  });
  const timePart = d.toLocaleTimeString('en-GB', {
    timeZone: 'Asia/Karachi',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
  return `(${datePart}, ${timePart})`;
}

function latestTimeMs(...values) {
  let best = 0;
  for (const value of values) {
    if (!value) continue;
    const d = value instanceof Date ? value : new Date(value);
    const ms = d.getTime();
    if (Number.isFinite(ms) && ms > best) best = ms;
  }
  return best || 0;
}

// A later sitting, not a pause inside the same class run.
const LATER_GRADE_GAP_MS = 6 * 60 * 60 * 1000;

function parseRegradeRuns(raw) {
  let value = raw;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];
    try { value = JSON.parse(trimmed); } catch { return []; }
  }
  return Array.isArray(value) ? value : [];
}

function gradeNameKeys(value) {
  const text = String(value || '').replace(/\s+/g, ' ').trim().toLowerCase();
  const keys = new Set();
  if (text) keys.add(text);
  const roll = text.match(/\(([^)]+)\)/);
  if (roll && roll[1].trim()) keys.add(roll[1].trim());
  return keys;
}

function rowGradeTimes(row) {
  const times = [];
  const created = latestTimeMs(row && row.created_at);
  if (created) times.push(created);
  let history = row && row.table_history;
  if (typeof history === 'string') {
    try { history = JSON.parse(history); } catch { history = []; }
  }
  if (Array.isArray(history)) {
    for (const run of history) {
      const at = latestTimeMs(run && run.at);
      if (at) times.push(at);
    }
  }
  return times;
}

// Students whose marks were written after the class batch: a gap of at least
// six hours in the first-grade times, or a later regrade that names a subset
// of the class. A regrade entry that lists almost everyone is the whole-class
// auto pass and is left out.
function laterGradedStudents(roster, rows, regradeRuns) {
  const byName = new Map();
  for (const row of rows || []) {
    const key = String(row.student_name || '').replace(/\s+/g, ' ').trim().toLowerCase();
    if (!key) continue;
    if (!byName.has(key)) byName.set(key, []);
    byName.get(key).push(row);
  }
  const timed = (roster || []).map((student) => {
    const key = String(student.student_name || '').replace(/\s+/g, ' ').trim().toLowerCase();
    const times = (byName.get(key) || []).flatMap(rowGradeTimes);
    return {
      student,
      first: times.length ? Math.min(...times) : 0,
      last: times.length ? Math.max(...times) : 0
    };
  });
  const ordered = timed.filter((item) => item.first).sort((a, b) => a.first - b.first);
  let boundary = 0;
  for (let i = 1; i < ordered.length; i += 1) {
    const gap = ordered[i].first - ordered[i - 1].first;
    if (gap >= LATER_GRADE_GAP_MS) boundary = ordered[i].first;
  }
  const later = new Set();
  if (boundary) {
    for (const item of timed) {
      if (item.first >= boundary || (item.last && item.last >= boundary)) {
        later.add(String(item.student.id));
      }
    }
  }
  const runs = parseRegradeRuns(regradeRuns);
  const classSize = (roster || []).length;
  for (const run of runs) {
    const names = Array.isArray(run && run.students) ? run.students : [];
    if (!names.length || !classSize || names.length >= Math.ceil(classSize * 0.8)) continue;
    const wanted = new Set();
    for (const name of names) {
      for (const key of gradeNameKeys(name)) wanted.add(key);
    }
    for (const student of roster) {
      for (const key of gradeNameKeys(student.student_name)) {
        if (wanted.has(key)) later.add(String(student.id));
      }
    }
  }
  return (roster || []).filter((student) => later.has(String(student.id)));
}

// Vertical space of the preview's between-student <hr> (margin 4px + 1px rule).
// Margins are outside offsetHeight, so this measures a host that sandwiches the
// rule between two fixed bars and subtracts those bars.
function measureDividerPx() {
  const host = document.createElement('div');
  host.style.cssText = 'position:fixed;left:-10000px;top:0;width:' + A4_CONTENT_W + 'px;visibility:hidden;';
  const bar = () => {
    const d = document.createElement('div');
    d.style.height = '10px';
    return d;
  };
  const hr = document.createElement('hr');
  hr.style.border = 'none';
  hr.style.borderBottom = '1px solid #000';
  hr.style.margin = '4px 0';
  host.append(bar(), hr, bar());
  document.body.appendChild(host);
  try {
    const h = host.offsetHeight - 20;
    return h > 0 ? h : 9;
  } finally {
    host.remove();
  }
}

function measureFaceNode(sc, side) {
  const wrap = document.createElement('div');
  wrap.style.direction = sc.hasUrdu ? 'rtl' : 'ltr';
  if (side === 'front') {
    const header = document.createElement('div');
    header.style.cssText = 'font-weight:700;margin-bottom:2px;display:flex;justify-content:space-between;align-items:baseline;gap:12px;';
    const left = document.createElement('span');
    left.style.cssText = 'min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;';
    left.textContent = sc.headerLeft || '';
    header.appendChild(left);
    if (sc.headerRight) {
      const right = document.createElement('span');
      right.style.flexShrink = '0';
      right.textContent = sc.headerRight;
      header.appendChild(right);
    }
    wrap.appendChild(header);
  }
  if (sc.table) {
    if (side === 'front' && sc.table.before) {
      const before = document.createElement('div');
      before.style.cssText = 'white-space:pre-wrap;margin-bottom:4px;';
      before.textContent = sc.table.before;
      wrap.appendChild(before);
    }
    const sideTable = side === 'front' ? sc.tableFront : sc.tableBack;
    if (sideTable && sideTable.rows && sideTable.rows.length > 0) {
      const table = document.createElement('table');
      table.style.cssText = 'border-collapse:collapse;table-layout:auto;width:100%;font-size:inherit;line-height:inherit;margin-bottom:0;';
      const tbody = document.createElement('tbody');
      sideTable.rows.forEach((row) => {
        const tr = document.createElement('tr');
        row.forEach((cell, ci) => {
          const td = document.createElement('td');
          const isNarrow = ci === 0 || (row.length === 3 && ci === 2);
          td.style.border = '1px solid #000';
          td.style.padding = '2px 6px';
          td.style.fontSize = 'inherit';
          td.style.lineHeight = 'inherit';
          td.style.letterSpacing = 'normal';
          td.style.fontWeight = cell.bold ? '700' : '400';
          td.style.verticalAlign = 'top';
          td.style.whiteSpace = isNarrow ? 'nowrap' : 'normal';
          td.textContent = cell?.value ?? '';
          tr.appendChild(td);
        });
        tbody.appendChild(tr);
      });
      table.appendChild(tbody);
      wrap.appendChild(table);
    }
  } else {
    const text = document.createElement('div');
    text.style.whiteSpace = 'pre-wrap';
    text.textContent = side === 'front' ? (sc.firstHalf || '') : (sc.secondHalf || '');
    wrap.appendChild(text);
  }
  return wrap;
}

function measureColumn(sizePt) {
  const col = document.createElement('div');
  col.style.width = A4_CONTENT_W + 'px';
  col.style.boxSizing = 'border-box';
  col.style.fontFamily = "'Jameel Noori Nastaleeq', serif";
  col.style.fontSize = sizePt + 'pt';
  col.style.lineHeight = (sizePt * PREVIEW_LINE_MULT) + 'pt';
  col.style.color = '#000';
  return col;
}

// Heights of each student's aligned face at one front/back font pair.
// slot = max(front block, back block), which is what both faces occupy once
// the shorter side is padded. Gaps are NOT included; the packer adds them.
function measureFaceHeights(students, frontPt, backPt) {
  if (!students.length) return [];
  const host = document.createElement('div');
  host.setAttribute('data-pack-measure', '');
  host.style.cssText = 'position:fixed;left:-10000px;top:0;visibility:hidden;pointer-events:none;';
  const frontCol = measureColumn(frontPt);
  const backCol = measureColumn(backPt);
  const frontNodes = [];
  const backNodes = [];
  students.forEach((sc) => {
    const front = measureFaceNode(sc, 'front');
    const back = measureFaceNode(sc, 'back');
    frontCol.appendChild(front);
    backCol.appendChild(back);
    frontNodes.push(front);
    backNodes.push(back);
  });
  host.append(frontCol, backCol);
  document.body.appendChild(host);
  try {
    return students.map((_, i) => Math.max(frontNodes[i].offsetHeight, backNodes[i].offsetHeight));
  } finally {
    host.remove();
  }
}

// How many leading students fit on one sheet. The divider sits BETWEEN
// students, so it is added for every student after the first and never after
// the last. A student who alone exceeds A4 still occupies the sheet.
function countThatFit(slotHeights, frontGaps, backGaps, dividerH) {
  let frontUsed = 0;
  let backUsed = 0;
  let count = 0;
  for (let i = 0; i < slotHeights.length; i++) {
    const divider = count > 0 ? dividerH : 0;
    const nextFront = frontUsed + divider + slotHeights[i] + frontGaps[i];
    const nextBack = backUsed + divider + slotHeights[i] + backGaps[i];
    if (count > 0 && (nextFront > A4_CONTENT_H || nextBack > A4_CONTENT_H)) break;
    frontUsed = nextFront;
    backUsed = nextBack;
    count += 1;
  }
  return count;
}

function groupBySizes(students, sizes) {
  if (!students || !students.length || !sizes || !sizes.length) return [];
  const groups = [];
  let pointer = 0;
  for (let i = 0; i < sizes.length && pointer < students.length; i++) {
    const n = Math.max(1, sizes[i] || 1);
    groups.push(students.slice(pointer, Math.min(students.length, pointer + n)));
    pointer += n;
  }
  while (pointer < students.length) {
    groups.push(students.slice(pointer, pointer + 1));
    pointer += 1;
  }
  return groups;
}

// Pack `students` onto sheets. `prefixSizes` freezes earlier sheets (their
// students stay put). From there, a `locks[sheet]` count wins; every other
// sheet takes as many measured students as both faces can hold.
async function computeLayoutSizes({
  students,
  prefixSizes,
  locks,
  frontPtOf,
  backPtOf,
  frontGapOf,
  backGapOf,
}) {
  if (document.fonts?.ready) await document.fonts.ready;
  const dividerH = measureDividerPx();
  const sizes = [];
  let index = 0;
  const prefix = Array.isArray(prefixSizes) ? prefixSizes : [];
  for (let i = 0; i < prefix.length && index < students.length; i++) {
    const take = Math.max(1, Math.min(prefix[i] || 1, students.length - index));
    sizes.push(take);
    index += take;
  }
  const heightCache = new Map();
  let sheet = sizes.length;
  while (index < students.length && sheet <= students.length + 1) {
    const rawLock = locks ? locks[sheet] : undefined;
    const lockN = rawLock == null || rawLock === '' ? NaN : parseInt(rawLock, 10);
    if (Number.isFinite(lockN) && lockN >= 1) {
      const take = Math.min(lockN, students.length - index);
      sizes.push(take);
      index += take;
      sheet += 1;
      continue;
    }
    const frontPt = frontPtOf(sheet);
    const backPt = backPtOf(sheet);
    const cacheKey = frontPt + '|' + backPt;
    let heights = heightCache.get(cacheKey);
    if (!heights) {
      heights = measureFaceHeights(students, frontPt, backPt);
      heightCache.set(cacheKey, heights);
    }
    const tailH = heights.slice(index);
    const frontGaps = [];
    const backGaps = [];
    for (let i = index; i < students.length; i++) {
      frontGaps.push(frontGapOf(students[i], sheet));
      backGaps.push(backGapOf(students[i], sheet));
    }
    const fitted = countThatFit(tailH, frontGaps, backGaps, dividerH);
    const take = Math.max(1, Math.min(fitted || 1, students.length - index));
    sizes.push(take);
    index += take;
    sheet += 1;
  }
  return sizes;
}

// Print the preview pages directly (no DOCX). Clones each full-size page's
// content into a hidden iframe at true A4 scale (undoing the 50% preview
// shrink) and invokes the browser print dialog — which also offers "Save as
// PDF". WYSIWYG: it prints the exact HTML shown in the preview, including any
// inline edits the user typed into the cells.
function printPreviewPages() {
  const nodes = Array.from(document.querySelectorAll('[data-print-page]'));
  if (!nodes.length) return;
  const sheets = nodes.map((n) => {
    const c = n.cloneNode(true);
    c.style.transform = 'none';        // undo the 50% preview scale
    c.style.width = A4_FULL_W + 'px';  // true A4 width (210mm @ 96dpi)
    // Pin each sheet a few px UNDER A4 (matches the preview's fixed A4 box) and
    // clip overflow. If a full page's content + bottom margin reached the exact
    // A4 height, a 100%-scale print would spill a sliver onto a blank 2nd page.
    // The clipped px are only the bottom margin, not content (content fits well
    // within A4_CONTENT_H = sheet − top margin).
    c.style.height = (A4_FULL_H - 8) + 'px';
    c.style.overflow = 'hidden';
    c.style.margin = '0';
    c.style.boxShadow = 'none';
    c.setAttribute('data-print-sheet', '');
    c.querySelectorAll('[contenteditable]').forEach((el) => el.removeAttribute('contenteditable'));
    c.querySelectorAll('[data-no-print]').forEach((el) => el.remove());
    return c.outerHTML;
  }).join('');
  const html = '<!doctype html><html><head><meta charset="utf-8"><title>FastGrader — Print</title>'
    + '<style>'
    + '@page { size: A4; margin: 0; }'
    // Replicate the app's global reset (App.css `*`) so the iframe renders the
    // SAME as the preview: border-box (text wraps at the same width) AND the
    // same base sans-serif font (the preview's cells are sans-serif via this
    // rule). Without it the iframe was content-box + a different font, so cells
    // wrapped to fewer lines and the page printed shorter than the preview.
    + "* { box-sizing: border-box; margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Inter', 'Roboto', 'Oxygen', 'Ubuntu', sans-serif; }"
    + 'html, body { margin: 0; padding: 0; }'
    + "body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }"
    + '[data-print-sheet] { background: #fff; page-break-after: always; break-after: page; }'
    + '[data-print-sheet]:last-child { page-break-after: auto; break-after: auto; }'
    + 'table { border-collapse: collapse; }'
    + '</style></head><body>' + sheets + '</body></html>';
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
  document.body.appendChild(iframe);
  const idoc = iframe.contentWindow.document;
  idoc.open();
  idoc.write(html);
  idoc.close();
  const fire = () => {
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } finally {
      setTimeout(() => { try { document.body.removeChild(iframe); } catch (_) {} }, 1500);
    }
  };
  // Let layout + the Nastaleeq font settle before printing.
  setTimeout(() => {
    try {
      if (idoc.fonts && idoc.fonts.ready) idoc.fonts.ready.then(() => setTimeout(fire, 50));
      else fire();
    } catch (_) { fire(); }
  }, 200);
}

// Parse a markdown table out of a free-form comment string. Looks for the
// canonical AssessmentTab Verification-Table shape: a header row that
// starts with `|`, followed by a separator row like `| --- | --- |`,
// followed by one or more body rows starting with `|`. Returns
// { before, headers, rows, after } when a table is found, otherwise null.
// Cells have surrounding `**bold**` markers stripped (we track which were
// bolded via the second return field).
function parseMarkdownTable(text) {
  if (!text) return null;
  const lines = String(text).split(/\r?\n/);
  let start = -1;
  for (let i = 0; i < lines.length - 1; i++) {
    const line = lines[i].trim();
    const next = lines[i + 1].trim();
    if (line.startsWith('|') && line.endsWith('|') && /^\|[\s\-:|]+\|$/.test(next)) {
      start = i;
      break;
    }
  }
  if (start < 0) return null;
  const decorate = (c) => {
    const raw = String(c || '').trim();
    const bold = /^\*\*.*\*\*$/.test(raw);
    const value = raw.replace(/^\*\*/, '').replace(/\*\*$/, '');
    return { value, bold };
  };
  const headers = splitRawMarkdownCells(lines[start]).map(decorate);
  const rows = [];
  let i = start + 2;
  while (i < lines.length && lines[i].trim().startsWith('|')) {
    rows.push(splitMarkdownRowCells(lines[i], headers.length).map(decorate));
    i++;
  }
  return {
    before: lines.slice(0, start).join('\n').trim(),
    headers,
    rows,
    after: lines.slice(i).join('\n').trim(),
  };
}

// Convert the 5-column AssessmentTab Verification Table (Question / Marks /
// Deduction / Deduction Marks / Obtained) into the 3-column compact form
// the printout uses (Question / Deduction / Obtained-over-Total). The Grand
// Total row is pulled OUT of the table and returned separately so it can
// be folded into the per-student name header (e.g. "(4.6/15)").
//
// Returns { rows: [[cell, cell, cell], ...], grandTotal: { obtained, total } | null }.
// Each cell is { value, bold } so the existing buildWordTable can render it.
function transformVerificationTable(parsed) {
  if (!parsed) return { rows: [], grandTotal: null };
  const headerNames = parsed.headers.map(h => (h.value || '').trim().toLowerCase());
  const findCol = (test, fallback) => {
    const i = headerNames.findIndex(test);
    return i >= 0 ? i : fallback;
  };
  const cQ = findCol(h => /question/.test(h), 0);
  const cM = findCol(h => /^marks$/.test(h), 1);
  const cD = findCol(h => /^deduction$/.test(h) && !/marks/.test(h), 2);
  const cO = findCol(h => /obtained/.test(h), 4);

  const rows = [];
  let grandTotal = null;
  for (const row of parsed.rows) {
    if (!row || row.length === 0) continue;
    const qCell = row[cQ];
    const qValue = (qCell?.value || '').trim();
    if (!qValue) continue;
    if (/grand\s*total/i.test(qValue)) {
      grandTotal = {
        obtained: (row[cO]?.value || '').trim(),
        total:    (row[cM]?.value || '').trim()
      };
      continue;
    }
    const obtained = (row[cO]?.value || '').trim();
    const total    = (row[cM]?.value || '').trim();
    const score    = obtained || total ? `${obtained}/${total}` : '';
    rows.push([
      { value: qValue, bold: !!qCell.bold },
      { value: (row[cD]?.value || '').trim(), bold: false },
      { value: score, bold: false }
    ]);
  }
  return { rows, grandTotal };
}

// Render a teacher identifier as a human NAME — never a raw email. The stored
// value (teacher_names / created_by) may be a single value or a comma-
// separated list, and each token can be an email (hammad@gmail.com), a
// username, or already a display name. For each token we prefer the account's
// full name from `nameMap` (keyed by lowercased email AND username); if that's
// unknown we strip the "@domain" so a student-facing printout never exposes
// the teacher's gmail. Tokens already in plain-name form pass through as-is.
function teacherDisplayName(raw, nameMap) {
  if (!raw) return '';
  return String(raw)
    .split(',')
    .map((tok) => {
      const t = tok.trim();
      if (!t) return '';
      const mapped = nameMap && nameMap.get(t.toLowerCase());
      if (mapped) return mapped;
      const at = t.indexOf('@');
      return at > 0 ? t.slice(0, at) : t;
    })
    .filter(Boolean)
    .join(', ');
}

export default function ScansFolderView({ folder, currentUser, onBack, autoOpenStudent = null, onAutoOpenStudentConsumed }) {
  // Teachers may view/preview/download/rename scans but must never delete
  // them. Every delete affordance (per-row trash, Delete Selected, Clear All)
  // and the selection checkboxes that only feed deletion are gated on this.
  // Roles are stored lowercase (see AuthContext PERMISSIONS keys).
  const canDeleteScans = currentUser?.role !== 'teacher';
  const canPrintPreview = currentUser?.role !== 'teacher';
  const [scans, setScans] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploading, setUploading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [newFileName, setNewFileName] = useState('');
  const [savedResults, setSavedResults] = useState(null);
  // Ticked saved-result rows. Preview, Print, and Export DOCX use only these.
  // An empty set prints the whole class.
  const [selectedPrintIds, setSelectedPrintIds] = useState(() => new Set());
  // Map of lowercased email/username -> teacher full name, used to turn the
  // stored email in teacher_names/created_by into a real name on the DOCX.
  const [teacherNameMap, setTeacherNameMap] = useState(() => new Map());
  const [showResults, setShowResults] = useState(false);
  const [confirm, setConfirm] = useState({ show: false, text: '', onConfirm: null });
  const [selectedScans, setSelectedScans] = useState(new Set());
  const [lastSelectedIndex, setLastSelectedIndex] = useState(null);
  // Image view mode: 'grid' (thumbnail cards, the default) or 'list' (compact
  // rows with a small thumbnail). Persisted so the grader's choice sticks
  // across folders and reloads.
  const [viewMode, setViewMode] = useState(() => {
    const saved = localStorage.getItem('scansViewMode');
    return saved === 'list' ? 'list' : 'grid';
  });
  const setView = (mode) => {
    setViewMode(mode);
    try { localStorage.setItem('scansViewMode', mode); } catch (_) { /* ignore */ }
  };
  // Shared checkbox handler for BOTH views (grid + list): a plain click toggles
  // one scan; Shift+Click selects the range from the last-clicked one.
  const toggleSelect = (scan, e) => {
    const currentIndex = scans.findIndex(s => s.id === scan.id);
    const newSelected = new Set(selectedScans);
    if (e.nativeEvent.shiftKey && lastSelectedIndex !== null) {
      const start = Math.min(lastSelectedIndex, currentIndex);
      const end = Math.max(lastSelectedIndex, currentIndex);
      for (let i = start; i <= end; i++) newSelected.add(scans[i].id);
    } else if (e.target.checked) {
      newSelected.add(scan.id);
    } else {
      newSelected.delete(scan.id);
    }
    setSelectedScans(newSelected);
    setLastSelectedIndex(currentIndex);
  };
  const [previewImage, setPreviewImage] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  // ── Saved-results comment viewers (read-only) ──────────────────────────
  // viewingSavedComment: one student's full comment in a modal (per-row
  // "View" action). showAllSavedComments: the View All Comments modal.
  // Always table view. Display-only: neither changes stored comments or print.
  const [viewingSavedComment, setViewingSavedComment] = useState(null); // { student, index }
  const [showAllSavedComments, setShowAllSavedComments] = useState(false);
  // Saved-results table sort — default marks high → low
  const [savedSort, setSavedSort] = useState({ key: 'marks', dir: 'desc' });

  // ── Re-check requests ───────────────────────────────────────────────────
  // A teacher can contest one student's saved checking: attach an image of
  // the problem + explain it in text. The request lands in the Re-checks tab
  // where an examiner/admin approves (manual check / AI re-check / direct
  // marks correction) or rejects it. recheckByDataId maps
  // checked_results_data.id -> existing request so already-requested rows
  // show a status chip instead of the button.
  const [recheckModal, setRecheckModal] = useState(null); // { student } | null
  const [recheckExplanation, setRecheckExplanation] = useState('');
  // Queued annotated screenshots for the open recheck modal (and drafts per student).
  const [recheckFiles, setRecheckFiles] = useState([]); // [{ file, previewUrl }]
  // Persists across opening the modal so teachers can Screenshot → Save → Screenshot again.
  const [recheckDraftByStudentId, setRecheckDraftByStudentId] = useState(() => new Map());
  const [screenshotAnnotator, setScreenshotAnnotator] = useState(null); // { student, url } | null
  const [screenshotCapturing, setScreenshotCapturing] = useState(false);
  const [previewLightbox, setPreviewLightbox] = useState(null); // { url, name } | null
  const [recheckAudio, setRecheckAudio] = useState(null); // File | null
  const [recheckAudioUrl, setRecheckAudioUrl] = useState(null);
  const [recheckRecording, setRecheckRecording] = useState(false);
  const [recheckRecordSecs, setRecheckRecordSecs] = useState(0);
  const [recheckMicBusy, setRecheckMicBusy] = useState(false);
  const [recheckAudioError, setRecheckAudioError] = useState('');
  const [recheckSubmitting, setRecheckSubmitting] = useState(false);
  const [recheckByDataId, setRecheckByDataId] = useState(() => new Map());
  const [feedbackByDataId, setFeedbackByDataId] = useState(() => new Map());
  const recheckMediaRecorderRef = useRef(null);
  const recheckAudioChunksRef = useRef([]);
  const recheckRecordTimerRef = useRef(null);
  const recheckStreamRef = useRef(null);
  const recheckAudioUrlRef = useRef(null);

  const loadRecheckRequests = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/recheck-requests`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success && Array.isArray(data.requests)) {
        const map = new Map();
        data.requests
          .filter(r => String(r.rubric_id) === String(folder.id) && r.result_data_id != null)
          .forEach(r => { if (!map.has(r.result_data_id)) map.set(r.result_data_id, r); });
        setRecheckByDataId(map);
      }
    } catch (_) { /* non-critical — the button just stays available */ }
  };

  const loadFeedbackPacks = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/feedback`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success && Array.isArray(data.packs)) {
        const map = new Map();
        data.packs
          .filter(p => String(p.rubric_id) === String(folder.id) && p.result_data_id != null)
          .forEach(p => {
            const n = (map.get(p.result_data_id) || 0) + 1;
            map.set(p.result_data_id, n);
          });
        setFeedbackByDataId(map);
      }
    } catch (_) { /* non-critical — the button just stays available */ }
  };

  const clearRecheckAudio = () => {
    if (recheckRecordTimerRef.current) {
      clearInterval(recheckRecordTimerRef.current);
      recheckRecordTimerRef.current = null;
    }
    if (recheckMediaRecorderRef.current && recheckMediaRecorderRef.current.state !== 'inactive') {
      try { recheckMediaRecorderRef.current.stop(); } catch (_) { /* ignore */ }
    }
    recheckMediaRecorderRef.current = null;
    if (recheckStreamRef.current) {
      recheckStreamRef.current.getTracks().forEach(t => t.stop());
      recheckStreamRef.current = null;
    }
    if (recheckAudioUrlRef.current) {
      URL.revokeObjectURL(recheckAudioUrlRef.current);
      recheckAudioUrlRef.current = null;
    }
    setRecheckAudioUrl(null);
    setRecheckAudio(null);
    setRecheckRecording(false);
    setRecheckMicBusy(false);
    setRecheckRecordSecs(0);
    setRecheckAudioError('');
    recheckAudioChunksRef.current = [];
  };

  const revokeRecheckFilePreviews = (items) => {
    (items || []).forEach((it) => {
      if (it && it.previewUrl) {
        try { URL.revokeObjectURL(it.previewUrl); } catch (_) { /* ignore */ }
      }
    });
  };

  const setDraftImagesForStudent = (studentId, items) => {
    setRecheckDraftByStudentId((prev) => {
      const next = new Map(prev);
      const old = next.get(studentId) || [];
      // Revoke previews that are no longer referenced
      const keep = new Set((items || []).map((i) => i.previewUrl));
      old.forEach((it) => {
        if (it.previewUrl && !keep.has(it.previewUrl)) {
          try { URL.revokeObjectURL(it.previewUrl); } catch (_) { /* ignore */ }
        }
      });
      if (!items || items.length === 0) next.delete(studentId);
      else next.set(studentId, items);
      return next;
    });
  };

  const appendDraftImage = (student, file) => {
    if (!student?.id || !file) return;
    const previewUrl = URL.createObjectURL(file);
    const entry = { file, previewUrl };
    setRecheckDraftByStudentId((prev) => {
      const next = new Map(prev);
      const list = [...(next.get(student.id) || []), entry];
      next.set(student.id, list);
      return next;
    });
    // If the recheck / feedback modal for this student is open, keep it in sync.
    setRecheckFiles((prev) => {
      if (!recheckModal || recheckModal.student?.id !== student.id) return prev;
      return [...prev, entry];
    });
    return entry;
  };

  const openRecheckModal = (student, kind = 'recheck') => {
    setRecheckExplanation('');
    const draft = recheckDraftByStudentId.get(student.id) || [];
    // Clone entries so modal edits don't mutate the map until we sync on close/submit
    setRecheckFiles(draft.map((d) => ({ file: d.file, previewUrl: d.previewUrl })));
    clearRecheckAudio();
    setRecheckModal({ student, kind });
  };

  const openFeedbackModal = (student) => openRecheckModal(student, 'feedback');

  const closeRecheckModal = () => {
    // Persist current modal files back into the per-student draft
    if (recheckModal?.student?.id) {
      setDraftImagesForStudent(recheckModal.student.id, recheckFiles);
    }
    setRecheckFiles([]);
    clearRecheckAudio();
    setRecheckModal(null);
  };

  const removeRecheckFileAt = (idx) => {
    setRecheckFiles((prev) => {
      const next = [...prev];
      const [removed] = next.splice(idx, 1);
      if (removed?.previewUrl) {
        // Only revoke if this preview isn't still in the draft map
        const draft = recheckModal?.student?.id
          ? (recheckDraftByStudentId.get(recheckModal.student.id) || [])
          : [];
        const stillUsed = draft.some((d) => d.previewUrl === removed.previewUrl)
          || next.some((d) => d.previewUrl === removed.previewUrl);
        if (!stillUsed) {
          try { URL.revokeObjectURL(removed.previewUrl); } catch (_) { /* ignore */ }
        }
      }
      return next;
    });
  };

  const setRecheckAudioFile = (file) => {
    if (!file) return;
    const ok = (file.type && file.type.startsWith('audio/'))
      || /\.(webm|ogg|mp3|wav|m4a|aac|mp4)$/i.test(file.name || '');
    if (!ok) {
      setRecheckAudioError('Please choose an audio file (webm, mp3, wav, m4a, ogg).');
      return;
    }
    if (recheckAudioUrlRef.current) URL.revokeObjectURL(recheckAudioUrlRef.current);
    const url = URL.createObjectURL(file);
    recheckAudioUrlRef.current = url;
    setRecheckAudio(file);
    setRecheckAudioUrl(url);
    setRecheckAudioError('');
  };

  const startRecheckRecording = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setRecheckAudioError('');
    setRecheckMicBusy(true);

    try {
      // Browsers block mic on non-secure origins (http://fast107:…).
      // localhost / https are fine; plain LAN HTTP is not.
      if (!window.isSecureContext) {
        setRecheckAudioError(
          'Microphone recording needs HTTPS (or localhost). This page is not a secure context, so the browser blocked the mic. Use "Upload audio" below, or open the app via https / localhost.'
        );
        return;
      }
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setRecheckAudioError(
          'This browser does not support microphone recording. Use "Upload audio" instead.'
        );
        return;
      }
      if (typeof MediaRecorder === 'undefined') {
        setRecheckAudioError(
          'MediaRecorder is not available in this browser. Use "Upload audio" instead.'
        );
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      recheckStreamRef.current = stream;
      recheckAudioChunksRef.current = [];

      let mimeType = '';
      if (typeof MediaRecorder.isTypeSupported === 'function') {
        if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) mimeType = 'audio/webm;codecs=opus';
        else if (MediaRecorder.isTypeSupported('audio/webm')) mimeType = 'audio/webm';
        else if (MediaRecorder.isTypeSupported('audio/mp4')) mimeType = 'audio/mp4';
        else if (MediaRecorder.isTypeSupported('audio/ogg')) mimeType = 'audio/ogg';
      }
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);
      recheckMediaRecorderRef.current = recorder;

      recorder.ondataavailable = (ev) => {
        if (ev.data && ev.data.size > 0) recheckAudioChunksRef.current.push(ev.data);
      };
      recorder.onerror = (ev) => {
        console.error('MediaRecorder error:', ev);
        setRecheckAudioError('Recording failed. Please try again or upload an audio file.');
        setRecheckRecording(false);
        setRecheckMicBusy(false);
      };
      recorder.onstop = () => {
        const blobType = recorder.mimeType || mimeType || 'audio/webm';
        const blob = new Blob(recheckAudioChunksRef.current, { type: blobType });
        if (!blob.size) {
          setRecheckAudioError('Recording was empty. Please try again.');
          setRecheckRecording(false);
          setRecheckMicBusy(false);
          return;
        }
        const ext = blobType.includes('mp4') ? 'm4a'
          : blobType.includes('ogg') ? 'ogg'
          : 'webm';
        const file = new File([blob], `voice-note.${ext}`, { type: blobType.split(';')[0] });
        if (recheckAudioUrlRef.current) URL.revokeObjectURL(recheckAudioUrlRef.current);
        const url = URL.createObjectURL(blob);
        recheckAudioUrlRef.current = url;
        setRecheckAudio(file);
        setRecheckAudioUrl(url);
        if (recheckStreamRef.current) {
          recheckStreamRef.current.getTracks().forEach(t => t.stop());
          recheckStreamRef.current = null;
        }
        if (recheckRecordTimerRef.current) {
          clearInterval(recheckRecordTimerRef.current);
          recheckRecordTimerRef.current = null;
        }
        setRecheckRecording(false);
        setRecheckMicBusy(false);
      };

      // timeslice keeps data flowing on some browsers that buffer until stop
      recorder.start(250);
      setRecheckRecording(true);
      setRecheckRecordSecs(0);
      setRecheckMicBusy(false);
      recheckRecordTimerRef.current = setInterval(() => {
        setRecheckRecordSecs(s => s + 1);
      }, 1000);
    } catch (err) {
      console.error('Microphone error:', err);
      const name = err && err.name;
      let msg = 'Could not access the microphone.';
      if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
        msg = 'Microphone permission was denied. Allow mic access in the browser address bar, then try again — or use "Upload audio".';
      } else if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
        msg = 'No microphone was found on this device. Use "Upload audio" instead.';
      } else if (name === 'NotReadableError') {
        msg = 'Microphone is busy in another app. Close it and try again, or use "Upload audio".';
      } else if (!window.isSecureContext) {
        msg = 'Microphone needs HTTPS. Use "Upload audio" on this HTTP site.';
      } else if (err && err.message) {
        msg = `Microphone error: ${err.message}. You can also use "Upload audio".`;
      }
      setRecheckAudioError(msg);
      if (recheckStreamRef.current) {
        recheckStreamRef.current.getTracks().forEach(t => t.stop());
        recheckStreamRef.current = null;
      }
    } finally {
      setRecheckMicBusy(false);
    }
  };

  const stopRecheckRecording = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const recorder = recheckMediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      try { recorder.requestData(); } catch (_) { /* optional */ }
      recorder.stop();
    }
  };

  const setRecheckImageFile = (file) => {
    if (!file || !file.type || !file.type.startsWith('image/')) {
      setError('Please attach an image file (JPEG, PNG, GIF, or WEBP).');
      return;
    }
    const previewUrl = URL.createObjectURL(file);
    setRecheckFiles((prev) => [...prev, { file, previewUrl }]);
    setError('');
  };

  const handleRecheckFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setRecheckImageFile(file);
    // Allow re-selecting the same file later
    e.target.value = '';
  };

  // Prefer an image item from the clipboard; ignore plain-text pastes so
  // Ctrl+V still works normally inside the explanation textarea.
  const applyRecheckClipboardImage = (clipboardData) => {
    if (!clipboardData) return false;
    const items = clipboardData.items || [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item && item.type && item.type.startsWith('image/')) {
        const blob = item.getAsFile();
        if (!blob) continue;
        const ext = (item.type.split('/')[1] || 'png').replace('jpeg', 'jpg');
        const file = new File([blob], `pasted-image.${ext}`, { type: item.type });
        setRecheckImageFile(file);
        return true;
      }
    }
    const files = clipboardData.files;
    if (files && files.length) {
      for (let i = 0; i < files.length; i++) {
        if (files[i].type && files[i].type.startsWith('image/')) {
          setRecheckImageFile(files[i]);
          return true;
        }
      }
    }
    return false;
  };

  // Global paste while the re-check modal is open (screenshot → Ctrl+V).
  useEffect(() => {
    if (!recheckModal) return;
    const onPaste = (e) => {
      if (applyRecheckClipboardImage(e.clipboardData)) {
        e.preventDefault();
      }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recheckModal, recheckFiles.length]);

  // Park the page at the scroll origin, waiting for the jump to land.
  // App.css sets `scroll-behavior: smooth`, which would otherwise animate it.
  const scrollToOriginForCapture = async () => {
    document.documentElement.style.scrollBehavior = 'auto';
    window.scrollTo(0, 0);
    for (let i = 0; i < 30 && (window.scrollX || window.scrollY); i++) {
      await new Promise((resolve) => requestAnimationFrame(resolve));
    }
  };

  // The region to capture: the panes the teacher is reviewing (comments |
  // image | OCR | rubric) and nothing else — no app header above them, no
  // scans list below. Clamped to the viewport because a pane taller than the
  // screen is cut off on screen too, and html2canvas would pad the rest blank.
  // Falls back to the whole viewport if no pane is on screen.
  const captureRegionForScreenshot = () => {
    let left = Infinity;
    let top = Infinity;
    let right = -Infinity;
    let bottom = -Infinity;
    document.querySelectorAll('[data-recheck-capture-pane="1"]').forEach((pane) => {
      const r = pane.getBoundingClientRect();
      if (r.width < 2 || r.height < 2) return;
      left = Math.min(left, r.left);
      top = Math.min(top, r.top);
      right = Math.max(right, r.right);
      bottom = Math.max(bottom, r.bottom);
    });
    // `main` carries `z-index: 1`, so the panes' own z-index can never lift
    // them above the fixed app header — it paints over their top strip. Start
    // below it, otherwise the screenshot opens with the nav bar instead of the
    // panes.
    const header = document.querySelector('.app-header');
    if (header) {
      const hr = header.getBoundingClientRect();
      if (hr.height > 0 && hr.bottom > top && hr.top < bottom) top = hr.bottom;
    }

    const x = Math.max(0, Math.floor(left));
    const y = Math.max(0, Math.floor(top));
    const width = Math.min(window.innerWidth, Math.ceil(right)) - x;
    const height = Math.min(window.innerHeight, Math.ceil(bottom)) - y;
    if (!(width > 1 && height > 1)) {
      return { x: 0, y: 0, width: window.innerWidth, height: window.innerHeight };
    }
    return { x, y, width, height };
  };

  // html2canvas paints a <textarea> with one fillText and stops at the first
  // newline, so the OCR and rubric panes come out blank under their first
  // line. Swap each box in the clone for a normal text block that wraps.
  const paintTextareasForCapture = (liveRoot, clonedDoc, clonedRoot) => {
    const live = liveRoot.querySelectorAll('textarea');
    const cloned = clonedRoot.querySelectorAll('textarea');
    const view = clonedDoc.defaultView || window;
    cloned.forEach((ta, i) => {
      const source = live[i] || ta;
      const cs = view.getComputedStyle(ta);
      const liveCs = window.getComputedStyle(source);
      const liveBox = source.getBoundingClientRect();
      const wrap = clonedDoc.createElement('div');
      wrap.style.boxSizing = 'border-box';
      wrap.style.overflow = 'hidden';
      wrap.style.flex = '1 1 auto';
      wrap.style.minHeight = '0';
      wrap.style.width = '100%';
      wrap.style.height = `${liveBox.height || parseFloat(cs.height) || 0}px`;
      wrap.style.background = liveCs.backgroundColor;
      wrap.style.color = liveCs.color;
      const inner = clonedDoc.createElement('div');
      inner.setAttribute('dir', source.getAttribute('dir') || 'auto');
      inner.textContent = source.value || '';
      inner.style.boxSizing = 'border-box';
      inner.style.whiteSpace = 'pre-wrap';
      inner.style.overflowWrap = 'break-word';
      inner.style.marginTop = `-${source.scrollTop || 0}px`;
      inner.style.padding = `${liveCs.paddingTop} ${liveCs.paddingRight} ${liveCs.paddingBottom} ${liveCs.paddingLeft}`;
      inner.style.font = liveCs.font;
      inner.style.lineHeight = liveCs.lineHeight;
      inner.style.color = liveCs.color;
      inner.style.textAlign = liveCs.textAlign;
      wrap.appendChild(inner);
      ta.replaceWith(wrap);
    });
  };

  const captureFastGraderScreenshot = async (student) => {
    if (!student || screenshotCapturing || screenshotAnnotator) return;
    const prevScroll = { x: window.scrollX, y: window.scrollY };
    const prevScrollBehavior = document.documentElement.style.scrollBehavior;
    try {
      setScreenshotCapturing(true);
      setError('');

      // This viewer is built entirely from `position: fixed` panes, which
      // html2canvas lays out against the scroll position it is handed in
      // `scrollX`/`scrollY`, while `x`/`y` crop the document. Both have to
      // describe the same region or the panes land offset by the page scroll,
      // so the capture is taken from the scroll origin. The fixed panes cover
      // the viewport, so nothing appears to move while it runs.
      await scrollToOriginForCapture();

      const shell =
        document.querySelector('.app-root')
        || document.querySelector('#root')
        || document.body;

      // Measured after the scroll is parked, so the crop matches what
      // html2canvas is about to lay out.
      const region = captureRegionForScreenshot();

      const canvas = await html2canvas(shell, {
        backgroundColor: '#ffffff',
        scale: Math.min(2, window.devicePixelRatio || 1.5),
        logging: false,
        useCORS: true,
        allowTaint: false,
        x: region.x,
        y: region.y,
        width: region.width,
        height: region.height,
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight,
        scrollX: 0,
        scrollY: 0,
        onclone: (clonedDoc, clonedRoot) => {
          paintTextareasForCapture(shell, clonedDoc, clonedRoot);
        },
        ignoreElements: (el) => {
          if (!el || !el.dataset) return false;
          if (el.dataset.html2canvasIgnore === 'true') return true;
          if (el.dataset.recheckAnnotator === '1') return true;
          return false;
        }
      });

      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Empty screenshot'))), 'image/png');
      });
      const url = URL.createObjectURL(blob);
      setScreenshotAnnotator({ student, url });
    } catch (err) {
      console.error('Screenshot capture failed:', err);
      const msg = /tainted|insecure/i.test(err?.message || '')
        ? 'Could not capture the screen (cross-origin image blocked). Try again after the paper image has fully loaded, or paste a screenshot instead.'
        : `Screenshot failed: ${err?.message || err}`;
      setError(msg);
    } finally {
      window.scrollTo(prevScroll.x, prevScroll.y);
      document.documentElement.style.scrollBehavior = prevScrollBehavior;
      setScreenshotCapturing(false);
    }
  };

  const closeScreenshotAnnotator = () => {
    setScreenshotAnnotator((prev) => {
      if (prev?.url) {
        try { URL.revokeObjectURL(prev.url); } catch (_) { /* ignore */ }
      }
      return null;
    });
  };

  const saveAnnotatedForRecheck = async (blob, fileName) => {
    if (!screenshotAnnotator?.student || !blob) return;
    const student = screenshotAnnotator.student;
    const file = new File([blob], fileName || `recheck_${student.id}_${Date.now()}.png`, { type: 'image/png' });
    appendDraftImage(student, file);
    closeScreenshotAnnotator();
    setSuccess(`Screenshot saved for ${student.student_name}. Click Screenshot again to add another, then Feedback or Re-check.`);
  };

  const draftCountFor = (student) => {
    if (!student?.id) return 0;
    if (recheckModal?.student?.id === student.id) return recheckFiles.length;
    return (recheckDraftByStudentId.get(student.id) || []).length;
  };

  const renderScreenshotButton = (student) => {
    if (!student) return null;
    const n = draftCountFor(student);
    return (
      <button
        type="button"
        onClick={() => captureFastGraderScreenshot(student)}
        disabled={screenshotCapturing || !!screenshotAnnotator}
        title="Capture this FastGrader view, annotate, then Save. Repeat, then Feedback or Re-check."
        style={{
          padding: '7px 14px',
          background: screenshotCapturing
            ? '#9ca3af'
            : 'linear-gradient(135deg, #0ea5e9, #0284c7)',
          color: 'white',
          border: 'none',
          borderRadius: 8,
          cursor: screenshotCapturing ? 'wait' : 'pointer',
          fontWeight: 700,
          fontSize: 13,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          whiteSpace: 'nowrap'
        }}
      >
        <FontAwesomeIcon icon={screenshotCapturing ? faSpinner : faCamera} spin={screenshotCapturing} />
        {screenshotCapturing ? 'Capturing…' : (n > 0 ? `Screenshot (${n})` : 'Screenshot')}
      </button>
    );
  };

  const renderFeedbackButton = (student, compact = false) => {
    if (!student) return null;
    const draftN = draftCountFor(student);
    const sentN = feedbackByDataId.get(student.id) || 0;
    return (
      <button
        type="button"
        onClick={() => openFeedbackModal(student)}
        title={draftN
          ? `Send ${draftN} screenshot(s) to Feedback`
          : (sentN
            ? 'Already in Feedback — screenshot more, then send another pack'
            : 'Send annotated screenshots to the Feedback tab to show this student')}
        style={{
          padding: compact ? '6px 12px' : '7px 14px',
          background: 'linear-gradient(135deg, #14B8A6, #0D9488)',
          color: 'white',
          border: 'none',
          borderRadius: compact ? 6 : 8,
          cursor: 'pointer',
          fontWeight: compact ? 600 : 700,
          fontSize: 13,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          whiteSpace: 'nowrap',
          flexShrink: 0
        }}
      >
        <FontAwesomeIcon icon={faCommentDots} />
        {draftN > 0 ? `Feedback (${draftN})` : 'Feedback'}
      </button>
    );
  };

  const submitRecheckRequest = async () => {
    if (!recheckModal) return;
    if (recheckRecording) {
      setError('Please stop the voice recording before submitting.');
      return;
    }
    if (!recheckExplanation.trim() && !recheckAudio) {
      setError('Please type an explanation or record a voice note describing the problem.');
      return;
    }
    if (!recheckFiles.length) {
      setError('Please attach at least one image of the problem before submitting.');
      return;
    }
    const student = recheckModal.student;
    try {
      setRecheckSubmitting(true);
      setError('');
      const token = localStorage.getItem('authToken');
      const formData = new FormData();
      formData.append('file', recheckFiles[0].file);
      for (let i = 1; i < recheckFiles.length; i++) {
        formData.append('files', recheckFiles[i].file);
      }
      // Also send all via files[] so backends that only read "files" still work
      if (recheckFiles.length === 1) {
        formData.append('files', recheckFiles[0].file);
      }
      if (recheckAudio) formData.append('audio', recheckAudio);
      formData.append('rubric_id', folder.id);
      formData.append('result_data_id', student.id);
      formData.append('student_name', student.student_name || '');
      formData.append('original_marks', student.marks != null ? String(student.marks) : '');
      formData.append('class', folder.class || '');
      formData.append('section', folder.section || student.section || '');
      formData.append('subject', folder.subject || '');
      formData.append('student_images', imageNamesFromComment(student.comments).join(','));
      formData.append('explanation', recheckExplanation.trim());

      const res = await fetch(`${API_BASE_URL}/recheck-requests`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to submit re-check request');
      }
      setSuccess(`Re-check request for ${student.student_name} submitted — the examiner will review it.`);
      // Clear drafts for this student (files were uploaded)
      revokeRecheckFilePreviews(recheckFiles);
      setRecheckDraftByStudentId((prev) => {
        const next = new Map(prev);
        next.delete(student.id);
        return next;
      });
      setRecheckFiles([]);
      clearRecheckAudio();
      setRecheckModal(null);
      loadRecheckRequests();
    } catch (err) {
      console.error('Error submitting recheck request:', err);
      setError(err.message || 'Failed to submit re-check request');
    } finally {
      setRecheckSubmitting(false);
    }
  };

  const submitFeedback = async () => {
    if (!recheckModal) return;
    if (!recheckFiles.length) {
      setError('Please attach at least one annotated screenshot before sending to Feedback.');
      return;
    }
    const student = recheckModal.student;
    try {
      setRecheckSubmitting(true);
      setError('');
      const token = localStorage.getItem('authToken');
      const formData = new FormData();
      formData.append('file', recheckFiles[0].file);
      for (let i = 1; i < recheckFiles.length; i++) {
        formData.append('files', recheckFiles[i].file);
      }
      if (recheckFiles.length === 1) {
        formData.append('files', recheckFiles[0].file);
      }
      formData.append('rubric_id', folder.id);
      formData.append('result_data_id', student.id);
      formData.append('student_name', student.student_name || '');
      formData.append('original_marks', student.marks != null ? String(student.marks) : '');
      formData.append('class', folder.class || '');
      formData.append('section', folder.section || student.section || '');
      formData.append('subject', folder.subject || '');
      formData.append('note', recheckExplanation.trim());

      const res = await fetch(`${API_BASE_URL}/feedback`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to send to Feedback');
      }
      setSuccess(`Sent ${student.student_name} to Feedback — open the Feedback tab to show the class.`);
      revokeRecheckFilePreviews(recheckFiles);
      setRecheckDraftByStudentId((prev) => {
        const next = new Map(prev);
        next.delete(student.id);
        return next;
      });
      setRecheckFiles([]);
      clearRecheckAudio();
      setRecheckModal(null);
      loadFeedbackPacks();
    } catch (err) {
      console.error('Error sending feedback:', err);
      setError(err.message || 'Failed to send to Feedback');
    } finally {
      setRecheckSubmitting(false);
    }
  };

  // One student's comment body: always the verification table when present
  // (with the sheet-count footer below). Falls back to the one-line summary
  // only when no table markdown was saved.
  const normStudentName = (s) => String(s || '').trim().toLowerCase().replace(/\s+/g, ' ');
  const findSnapshotRow = (rows, student) => {
    if (!Array.isArray(rows)) return null;
    const target = normStudentName(student.student_name);
    return rows.find(r => normStudentName(r.student_name) === target) || null;
  };

  // Prefer Evaluate's sheet count (assess snapshot / live results) when the
  // scans footer was wrongly saved as "[1 sheet]" from image_name.length.
  const resolveSheetCount = (student, rows) => {
    const row = findSnapshotRow(rows, student);
    return sheetCountFromText(row && row.comments)
      || sheetCountFromText(student && student.comments)
      || null;
  };

  const focusSavedQuestion = (raw, student, deduction) => {
    const id = normalizeQuestionId(raw);
    if (!id) return;
    const owner = student && student.id != null ? String(student.id) : '';
    const detail = savedDetail && savedDetail.student;
    const sameStudent = !!(detail && student && String(detail.id) === owner);
    const ocrEl = scansOcrRef.current;
    const rubricEl = scansRubricRef.current;
    const rubricSource = (rubricEl && rubricEl.value) || rubricDraft || (savedRubric && savedRubric.text) || '';
    const ocrSpan = ocrEl && sameStudent ? findTranscriptSpan(ocrEl.value, id, deduction, rubricSource) : null;
    const rubricSpan = rubricEl ? findQuestionSpan(rubricEl.value, id) : null;
    setLinkedQuestion({ id, owner, ocr: ocrSpan, rubric: rubricSpan, tick: Date.now() });
    if (ocrEl && ocrSpan) revealTextareaSpan(ocrEl, ocrSpan.start, ocrSpan.end);
    if (sameStudent && savedDetail && ocrEl && ocrSpan) {
      const images = (savedDetail.names || []).map((name) => ({ name }));
      const idx = transcriptPageIndex(ocrEl.value, ocrSpan, images);
      if (idx >= 0 && idx !== savedDetail.idx) {
        setSavedDetail((prev) => prev ? { ...prev, idx } : prev);
      }
    }
  };

  const renderSavedCommentBody = (student) => {
    const { tableMd, afterText } = splitSavedComment(student.comments);
    const owner = student && student.id != null ? String(student.id) : '';
    const linkedId = linkedQuestion && linkedQuestion.owner === owner ? linkedQuestion.id : null;
    const correctCount = resolveSheetCount(student, savedSnapshotRows);
    const displayAfter = correctCount
      ? rewriteSheetCountInText(afterText, correctCount)
      : afterText;
    if (tableMd) {
      return (
        <div>
          {renderSavedMarkdownTable(tableMd, { linkedId, onPick: (qid, deduction) => focusSavedQuestion(qid, student, deduction) }) || (
            <div style={{ whiteSpace: 'pre-wrap' }}>{student.comments || '-'}</div>
          )}
          {displayAfter && (
            <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'pre-wrap' }}>
              {displayAfter}
            </div>
          )}
        </div>
      );
    }
    const oneLine = savedCommentOneLine(student.comments) || '-';
    return (
      <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.7, color: 'var(--text-primary)', fontSize: 14 }}>
        {correctCount ? rewriteSheetCountInText(oneLine, correctCount) : oneLine}
      </div>
    );
  };

  // Comments column is only 25% wide — listing every sheet as a wrapping
  // button pushes the comment body off-screen. Show the first image name
  // only; remaining sheets are reached via the image pane arrows (1 / N).
  const renderFirstImageChip = (student) => {
    const names = studentImageNames(student, savedSnapshotRows);
    if (!names.length) return null;
    const first = names[0];
    const extra = names.length - 1;
    return (
      <button
        onClick={() => openSavedDetail(student, first)}
        title={extra > 0
          ? `Show ${first} with OCR and rubric (${names.length} sheets — use the image arrows to switch)`
          : 'Show this image with the OCR transcript and rubric'}
        style={{
          background: 'rgba(59,130,246,0.1)', color: '#2563EB',
          border: '1px solid rgba(59,130,246,0.35)', borderRadius: 6,
          padding: '3px 9px', fontSize: 12, fontWeight: 700,
          cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5
        }}
      >
        <FontAwesomeIcon icon={faImage} />
        {first}
        {extra > 0 ? ` +${extra}` : ''}
      </button>
    );
  };


  // ── Saved-results detail data (image / OCR / rubric side panes) ────────
  // The saved checked_results rows only carry name + marks + comments. The
  // OCR transcript and image names live in the folder's ASSESS snapshot (the
  // Check tab's saved table, keyed by the same rubrics/<id> path), and the
  // rubric text on the rubric itself. Both are fetched lazily the first time
  // they're needed and cached for the rest of the visit.
  const [savedSnapshotRows, setSavedSnapshotRows] = useState(null); // null = not fetched yet
  const [savedRubric, setSavedRubric] = useState(null);             // { text } | null
  const [regradeRuns, setRegradeRuns] = useState([]);
  const savedSnapshotRowsRef = useRef(null);
  const savedExtrasFetchedRef = useRef(false);
  // { student, names, idx, urls, ocrText, ocrStatus } — drives the right-side
  // image + OCR + rubric panel opened from the All Comments modal.
  const [savedDetail, setSavedDetail] = useState(null);
  const savedDetailBlobsRef = useRef({ studentId: null, urls: [] });
  const [linkedQuestion, setLinkedQuestion] = useState(null);
  const scansOcrRef = useRef(null);
  const scansRubricRef = useRef(null);
  const [ocrDraft, setOcrDraft] = useState('');
  const [ocrSaving, setOcrSaving] = useState(false);
  const [ocrSaveNote, setOcrSaveNote] = useState('');
  const [rubricDraft, setRubricDraft] = useState('');
  const [rubricSaving, setRubricSaving] = useState(false);
  const [rubricSaveNote, setRubricSaveNote] = useState('');

  const ensureSavedExtras = async () => {
    if (savedExtrasFetchedRef.current) return savedSnapshotRowsRef.current;
    savedExtrasFetchedRef.current = true;
    const token = localStorage.getItem('authToken');
    const folderPath = `rubrics/${folder.id}`;
    try {
      const res = await fetch(
        `${API_BASE_URL}/assess/snapshots?folder_path=${encodeURIComponent(folderPath)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json().catch(() => ({}));
      let rows = res.ok && Array.isArray(data.data) ? data.data : [];
      // Snapshot may be empty or lack the "[N sheets]" trailer. Fall back to
      // live assessment_results (what Evaluate shows) so Scans can show the
      // same sheet count and expand all pages.
      const needsLive = rows.length === 0
        || rows.every(r => !sheetCountFromText(r.comments));
      let liveRows = [];
      try {
        const liveRes = await fetch(
          `${API_BASE_URL}/assess/results?folder_path=${encodeURIComponent(folderPath)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const liveData = await liveRes.json().catch(() => ({}));
        liveRows = liveRes.ok && Array.isArray(liveData.data)
          ? liveData.data
          : (liveRes.ok && Array.isArray(liveData.results) ? liveData.results : []);
      } catch (_) { /* grade times stay on the snapshot rows */ }
      if (liveRows.length > 0) {
        if (rows.length === 0) {
          rows = liveRows;
        } else {
          const byName = new Map(liveRows.map(r => [
            String(r.student_name || '').trim().toLowerCase().replace(/\s+/g, ' '),
            r
          ]));
          rows = rows.map(r => {
            const key = String(r.student_name || '').trim().toLowerCase().replace(/\s+/g, ' ');
            const live = byName.get(key);
            if (!live) return r;
            const merged = {
              ...r,
              created_at: live.created_at || r.created_at || null,
              table_history: live.table_history || r.table_history || null,
              saved_at: r.saved_at || live.saved_at || null
            };
            if (!needsLive) return merged;
            return {
              ...merged,
              comments: live.comments || r.comments,
              image_name: live.image_name || r.image_name,
              ocr_text: r.ocr_text || live.ocr_text,
              ocr_status: r.ocr_status || live.ocr_status,
              grading_model: r.grading_model || live.grading_model || null,
              grading_host: r.grading_host || live.grading_host || null
            };
          });
        }
      }
      savedSnapshotRowsRef.current = rows;
      setSavedSnapshotRows(rows);
    } catch (_) {
      savedSnapshotRowsRef.current = [];
      setSavedSnapshotRows([]);
    }
    try {
      const res = await fetch(`${API_BASE_URL}/rubrics/${folder.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      const rubric = res.ok ? data.rubric : null;
      setSavedRubric(rubric && rubric.content ? { text: rubric.content } : null);
      setRegradeRuns(parseRegradeRuns(rubric && rubric.regrade_runs));
    } catch (_) {
      setSavedRubric(null);
    }
    return savedSnapshotRowsRef.current;
  };

  // Pre-fetch the extras as soon as a comments modal opens, so the
  // image / OCR / rubric panes are ready (All Comments or single-row View).
  useEffect(() => {
    if (showAllSavedComments || viewingSavedComment) ensureSavedExtras();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAllSavedComments, viewingSavedComment]);

  // Keyboard navigation for the detail panel: ← / → walk the student's
  // sheets. Escape closes the detail panel in All Comments, or the whole
  // single-student view (which always shows the 4 panes together).
  const ocrStudentRef = useRef(null);
  const ocrBaseRef = useRef('');
  const rubricBaseRef = useRef('');

  useEffect(() => {
    const text = savedDetail?.ocrText || '';
    const studentId = savedDetail?.student?.id ?? null;
    if (studentId !== ocrStudentRef.current) {
      ocrStudentRef.current = studentId;
      ocrBaseRef.current = text;
      setOcrDraft(text);
      setOcrSaveNote('');
      return;
    }
    setOcrDraft((current) => {
      const unchanged = current === ocrBaseRef.current;
      ocrBaseRef.current = text;
      return unchanged ? text : current;
    });
  }, [savedDetail?.student?.id, savedDetail?.ocrText]);

  useEffect(() => {
    setLinkedQuestion(null);
  }, [savedDetail?.student?.id]);

  useEffect(() => {
    const text = stripQuestionPaperText(savedRubric?.text || '');
    setRubricDraft((current) => {
      const unchanged = current === rubricBaseRef.current;
      rubricBaseRef.current = text;
      return unchanged ? text : current;
    });
  }, [folder.id, savedRubric?.text]);

  const saveOcrDraft = async () => {
    const student = savedDetail && savedDetail.student;
    if (!student || ocrSaving) return;
    if (ocrDraft === (savedDetail.ocrText || '')) return;
    setOcrSaving(true);
    setOcrSaveNote('');
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/assess/snapshots/ocr`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          folder_path: `rubrics/${folder.id}`,
          student_name: student.student_name,
          ocr_text: ocrDraft
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || `Save failed (${res.status})`);
      const target = normStudentName(student.student_name);
      setSavedSnapshotRows((prev) => {
        if (!Array.isArray(prev)) return prev;
        const next = prev.map((r) => (
          normStudentName(r.student_name) === target
            ? { ...r, ocr_text: ocrDraft, ocr_status: 'ok' }
            : r
        ));
        savedSnapshotRowsRef.current = next;
        return next;
      });
      setSavedDetail((prev) => (
        prev && prev.student === student
          ? { ...prev, ocrText: ocrDraft, ocrStatus: 'ok' }
          : prev
      ));
      setOcrSaveNote('Saved');
    } catch (err) {
      setOcrSaveNote(err.message || 'Save failed');
    } finally {
      setOcrSaving(false);
    }
  };

  const saveRubricDraft = async () => {
    if (rubricSaving) return;
    const full = (savedRubric && savedRubric.text) || '';
    if (rubricDraft === stripQuestionPaperText(full)) return;
    setRubricSaving(true);
    setRubricSaveNote('');
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/rubrics/${folder.id}/content`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ content: restorePaperNotes(rubricDraft, full) })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || `Save failed (${res.status})`);
      setSavedRubric({ text: restorePaperNotes(rubricDraft, full) });
      setRubricSaveNote('Saved');
    } catch (err) {
      setRubricSaveNote(err.message || 'Save failed');
    } finally {
      setRubricSaving(false);
    }
  };

  useEffect(() => {
    if (!savedDetail) return;
    const onKey = (e) => {
      const tag = e.target && e.target.tagName;
      if (tag === 'TEXTAREA' || tag === 'INPUT') return;
      if (e.key === 'ArrowLeft') {
        setSavedDetail(p => p ? { ...p, idx: Math.max(0, p.idx - 1) } : p);
      } else if (e.key === 'ArrowRight') {
        setSavedDetail(p => p ? { ...p, idx: Math.min(p.names.length - 1, p.idx + 1) } : p);
      } else if (e.key === 'Escape') {
        if (viewingSavedComment) {
          setViewingSavedComment(null);
          setSavedDetail(null);
        } else {
          setSavedDetail(null);
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [savedDetail, viewingSavedComment]);

  const closeSingleSavedComment = () => {
    setViewingSavedComment(null);
    setSavedDetail(null);
  };

  const openSingleSavedComment = (student, index) => {
    setViewingSavedComment({ student, index });
    // Same 4-pane layout as View All Comments: comments + image + OCR + rubric.
    openSavedDetail(student);
  };

  // From Re-checks: once saved results + scans are in, jump straight to that student.
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    autoOpenedRef.current = false;
  }, [folder.id]);

  // Revoke detail-panel blob: URLs when the panel closes or switches student.
  useEffect(() => {
    const studentId = savedDetail?.student?.id ?? null;
    const urls = savedDetail?.urls
      ? Object.values(savedDetail.urls).filter((u) => String(u).startsWith('blob:'))
      : [];
    const prev = savedDetailBlobsRef.current;
    if (prev.studentId != null && prev.studentId !== studentId) {
      prev.urls.forEach((u) => {
        try { URL.revokeObjectURL(u); } catch (_) { /* ignore */ }
      });
    }
    if (studentId == null && prev.urls.length) {
      prev.urls.forEach((u) => {
        try { URL.revokeObjectURL(u); } catch (_) { /* ignore */ }
      });
      savedDetailBlobsRef.current = { studentId: null, urls: [] };
      return;
    }
    savedDetailBlobsRef.current = { studentId, urls };
  }, [savedDetail]);

  useEffect(() => {
    if (!autoOpenStudent || autoOpenedRef.current) return;
    // Wait for scans fetch to finish so openSavedDetail can resolve image URLs.
    if (loading) return;
    if (!savedResults?.students?.length) {
      // Folder has no saved results (or still loading them). If it isn't a
      // completed folder, nothing to open — clear the hint.
      if (!folder.completed_at) {
        autoOpenedRef.current = true;
        onAutoOpenStudentConsumed?.();
      }
      return;
    }

    const images = String(autoOpenStudent.studentImages || '')
      .split(',')
      .map(s => s.trim().toLowerCase())
      .filter(Boolean);

    let student = null;
    if (autoOpenStudent.resultDataId != null && autoOpenStudent.resultDataId !== '') {
      student = savedResults.students.find(s => String(s.id) === String(autoOpenStudent.resultDataId));
    }
    if (!student && images.length) {
      student = savedResults.students.find(s => {
        const names = imageNamesFromComment(s.comments).map(n => n.toLowerCase());
        return images.some(img => names.includes(img));
      });
    }
    if (!student && autoOpenStudent.studentName) {
      const target = normStudentName(autoOpenStudent.studentName);
      student = savedResults.students.find(s => normStudentName(s.student_name) === target);
    }

    autoOpenedRef.current = true;
    if (student) {
      const index = savedResults.students.indexOf(student);
      openSingleSavedComment(student, index >= 0 ? index : 0);
    }
    onAutoOpenStudentConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenStudent, savedResults, loading, folder.completed_at]);

  // Image filenames for one saved student.
  // assessment_results.image_name (and the comment footer) store ONLY the
  // first front page — backs / extra sheets are implied. Previously we only
  // appended filename+1, so a student with 2+ physical sheets (4+ images)
  // showed just 2. Resolve the full list from, in order:
  //   1) OCR transcript "--- Image_x.jpg ---" headers (all pages)
  //   2) stored image_name / comment footer + each front's +1 back
  //   3) expand consecutively up to [N sheets] × 2 while files exist here
  //      and aren't another student's front
  const studentImageNames = (student, rows) => {
    const row = findSnapshotRow(rows, student);
    const primary = row && row.image_name
      ? String(row.image_name).split(',').map(s => s.trim()).filter(Boolean)
      : imageNamesFromComment(student.comments);

    const inFolder = new Map();
    for (const s of scans) {
      const fn = s.file_name || '';
      if (fn) inFolder.set(fn.toLowerCase(), fn);
    }
    const names = [];
    const add = (n) => {
      if (!n) return;
      const real = inFolder.get(String(n).toLowerCase()) || n;
      if (!names.some(x => x.toLowerCase() === String(real).toLowerCase())) {
        names.push(real);
      }
    };

    // 1) Prefer OCR page headers when those files are in this folder.
    const ocrSrc = (row && row.ocr_text) || '';
    const fromOcr = [];
    for (const m of String(ocrSrc).matchAll(/^---\s*([^\n]+?\.(?:jpe?g|png|gif|webp))\s*---\s*$/gim)) {
      const t = m[1].trim();
      if (t && !fromOcr.some(x => x.toLowerCase() === t.toLowerCase())) fromOcr.push(t);
    }
    if (fromOcr.length > 0) {
      for (const n of fromOcr) {
        if (inFolder.has(n.toLowerCase())) add(n);
      }
      if (names.length > 0) return names;
    }

    // 2) Stored front(s) + immediate back side.
    for (const n of primary) {
      add(n);
      const back = nextImageFilename(n);
      if (back && back !== n && inFolder.has(back.toLowerCase())) add(back);
    }

    // 3) Expand for multi-sheet students. Prefer Evaluate's sheet count from
    // the assess snapshot — the scans-folder footer was often wrongly saved
    // as "[1 sheet]" because finalize used image_name.length (always 1 front).
    const sheetCount = resolveSheetCount(student, rows);
    if (sheetCount && sheetCount > 1 && primary.length > 0) {
      const others = new Set();
      for (const peer of (savedResults && savedResults.students) || []) {
        if (peer === student) continue;
        const peerRow = findSnapshotRow(rows, peer);
        const peerPrimary = peerRow && peerRow.image_name
          ? String(peerRow.image_name).split(',').map(s => s.trim()).filter(Boolean)
          : imageNamesFromComment(peer.comments);
        for (const p of peerPrimary) {
          if (p) others.add(p.toLowerCase());
        }
      }

      const maxImages = sheetCount * 2;
      let cur = primary[0];
      for (let i = 0; i < maxImages; i++) {
        if (i > 0) {
          cur = nextImageFilename(cur);
          if (!cur || !inFolder.has(cur.toLowerCase())) break;
          if (others.has(cur.toLowerCase())) break;
        }
        add(cur);
      }
    }

    return names;
  };

  // Open the image + OCR + rubric detail panel for one saved student.
  const openSavedDetail = async (student, startName = null) => {
    const rows = await ensureSavedExtras();
    const row = findSnapshotRow(rows, student);
    const names = studentImageNames(student, rows);
    const idx = startName
      ? Math.max(0, names.findIndex(n => n.toLowerCase() === String(startName).toLowerCase()))
      : 0;
    setSavedDetail({
      student,
      names,
      idx,
      urls: {},
      ocrText: (row && row.ocr_text) || '',
      ocrStatus: (row && row.ocr_status) || ''
    });
    const token = localStorage.getItem('authToken');
    names.forEach(async (n) => {
      const scan = scans.find(s => (s.file_name || '').toLowerCase() === n.toLowerCase());
      if (!scan) return;
      try {
        // Prefer same-origin /raw → blob: URL so Screenshot can paint the
        // paper onto the capture canvas (S3 presigned URLs have no CORS and
        // taint/blank the middle pane).
        const rawResp = await fetch(`${API_BASE_URL}/scans/file/${scan.id}/raw`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (rawResp.ok) {
          const blob = await rawResp.blob();
          const blobUrl = URL.createObjectURL(blob);
          setSavedDetail(prev => {
            if (!prev || prev.student !== student) {
              URL.revokeObjectURL(blobUrl);
              return prev;
            }
            const prevUrl = prev.urls[n];
            if (prevUrl && prevUrl.startsWith('blob:')) {
              try { URL.revokeObjectURL(prevUrl); } catch (_) { /* ignore */ }
            }
            return { ...prev, urls: { ...prev.urls, [n]: blobUrl } };
          });
          return;
        }
        const res = await fetch(`${API_BASE_URL}/scans/file/${scan.id}/download`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok && data.downloadUrl) {
          setSavedDetail(prev => prev && prev.student === student
            ? { ...prev, urls: { ...prev.urls, [n]: data.downloadUrl } }
            : prev);
        }
      } catch (_) { /* leave the placeholder spinner */ }
    });
  };

  // Per-row eye button: open the folder's full-screen image preview jumped
  // to this student's first sheet (arrows/keys then walk the whole folder).
  const previewStudentImages = async (student) => {
    const rows = await ensureSavedExtras();
    const names = studentImageNames(student, rows);
    const scan = names
      .map(n => scans.find(s => (s.file_name || '').toLowerCase() === n.toLowerCase()))
      .find(Boolean);
    if (!scan) {
      setError(`No matching image found in this folder for ${student.student_name}`);
      return;
    }
    handlePreview(scan, scans.indexOf(scan));
  };
  // Vertical gap (px) between each student's table and the next. Drives BOTH
  // the preview (table marginBottom) and the DOCX spacer (px × 15 = twips), so
  // the two stay in sync. Print clones the preview DOM, so it follows too.
  const [tableGap, setTableGap] = useState(20);
  const [pageOverrides, setPageOverrides] = useState({});
  // Auto-packed students-per-sheet. null until the offscreen packer has run,
  // so the preview does not paint a fixed count and then jump.
  const [layoutSizes, setLayoutSizes] = useState(null);
  const layoutSizesRef = useRef(null);
  const packGenRef = useRef(0);
  const packInflightRef = useRef(null);
  const runPackRef = useRef(async () => null);
  const [packPending, setPackPending] = useState(false);
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const [showPrintStudentList, setShowPrintStudentList] = useState(false);
  const [showPrintLaterList, setShowPrintLaterList] = useState(false);
  useEffect(() => {
    if (showPrintPreview) ensureSavedExtras();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPrintPreview]);
  const [overflowInfo, setOverflowInfo] = useState({ pages: new Set(), suggestedMax: null });
  // Measured per-student alignment padding (px), applied to the SHORTER of
  // each student's front/back block so both faces end up exactly equal height
  // and their separators line up on the printed sheet. Keyed
  // `${gIdx}-${studentIdx}|${side}`. Filled by the align-measure effect below;
  // replaces the old estimate-based 18pt padding rows for the HTML/print path
  // (the DOCX export still uses paddingLines).
  const [alignPad, setAlignPad] = useState({});
  // Per-pair font size in points. Each "pair" is one printed sheet (front
  // + back). `pairFontSize[gIdx]` is the size shared by both faces of
  // pair gIdx; `backFontOverride[gIdx]` lets the back side diverge from
  // the front independently. Missing entries fall back to DEFAULT_PT.
  const DEFAULT_PT = 8;
  const FONT_SIZE_OPTIONS = [8, 9, 10, 11, 12, 13, 14, 16, 18];
  const [pairFontSize, setPairFontSize] = useState({});
  const [backFontOverride, setBackFontOverride] = useState({});
  // Global font size for every sheet that has no per-sheet font.
  // Changing it repacks every unlocked sheet from the first page.
  const [globalFontPt, setGlobalFontPt] = useState(DEFAULT_PT);
  const getFrontPt = (gIdx) => pairFontSize[gIdx] || globalFontPt;
  const getBackPt  = (gIdx) => backFontOverride[gIdx] || pairFontSize[gIdx] || globalFontPt;
  // Per-student override of the space AFTER a student's table. Keyed by a
  // stable student id (image/roll/name) so it survives regrouping. The gap
  // on an odd page sets both faces of that sheet; a per-student gap still wins.
  const [perStudentGap, setPerStudentGap] = useState({});
  const [pageGap, setPageGap] = useState({});
  // Keyed by student identity AND side ('front'|'back') so the front and back
  // gaps for the same student are independent. The sheet gap is set on the
  // odd (front) page and applied to both faces of that sheet.
  const gapKey = (sc, side) => String(sc.imageList || sc.image_no || sc.unique_code || sc.student_name || sc.name || '') + '|' + (side || 'front');
  const getStudentGap = (sc, side, sheet) => {
    const k = gapKey(sc, side);
    if (perStudentGap[k] != null) return perStudentGap[k];
    if (sheet != null && pageGap[sheet] != null) return pageGap[sheet];
    return tableGap;
  };
  const applyPackedSizes = (sizes) => {
    if (!Array.isArray(sizes)) return;
    setLayoutSizes(sizes.slice());
    setPackPending(false);
  };
  const setSheetGap = (sheet, raw) => {
    const parsed = parseInt(raw, 10);
    const gap = Math.max(0, Math.min(120, Number.isFinite(parsed) ? parsed : 0));
    const next = { ...pageGap };
    if (gap === tableGap) delete next[sheet];
    else next[sheet] = gap;
    setPageGap(next);
    runPackRef.current(sheet, { pageGap: next }).then(applyPackedSizes);
  };
  const adjustStudentGap = (sc, side, delta) => {
    const k = gapKey(sc, side);
    const sheet = sheetIndexForStudent(sc);
    const base = pageGap[sheet] != null ? pageGap[sheet] : tableGap;
    const cur = (perStudentGap[k] != null) ? perStudentGap[k] : base;
    // Range -50..50: positive = extra space after the student, negative =
    // pull the next student up (tighter than zero) to fit more on a page.
    const next = { ...perStudentGap, [k]: Math.max(-50, Math.min(50, cur + delta)) };
    setPerStudentGap(next);
    runPackRef.current(sheet, { perStudentGap: next }).then(applyPackedSizes);
  };

  useEffect(() => {
    loadScans();
    if (folder.completed_at) {
      loadSavedResults();
      loadRecheckRequests();
      loadFeedbackPacks();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folder.id]);

  // Build the email/username -> full-name map so the DOCX header can print a
  // teacher's NAME instead of their gmail. Seed from the current user first
  // (covers a teacher printing their own folder, who can't hit the elevated
  // /auth/teachers endpoint). For examiners/admins/management, also pull the
  // full teacher roster so another teacher's folder still resolves to a name.
  useEffect(() => {
    const map = new Map();
    const seed = (id, name) => { if (id && name) map.set(String(id).toLowerCase(), String(name).trim()); };
    if (currentUser) {
      const myName = (currentUser.name || currentUser.full_name || '').trim();
      seed(currentUser.email, myName);
      seed(currentUser.username, myName);
    }
    const elevated = currentUser && ['examiner', 'administrator', 'management'].includes(currentUser.role);
    if (!elevated) { setTeacherNameMap(map); return; }
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('authToken');
        const res = await fetch(`${API_BASE_URL}/auth/teachers`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) {
          const data = await res.json();
          for (const t of (data.teachers || [])) {
            const nm = (t.full_name || '').trim();
            if (!nm) continue;
            seed(t.email, nm);
            seed(t.username, nm);
          }
        }
      } catch (_) { /* non-fatal — falls back to stripping the @domain */ }
      if (!cancelled) setTeacherNameMap(map);
    })();
    return () => { cancelled = true; };
  }, [currentUser]);

  // Measure page overflow when preview is open
  useEffect(() => {
    if (!showPrintPreview) {
      setOverflowInfo({ pages: new Set(), suggestedMax: null });
      return;
    }
    let cancelled = false;
    const measure = () => {
      if (cancelled) return;
      const contentDivs = document.querySelectorAll('[data-page-id]');
      const overflowing = new Set();
      contentDivs.forEach(div => {
        if (div.scrollHeight > A4_CONTENT_H) {
          overflowing.add(div.getAttribute('data-page-id'));
        }
      });
      if (!cancelled) {
        setOverflowInfo({ pages: overflowing, suggestedMax: null });
      }
    };
    const run = async () => {
      if (document.fonts?.ready) await document.fonts.ready;
      setTimeout(measure, 100);
    };
    run();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPrintPreview, layoutSizes, tableGap, pageGap, perStudentGap, globalFontPt, pageOverrides, savedResults, pairFontSize, backFontOverride, alignPad, packPending]);

  // Measure each student's rendered front/back block heights and pad the
  // SHORTER side by the exact pixel difference, so the per-student separators
  // line up on both faces of the printed sheet. Replaces the old estimate
  // (chars-per-line + fixed 18pt padding rows) that drifted and made the back
  // (even) pages creep up. Blocks are measured WITHOUT their alignment padding
  // (it's a sibling), so a single pass converges — no feedback loop.
  useEffect(() => {
    if (!showPrintPreview) { setAlignPad({}); return; }
    let cancelled = false;
    const measure = () => {
      if (cancelled) return;
      const blocks = document.querySelectorAll('[data-align-block]');
      const byKey = {};
      blocks.forEach(el => {
        const attr = el.getAttribute('data-align-block') || '';
        const sep = attr.lastIndexOf('|');
        if (sep < 0) return;
        const key = attr.slice(0, sep);
        const side = attr.slice(sep + 1);
        (byKey[key] || (byKey[key] = {}))[side] = el.offsetHeight;
      });
      const next = {};
      Object.keys(byKey).forEach(key => {
        const hf = byKey[key].front || 0;
        const hb = byKey[key].back || 0;
        if (hf < hb) next[`${key}|front`] = hb - hf;
        else if (hb < hf) next[`${key}|back`] = hf - hb;
      });
      if (cancelled) return;
      setAlignPad(prev => {
        const allKeys = new Set([...Object.keys(prev), ...Object.keys(next)]);
        for (const k of allKeys) {
          if ((prev[k] || 0) !== (next[k] || 0)) return next;
        }
        return prev; // unchanged — avoid a needless re-render
      });
    };
    const run = async () => {
      if (document.fonts?.ready) await document.fonts.ready;
      setTimeout(measure, 120);
    };
    run();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPrintPreview, layoutSizes, tableGap, pageGap, perStudentGap, globalFontPt, pageOverrides, savedResults, pairFontSize, backFontOverride]);

  const loadScans = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('authToken');

      const response = await fetch(`${API_BASE_URL}/scans/${folder.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to load scans');
      }

      // Sort scans by file name (natural sorting)
      const sortedScans = (data.scans || []).sort((a, b) => {
        const nameA = a.file_name || '';
        const nameB = b.file_name || '';
        return nameA.localeCompare(nameB, undefined, { 
          numeric: true, 
          sensitivity: 'base' 
        });
      });
      setScans(sortedScans);
    } catch (err) {
      console.error('Error loading scans:', err);
      setError('Failed to load scans');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to extract section from roll number
  const extractSection = (studentName) => {
    const rollMatch = studentName.match(/\(([A-Za-z])/); // Match first letter inside parentheses
    return rollMatch ? rollMatch[1].toUpperCase() : '';
  };

  const loadSavedResults = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/checked-results/rubric/${folder.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Extract section from roll number and add to each student
        if (data.students) {
          data.students = data.students.map(student => ({
            ...student,
            section: extractSection(student.student_name)
          }));
          // Roll number order: A01, A02, A03, then B01. Marks do not move a student.
          data.students.sort(compareStudentsByRoll);
        }
        setSavedResults(data);
        setShowResults(true); // Auto-show results for completed folders
      }
    } catch (err) {
      console.error('Error loading saved results:', err);
    }
  };

  // Helper function to detect Urdu text
  const containsUrdu = (text) => {
    if (!text) return false;
    // Urdu Unicode range: \u0600-\u06FF (Arabic script used for Urdu)
    const urduRegex = /[\u0600-\u06FF]/;
    return urduRegex.test(text);
  };

  // Shared helper: one print-student record per saved result. Grouping onto
  // sheets is separate (layoutSizes + pageOverrides) so preview and DOCX
  // share the same slices.
  const buildPrintStudents = () => {
    if (!savedResults || !savedResults.students) return [];
    const cls = (savedResults.class || '').trim();
    const sub = (savedResults.subject || '').trim();
    const course = cls && sub ? `${cls}-${sub}` : (cls || sub);
    const dateStr = savedResults.checked_date
      ? new Date(savedResults.checked_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      : '';
    const teacher = teacherDisplayName(savedResults.teacher_names || folder?.created_by || '', teacherNameMap);
    const teacherLabel = teacher ? `"${teacher}"` : '';
    const folderClassLabel = [course, teacherLabel, dateStr].filter(Boolean).join(' ');
    const sortedStudents = [...savedResults.students].sort(compareStudentsByRoll);
    const picked = selectedPrintIds.size > 0
      ? sortedStudents.filter((student) => selectedPrintIds.has(String(student.id)))
      : [];
    const sourceStudents = picked.length > 0 ? picked : sortedStudents;
    const splitComments = sourceStudents.map(student => {
      const commentText = student.comments || 'No comments';
      const mid = Math.ceil(commentText.length / 2);
      let splitAt = commentText.lastIndexOf(' ', mid);
      if (splitAt <= 0) splitAt = commentText.indexOf(' ', mid);
      if (splitAt <= 0) splitAt = mid;
      // Detect a markdown Verification Table in the comment. When present,
      // we split the TABLE'S DATA ROWS in half — first half on the front
      // side, second half (plus the Grand Total row, if any) on the back —
      // so both faces of the printed sheet are used efficiently. The header
      // row is repeated on the back so it's still readable on its own. The
      // "after" text (e.g. "[Image_X.jpg] [2 sheets]") goes on the back too.
      const parsedTable = parseMarkdownTable(commentText);
      // Transform the 5-column AI Verification Table into the 3-column
      // print layout (Question | Deduction | obtained/total) and pull the
      // Grand Total out into a per-student score for the header line.
      const transformed = parsedTable ? transformVerificationTable(parsedTable) : { rows: [], grandTotal: null };
      let tableFront = null;
      let tableBack = null;
      if (parsedTable) {
        const totalRows = transformed.rows.length;
        if (totalRows <= 1) {
          tableFront = { headers: [], rows: transformed.rows, paddingLines: 0 };
          tableBack = null;
        } else {
          // Balance the split by ESTIMATED VISUAL LINES, not by row count.
          // A row with a 200-char deduction wraps to ~3 visual lines while
          // a row with a 30-char deduction is 1 line, so a 3/3 row split
          // can still produce 8 lines on the front and 5 on the back. The
          // estimator counts characters in the widest cell of each row
          // (always the Deduction column for the Verification Table) and
          // divides by an approximate chars-per-line.
          //
          // CHARS_PER_LINE matches what the real print column fits. The two
          // side-by-side columns take ~half the A4 width each, and at the
          // print font that column holds ~115 chars before wrapping — NOT 80.
          // Using 80 made long left-column rows look like 2-3 wrapped lines
          // when they actually render on 1, so the split dumped too few rows
          // on the long side (e.g. 5 long left / 9 short right).
          //
          // ROW_OVERHEAD accounts for each row's fixed cell padding + border,
          // which adds height regardless of text. Without it, a column with
          // many SHORT rows (each just "Correct" / "Not attempted") is taller
          // than its text-line count suggests, because every row still carries
          // padding. ~1 line-equivalent per row matches the observed 1.5x
          // height ratio between a 2-line and a 1-line row.
          const CHARS_PER_LINE = 115;
          const ROW_OVERHEAD = 1.0;
          const estimateRowLines = (cells) => {
            if (!cells || cells.length === 0) return 1 + ROW_OVERHEAD;
            let maxLen = 0;
            for (const c of cells) {
              const len = (c?.value || '').length;
              if (len > maxLen) maxLen = len;
            }
            return Math.max(1, Math.ceil(maxLen / CHARS_PER_LINE)) + ROW_OVERHEAD;
          };
          const perRowLines = transformed.rows.map(estimateRowLines);
          const totalLines  = perRowLines.reduce((a, b) => a + b, 0);
          // Front side gets a bold name/class/score header (always 1 line)
          // plus any non-empty lines from the table's "before" text. The
          // back side has no such overhead — it's the bare continuation of
          // the table. Subtract that fixed cost from the front's share so
          // (front_overhead + front_rows) ≈ back_rows, which is what makes
          // a 7-row, 1-header comment split 3/4 instead of 4/3.
          const beforeLineCount = parsedTable.before
            ? parsedTable.before.split(/\r?\n/).filter(l => l.trim()).length
            : 0;
          const FRONT_OVERHEAD = 1 + beforeLineCount;
          const target = Math.max(0, (totalLines - FRONT_OVERHEAD) / 2);
          // Find the row-index whose cumulative line count brings the
          // front side closest to half the remaining (post-overhead) total.
          let cum = 0;
          let splitIdx = 0;
          for (let i = 0; i < perRowLines.length; i++) {
            cum += perRowLines[i];
            if (cum >= target) {
              const includeDiff = Math.abs(cum - target);
              const excludeDiff = Math.abs((cum - perRowLines[i]) - target);
              splitIdx = excludeDiff < includeDiff ? i : i + 1;
              break;
            }
          }
          // Guarantee at least one row on each side.
          splitIdx = Math.max(1, Math.min(transformed.rows.length - 1, splitIdx || Math.ceil(transformed.rows.length / 2)));

          const frontRows = transformed.rows.slice(0, splitIdx);
          const backRows  = transformed.rows.slice(splitIdx);
          const frontLines = perRowLines.slice(0, splitIdx).reduce((a, b) => a + b, 0);
          const backLines  = perRowLines.slice(splitIdx).reduce((a, b) => a + b, 0);
          // Pad the shorter side using VISIBLE line counts (header +
          // before-text included on the front) so the per-student
          // separator sits at the same vertical position on both faces of
          // the printed sheet.
          const frontVisible = FRONT_OVERHEAD + frontLines;
          const backVisible  = backLines;
          const lineDiff = Math.abs(frontVisible - backVisible);
          tableFront = {
            headers: [],
            rows: frontRows,
            paddingLines: frontVisible < backVisible ? lineDiff : 0
          };
          tableBack = {
            headers: [],
            rows: backRows,
            paddingLines: backVisible < frontVisible ? lineDiff : 0
          };
        }
      }
      const row = {
        name: student.student_name,
        student_name: student.student_name,
        marks: student.marks,
        section: student.section,
        fullComment: commentText,
        firstHalf: commentText.substring(0, splitAt).trim(),
        secondHalf: commentText.substring(splitAt).trim(),
        hasUrdu: containsUrdu(commentText),
        table: parsedTable,
        tableFront,
        tableBack,
        // Prefer Evaluate's sheet count (assess snapshot) when the scans
        // footer was wrongly saved as "[1 sheet]" from image_name.length.
        sheetCount: resolveSheetCount(student, savedSnapshotRows)
          || (() => {
            if (!parsedTable || !parsedTable.after) return null;
            return sheetCountFromText(parsedTable.after);
          })(),
        imageList: (() => {
          if (!parsedTable || !parsedTable.after) return '';
          // First bracketed group that ISN'T the "[N sheets]" marker — that's
          // the image-name list. Handles both single and multi-image rows.
          const brackets = parsedTable.after.match(/\[([^\]]+)\]/g) || [];
          for (const b of brackets) {
            const content = b.slice(1, -1);
            if (!/^\d+\s*sheets?$/i.test(content)) return content;
          }
          return '';
        })(),
        // Grand total pulled out of the AI table — folded into the name
        // header as "(obtained/total)" instead of being a table row.
        grandTotal: transformed.grandTotal
      };
      const sheetLabel = row.sheetCount ? `${row.sheetCount} sheet${row.sheetCount === 1 ? '' : 's'}` : '';
      const imageLabel = row.imageList ? `[${String(row.imageList).replace(/Image_/gi, '')}]` : '';
      const target = normStudentName(student.student_name);
      const snaps = (savedSnapshotRows || []).filter((r) => normStudentName(r.student_name) === target);
      const gradeHost = student.grading_host || snaps.map((r) => r.grading_host).find(Boolean) || null;
      const gradeModel = student.grading_model || snaps.map((r) => r.grading_model).find(Boolean) || null;
      const machine = gradingMachine(gradeHost, gradeModel);
      const imageWithMachine = imageLabel && machine ? `${imageLabel} ${machine.letter}` : imageLabel;
      // Last graded (assessment / snapshot row) or last approved (print
      // list). The paper's test date stays in folderClassLabel; this stamp
      // is when THIS student's marks were last written or approved.
      const gradedMs = latestTimeMs(
        student.created_at,
        ...snaps.map((r) => r.created_at),
        ...snaps.map((r) => r.saved_at),
        savedResults.finalized_at,
        savedResults.updated_at
      );
      const gradedStamp = gradedMs ? formatGradedStamp(gradedMs) : '';
      const headerBase = [row.student_name, folderClassLabel, sheetLabel, imageWithMachine].filter(Boolean).join('-');
      row.headerLeft = gradedStamp ? `${headerBase} ${gradedStamp}` : headerBase;
      row.headerRight = row.grandTotal && (row.grandTotal.obtained || row.grandTotal.total)
        ? `${row.grandTotal.obtained || '0'}/${row.grandTotal.total || '0'}`
        : '';
      return row;
    });
    return splitComments;
  };

  const getSplitGroups = () => groupBySizes(buildPrintStudents(), layoutSizesRef.current);

  const sheetIndexForStudent = (sc) => {
    const id = printStudentId(sc);
    const students = buildPrintStudents();
    const sizes = layoutSizesRef.current || [];
    let cursor = 0;
    for (let s = 0; s < sizes.length; s++) {
      const n = sizes[s] || 0;
      for (let i = 0; i < n && cursor + i < students.length; i++) {
        if (printStudentId(students[cursor + i]) === id) return s;
      }
      cursor += n;
    }
    return 0;
  };

  // Measure offscreen and pack. `fromSheet` keeps every earlier sheet's
  // student list. Returns the new size list, or null if a newer pack won.
  runPackRef.current = (fromSheet, overrides = {}) => {
    const gen = ++packGenRef.current;
    setPackPending(true);
    const students = buildPrintStudents();
    const locks = overrides.locks !== undefined ? overrides.locks : pageOverrides;
    const pair = overrides.pairFontSize !== undefined ? overrides.pairFontSize : pairFontSize;
    const back = overrides.backFontOverride !== undefined ? overrides.backFontOverride : backFontOverride;
    const globalPt = overrides.globalFontPt !== undefined ? overrides.globalFontPt : globalFontPt;
    const gaps = overrides.perStudentGap !== undefined ? overrides.perStudentGap : perStudentGap;
    const gapDefault = overrides.tableGap !== undefined ? overrides.tableGap : tableGap;
    const sheetGaps = overrides.pageGap !== undefined ? overrides.pageGap : pageGap;
    const gapOf = (sc, side, sheet) => {
      const k = printStudentId(sc) + '|' + (side || 'front');
      if (gaps[k] != null) return gaps[k];
      if (sheetGaps[sheet] != null) return sheetGaps[sheet];
      return gapDefault;
    };
    let prefix = [];
    if (fromSheet > 0 && layoutSizesRef.current && layoutSizesRef.current.length) {
      prefix = layoutSizesRef.current.slice(0, fromSheet);
    }
    const job = (async () => {
      try {
        if (!students.length) {
          if (gen !== packGenRef.current) return null;
          layoutSizesRef.current = [];
          return [];
        }
        const sizes = await computeLayoutSizes({
          students,
          prefixSizes: prefix,
          locks,
          frontPtOf: (sheet) => pair[sheet] || globalPt,
          backPtOf: (sheet) => back[sheet] || pair[sheet] || globalPt,
          frontGapOf: (sc, sheet) => gapOf(sc, 'front', sheet),
          backGapOf: (sc, sheet) => gapOf(sc, 'back', sheet),
        });
        if (gen !== packGenRef.current) return null;
        layoutSizesRef.current = sizes;
        return sizes;
      } catch (err) {
        console.error('Page pack failed', err);
        if (gen !== packGenRef.current) return null;
        const fallback = students.map(() => 1);
        layoutSizesRef.current = fallback;
        return fallback;
      }
    })();
    packInflightRef.current = job;
    job.finally(() => {
      if (packInflightRef.current === job) packInflightRef.current = null;
    });
    return job;
  };

  // Full pack when the preview opens. Pages stay hidden until this resolves.
  useEffect(() => {
    if (!showPrintPreview) return undefined;
    let alive = true;
    runPackRef.current(0).then((sizes) => {
      if (!alive) return;
      applyPackedSizes(sizes);
    });
    return () => {
      alive = false;
      setPackPending(false);
    };
  }, [showPrintPreview, savedResults, savedSnapshotRows, selectedPrintIds]);

  // Flip the folder's "printed" status on the backend (drives the "🖨 Printed"
  // badge on the folder cards). Fire-and-forget + non-fatal: a failed mark
  // must never block the actual print/export. Used by BOTH the Print button
  // and Export DOCX so either action marks the folder printed.
  const markFolderPrinted = async () => {
    try {
      const token = localStorage.getItem('authToken');
      await fetch(`${API_BASE_URL}/scans/folders/${folder.id}/mark-printed`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (_) { /* non-fatal — printing still proceeds */ }
  };

  const printSelectionActive = selectedPrintIds.size > 0
    && (savedResults?.students || []).some((student) => selectedPrintIds.has(String(student.id)));

  const togglePrintStudent = (id) => {
    const key = String(id);
    setSelectedPrintIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleSavedSort = (key) => {
    setSavedSort(prev => {
      if (prev.key === key) {
        return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
      }
      // New column: marks default desc (high→low), name default asc (A→Z)
      return { key, dir: key === 'marks' ? 'desc' : 'asc' };
    });
  };

  const sortedSavedStudents = useMemo(() => {
    const list = savedResults?.students ? [...savedResults.students] : [];
    const { key, dir } = savedSort;
    const mul = dir === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      if (key === 'marks') {
        const am = parseFloat(a.marks);
        const bm = parseFloat(b.marks);
        const aNum = Number.isFinite(am) ? am : -Infinity;
        const bNum = Number.isFinite(bm) ? bm : -Infinity;
        if (aNum !== bNum) return (aNum - bNum) * mul;
        return String(a.student_name || '').localeCompare(String(b.student_name || ''), undefined, { sensitivity: 'base' });
      }
      // name
      const cmp = String(a.student_name || '').localeCompare(String(b.student_name || ''), undefined, { sensitivity: 'base' });
      if (cmp !== 0) return cmp * mul;
      const am = parseFloat(a.marks);
      const bm = parseFloat(b.marks);
      return (Number.isFinite(bm) ? bm : 0) - (Number.isFinite(am) ? am : 0);
    });
    return list;
  }, [savedResults, savedSort]);

  const savedSortIcon = (key) => {
    if (savedSort.key !== key) return faSort;
    return savedSort.dir === 'asc' ? faSortUp : faSortDown;
  };

  const exportToDOCX = async () => {
    if (!savedResults || !savedResults.students) return;

    try {
      // Mark folder as printed in backend
      await markFolderPrinted();

      // Helpers used while assembling the document. Each student becomes
      // either (a) a real Word Table when their comments contain a markdown
      // Verification Table, or (b) one Paragraph per line of plain text so
      // newlines actually render as line breaks instead of being collapsed
      // to a single line. Tables are split across the front and back of
      // the sheet so both sides of the paper are used.
      const docSections = [];
      // Word's TextRun size is in HALF-points (28 == 14pt), so the
      // per-page font size in points is doubled. Default 14pt mirrors
      // the legacy behavior when no per-page selector has been touched.
      const txtRun = (text, opts = {}) => new TextRun({
        text,
        size: opts.sizePt ? Math.round(opts.sizePt * 2) : 28,
        font: 'Jameel Noori Nastaleeq',
        bold: !!opts.bold,
        rightToLeft: !!opts.rtl
      });
      // Word line spacing that matches the PREVIEW's lineHeight
      // (sizePt × PREVIEW_LINE_MULT) so a Word row is exactly as tall as a
      // preview row. 1pt = 20 twips; falls back to DEFAULT_PT when unsized.
      const lineSpacing = (pt) => ({
        before: 0, after: 0,
        line: Math.round((pt || DEFAULT_PT) * PREVIEW_LINE_MULT * 20),
        lineRule: LineRuleType.EXACT
      });
      const txtPara = (text, opts = {}) => new Paragraph({
        children: [txtRun(text, opts)],
        spacing: lineSpacing(opts.sizePt),
        bidirectional: !!opts.rtl,
        pageBreakBefore: !!opts.pageBreakBefore
      });
      const separatorPara = () => new Paragraph({
        border: {
          bottom: { color: '000000', space: 1, style: BorderStyle.SINGLE, size: 6 }
        },
        // Thin rule to match the preview's <hr> (4px margins + 1px ≈ 9px ≈
        // 135 twips), NOT a full text line — otherwise Word packs looser.
        spacing: { before: 0, after: 0, line: 135, lineRule: LineRuleType.EXACT }
      });
      const buildHeaderParts = (sc) => ({
        leftText: sc.headerLeft || '',
        rightText: sc.headerRight || ''
      });
      const buildWordTable = (parsed, opts = {}) => {
        const { headers, rows } = parsed;
        const hasHeader = Array.isArray(headers) && headers.length > 0;
        const sizePt = opts.sizePt; // forward per-page font size to every cell
        // Columns auto-size to their widest content (Word's "AutoFit to
        // Contents" layout). No fixed widths on cells — the table itself
        // is set to AUTO width so each column expands only as much as it
        // needs and the deduction column takes whatever's left.
        const cellPara = (cell) => new Paragraph({
          children: [txtRun(cell.value, { bold: cell.bold, rtl: opts.rtl, sizePt })],
          spacing: lineSpacing(sizePt),
          bidirectional: !!opts.rtl
        });
        const headerRowBlock = hasHeader ? [new TableRow({
          tableHeader: true,
          children: headers.map((h) => new TableCell({
            shading: { fill: 'E5E7EB' },
            margins: { top: 30, bottom: 30, left: 90, right: 90 },
            children: [new Paragraph({
              children: [txtRun(h.value, { bold: true, rtl: opts.rtl, sizePt })],
              spacing: lineSpacing(sizePt)
            })]
          }))
        })] : [];
        const dataRows = rows.map(row => new TableRow({
          children: row.map((cell) => new TableCell({
            margins: { top: 30, bottom: 30, left: 90, right: 90 },
            children: [cellPara(cell)]
          }))
        }));
        return new Table({
          rows: [...headerRowBlock, ...dataRows],
          layout: TableLayoutType.AUTOFIT,
          width: { size: 0, type: WidthType.AUTO }
        });
      };

      // Build a list of docx blocks for one student on a given sheet side.
      // The first block (a bold header line) accepts pageBreakBefore so
      // callers can chain students across pages with the correct break.
      const buildStudentBlocks = (sc, side, { pageBreakBefore, sizePt, sheet } = {}) => {
        const blocks = [];
        const halfPt = sizePt ? Math.round(sizePt * 2) : 28; // Word size = pt × 2
        // Name/class/score header only on the FRONT side — the back is the
        // continuation of the same student's table so a repeated header
        // would just be noise. The pageBreakBefore flag still needs to
        // attach to the first block on the back to force a new page, so
        // we put it on the first table row's lead paragraph by emitting
        // a leading empty paragraph in that case.
        if (side === 'front') {
          const { leftText, rightText } = buildHeaderParts(sc);
          // A4 page width in twips ≈ 11909 with 288-twip margins both
          // sides → ~11333 twips of usable width. Tab stop near the right
          // edge so the grand-total sits above the per-question score
          // column on the printed page.
          const RIGHT_TAB_POS = 11200;
          blocks.push(new Paragraph({
            children: rightText ? [
              new TextRun({ text: leftText, bold: true, size: halfPt, font: 'Jameel Noori Nastaleeq', rightToLeft: sc.hasUrdu }),
              new TextRun({ text: '\t' }),
              new TextRun({ text: rightText, bold: true, size: halfPt, font: 'Jameel Noori Nastaleeq', rightToLeft: sc.hasUrdu })
            ] : [
              new TextRun({ text: leftText, bold: true, size: halfPt, font: 'Jameel Noori Nastaleeq', rightToLeft: sc.hasUrdu })
            ],
            tabStops: rightText ? [{ type: TabStopType.RIGHT, position: RIGHT_TAB_POS }] : undefined,
            spacing: lineSpacing(sizePt),
            bidirectional: !!sc.hasUrdu,
            pageBreakBefore: !!pageBreakBefore
          }));
        } else if (pageBreakBefore) {
          blocks.push(new Paragraph({
            children: [],
            spacing: lineSpacing(sizePt),
            pageBreakBefore: true
          }));
        }
        if (sc.table) {
          const sideTable = side === 'front' ? sc.tableFront : sc.tableBack;
          if (side === 'front' && sc.table.before) {
            sc.table.before.split(/\r?\n/).forEach(line => {
              if (line.trim()) blocks.push(txtPara(line, { rtl: sc.hasUrdu, sizePt }));
            });
          }
          if (sideTable && sideTable.rows.length > 0) {
            blocks.push(buildWordTable(sideTable, { rtl: sc.hasUrdu, sizePt }));
          }
          // Pad with blank paragraphs so the front and back of the sheet
          // line up — see paddingLines in getSplitGroups.
          if (sideTable && sideTable.paddingLines > 0) {
            for (let p = 0; p < sideTable.paddingLines; p++) {
              blocks.push(txtPara('', { rtl: sc.hasUrdu, sizePt }));
            }
          }
          // No "after" text rendering — the image-name + sheet-count metadata
          // it carries is now folded into the header line above.
        } else {
          // Non-table fallback: front gets firstHalf, back gets secondHalf.
          const text = side === 'front' ? sc.firstHalf : sc.secondHalf;
          String(text || '').split(/\r?\n/).forEach(line => {
            blocks.push(txtPara(line, { rtl: sc.hasUrdu, sizePt }));
          });
        }
        // Mirror the preview's 25px table marginBottom (25px ≈ 375 twips at
        // 96dpi) so a Word student's vertical footprint matches the preview.
        blocks.push(new Paragraph({
          children: [],
          spacing: { before: 0, after: 0, line: Math.max(1, Math.round(getStudentGap(sc, side, sheet) * 15)), lineRule: LineRuleType.EXACT }
        }));
        return blocks;
      };

      if (packInflightRef.current) await packInflightRef.current;
      if (!layoutSizesRef.current) {
        const sizes = await runPackRef.current(0);
        applyPackedSizes(sizes);
      }
      const groups = getSplitGroups();
      groups.forEach((group, gIdx) => {
        const frontPt = getFrontPt(gIdx);
        const backPt  = getBackPt(gIdx);
        // FRONT side — all students in the group
        group.forEach((sc, index) => {
          docSections.push(...buildStudentBlocks(sc, 'front', {
            pageBreakBefore: gIdx > 0 && index === 0,
            sizePt: frontPt,
            sheet: gIdx
          }));
          if (index < group.length - 1) {
            docSections.push(separatorPara());
          }
        });
        // BACK side — page break, then second half of every student
        group.forEach((sc, index) => {
          docSections.push(...buildStudentBlocks(sc, 'back', {
            pageBreakBefore: index === 0,
            sizePt: backPt,
            sheet: gIdx
          }));
          if (index < group.length - 1) {
            docSections.push(separatorPara());
          }
        });
      });

      // Create document with 0.2 inch margins (0.2 inch = 288 TWIPs)
      const doc = new Document({
        sections: [
          {
            properties: {
              page: {
                margin: {
                  top: 288,    // 0.2 inch
                  right: 288,  // 0.2 inch
                  bottom: 288, // 0.2 inch
                  left: 288    // 0.2 inch
                }
              }
            },
            children: docSections
          }
        ]
      });

      // Generate and download
      const blob = await Packer.toBlob(doc);
      saveAs(blob, `${folder.class}_${folder.subject}_Results.docx`);
      setSuccess('DOCX file exported successfully!');
      setTimeout(() => setSuccess(''), 3000);
      
      // Update local folder state to reflect printed status
      if (onBack) {
        // Trigger refresh of parent component
        setTimeout(() => {
          // This will refresh the folders list when user goes back
        }, 500);
      }
    } catch (err) {
      console.error('Error exporting to DOCX:', err);
      setError('Failed to export DOCX: ' + err.message);
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Validate all files
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    for (const file of files) {
      if (!allowedTypes.includes(file.type)) {
        setError(`${file.name} is not an image file. Only JPEG, PNG, GIF, and WEBP images are allowed.`);
        return;
      }
      if (file.size > 50 * 1024 * 1024) {
        setError(`${file.name} is too large. File size must be less than 50MB.`);
        return;
      }
    }

    try {
      setUploading(true);
      setError('');
      setSuccess('');
      const token = localStorage.getItem('authToken');

      const uploadResults = [];
      const BATCH_SIZE = 5; // Upload 5 files concurrently

      // Helper function to upload a single file
      const uploadFile = async (file) => {
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch(`${API_BASE_URL}/scans/${folder.id}/upload`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || 'Failed to upload file');
        }

        return { success: true, fileName: file.name };
      };

      // Upload files in batches
      for (let i = 0; i < files.length; i += BATCH_SIZE) {
        const batch = files.slice(i, i + BATCH_SIZE);
        const batchPromises = batch.map(file => 
          uploadFile(file)
            .then(result => result)
            .catch(err => {
              console.error(`Error uploading ${file.name}:`, err);
              return { success: false, fileName: file.name, error: err.message };
            })
        );

        // Wait for current batch to complete before starting next batch
        const batchResults = await Promise.all(batchPromises);
        uploadResults.push(...batchResults);
        
        // Update progress message
        const successCount = uploadResults.filter(r => r.success).length;
        const failCount = uploadResults.filter(r => !r.success).length;
        setSuccess(`Uploading... ${uploadResults.length}/${files.length} (${successCount} succeeded, ${failCount} failed)`);
        
        // Small delay between batches to prevent server overload
        if (i + BATCH_SIZE < files.length) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }

      // Show final summary message
      const finalSuccessCount = uploadResults.filter(r => r.success).length;
      const finalFailCount = uploadResults.filter(r => !r.success).length;
      
      if (finalSuccessCount > 0) {
        setSuccess(`Successfully uploaded ${finalSuccessCount} file${finalSuccessCount > 1 ? 's' : ''}${finalFailCount > 0 ? `, ${finalFailCount} failed` : ''}!`);
      } else {
        setError('Failed to upload all files');
      }

      loadScans(); // Refresh the list
      
      // Clear the file input
      e.target.value = '';
      
      // Clear messages after 5 seconds
      setTimeout(() => {
        setSuccess('');
        setError('');
      }, 5000);
    } catch (err) {
      console.error('Error uploading files:', err);
      setError('Failed to upload files: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const handlePreview = async (scan, index) => {
    try {
      setPreviewLoading(true);
      setPreviewIndex(index !== undefined ? index : scans.findIndex(s => s.id === scan.id));
      const token = localStorage.getItem('authToken');

      const response = await fetch(`${API_BASE_URL}/scans/file/${scan.id}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to get image URL');
      }

      // Pre-fetch the image bytes through the backend's same-origin
      // /raw streaming endpoint and turn them into a `blob:` URL before
      // handing them to AnnotatableImage. Fetching the S3 presigned URL
      // directly fails CORS (the bucket has no CORS config), so using
      // the presigned link straight as <img src> taints the html2canvas
      // capture and the saved PNG ends up showing only the SVG
      // annotation layer on a black background. Routing through the
      // backend keeps the response same-origin, the canvas stays clean,
      // and the composite (scan + annotations) saves correctly.
      let imageUrl = data.downloadUrl;
      try {
        const imgResp = await fetch(`${API_BASE_URL}/scans/file/${scan.id}/raw`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (imgResp.ok) {
          const blob = await imgResp.blob();
          imageUrl = URL.createObjectURL(blob);
        } else {
          console.warn('[ScansFolderView] Raw stream returned non-ok, falling back to presigned URL:', imgResp.status);
        }
      } catch (e) {
        console.warn('[ScansFolderView] Raw stream failed, falling back to presigned URL:', e);
      }

      setPreviewImage({ url: imageUrl, name: scan.file_name, id: scan.id });
    } catch (err) {
      console.error('Error loading preview:', err);
      setError('Failed to load image preview: ' + err.message);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handlePreviousImage = async (e) => {
    e.stopPropagation();
    if (previewIndex > 0) {
      const newIndex = previewIndex - 1;
      await handlePreview(scans[newIndex], newIndex);
    }
  };

  const handleNextImage = async (e) => {
    e.stopPropagation();
    if (previewIndex < scans.length - 1) {
      const newIndex = previewIndex + 1;
      await handlePreview(scans[newIndex], newIndex);
    }
  };

  // Revoke the previous blob: URL whenever previewImage.url changes or
  // unmounts, so the per-image image buffer doesn't pile up in memory.
  // Direct presigned S3 URLs (non-blob) need no cleanup.
  React.useEffect(() => {
    const url = previewImage?.url;
    return () => {
      if (url && url.startsWith('blob:')) URL.revokeObjectURL(url);
    };
  }, [previewImage?.url]);

  // Handle keyboard navigation in preview
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (!previewImage) return;
      
      if (e.key === 'ArrowLeft' && previewIndex > 0) {
        handlePreview(scans[previewIndex - 1], previewIndex - 1);
      } else if (e.key === 'ArrowRight' && previewIndex < scans.length - 1) {
        handlePreview(scans[previewIndex + 1], previewIndex + 1);
      } else if (e.key === 'Escape') {
        setPreviewImage(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [previewImage, previewIndex, scans]);

  const handleDownload = async (scan) => {
    try {
      const token = localStorage.getItem('authToken');

      const response = await fetch(`${API_BASE_URL}/scans/file/${scan.id}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to get download URL');
      }

      // Open the signed URL in a new tab
      window.open(data.downloadUrl, '_blank');
    } catch (err) {
      console.error('Error downloading file:', err);
      setError('Failed to download file: ' + err.message);
    }
  };

  const handleRename = async (scanId) => {
    if (!newFileName || newFileName.trim() === '') {
      setError('Please enter a valid file name');
      return;
    }

    try {
      setError('');
      const token = localStorage.getItem('authToken');

      const response = await fetch(`${API_BASE_URL}/scans/${scanId}/rename`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ newFileName })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to rename file');
      }

      setSuccess('File renamed successfully!');
      setEditingId(null);
      setNewFileName('');
      loadScans(); // Refresh the list
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error renaming file:', err);
      setError('Failed to rename file: ' + err.message);
    }
  };

  const showConfirm = (text, callback) => setConfirm({ show: true, text, onConfirm: callback });
  const handleConfirm = () => { 
    confirm.onConfirm(); 
    setConfirm({ show: false, text: '', onConfirm: null }); 
  };
  const handleCancel = () => setConfirm({ show: false, text: '', onConfirm: null });

  const handleDelete = async (scan) => {
    if (!canDeleteScans) return;
    showConfirm(`Are you sure you want to delete "${scan.file_name}"?`, async () => {

    try {
      setError('');
      const token = localStorage.getItem('authToken');

      const response = await fetch(`${API_BASE_URL}/scans/${scan.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to delete file');
      }

      setSuccess('File deleted successfully!');
      loadScans(); // Refresh the list
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error deleting file:', err);
      setError('Failed to delete file: ' + err.message);
    }
    });
  };

  const handleDeleteSelected = async () => {
    if (!canDeleteScans) return;
    if (selectedScans.size === 0) {
      setError('No images selected');
      return;
    }

    showConfirm(`Are you sure you want to delete ${selectedScans.size} selected image(s)? This action cannot be undone!`, async () => {
      try {
        setUploading(true);
        setError('');
        const token = localStorage.getItem('authToken');

        let successCount = 0;
        let failCount = 0;

        // Delete selected scans
        for (const scanId of selectedScans) {
          try {
            const response = await fetch(`${API_BASE_URL}/scans/${scanId}`, {
              method: 'DELETE',
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });

            if (response.ok) {
              successCount++;
            } else {
              failCount++;
            }
          } catch (err) {
            failCount++;
          }
        }

        if (successCount > 0) {
          setSuccess(`Deleted ${successCount} file(s) successfully${failCount > 0 ? `, ${failCount} failed` : ''}`);
          setSelectedScans(new Set()); // Clear selection
          loadScans(); // Refresh the list
        } else {
          setError('Failed to delete files');
        }

        setTimeout(() => {
          setSuccess('');
          setError('');
        }, 5000);
      } catch (err) {
        console.error('Error deleting selected:', err);
        setError('Failed to delete selected files: ' + err.message);
      } finally {
        setUploading(false);
      }
    });
  };

  const handleClearAll = async () => {
    if (!canDeleteScans) return;
    if (scans.length === 0) {
      setError('No images to delete');
      return;
    }

    showConfirm(`Are you sure you want to delete ALL ${scans.length} image(s) in this folder? This action cannot be undone!`, async () => {
      try {
      setUploading(true);
      setError('');
      const token = localStorage.getItem('authToken');

      let successCount = 0;
      let failCount = 0;

      // Delete all scans
      for (const scan of scans) {
        try {
          const response = await fetch(`${API_BASE_URL}/scans/${scan.id}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          if (response.ok) {
            successCount++;
          } else {
            failCount++;
          }
        } catch (err) {
          failCount++;
        }
      }

      if (successCount > 0) {
        setSuccess(`Deleted ${successCount} file(s) successfully${failCount > 0 ? `, ${failCount} failed` : ''}`);
        loadScans(); // Refresh the list
      } else {
        setError('Failed to delete files');
      }

      setTimeout(() => {
        setSuccess('');
        setError('');
      }, 5000);
    } catch (err) {
      console.error('Error clearing all:', err);
      setError('Failed to clear folder: ' + err.message);
    } finally {
      setUploading(false);
    }
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className="scans-folder-view">
      {/* Header */}
      <div className="folder-view-header">
        <button className="back-button" onClick={onBack}>
          <FontAwesomeIcon icon={faArrowLeft} /> Back to Folders
        </button>
        <div className="folder-details">
          <h2>{folderDisplayTitle(folder)} {folder.test_date ? new Date(folder.test_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : ''}</h2>
          {folder.assignment_name && (
            <div
              title={`Assignment: ${folder.assignment_name}`}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 4,
                padding: '2px 12px', borderRadius: 999,
                background: 'rgba(37, 99, 235, 0.12)',
                border: '1px solid rgba(37, 99, 235, 0.35)',
                color: '#1D4ED8', fontSize: '0.8rem', fontWeight: 700, width: 'fit-content'
              }}
            >
              <FontAwesomeIcon icon={faTag} />
              {folder.assignment_name}
            </div>
          )}
        </div>
      </div>

      {/* Toggle button for completed folders */}
      {folder.completed_at && savedResults && (
        <div style={{ marginBottom: '20px', textAlign: 'center' }}>
          <button 
            onClick={() => setShowResults(!showResults)}
            style={{
              padding: '12px 24px',
              background: showResults ? '#6c757d' : '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: 'pointer',
              transition: 'background 0.3s'
            }}
          >
            {showResults ? 'Show Images' : 'Show Saved Results'}
          </button>
        </div>
      )}

      {/* Saved Results Display */}
      {showResults && savedResults && (
        <div style={{
          background: 'var(--panel-bg)',
          padding: '20px',
          borderRadius: '12px',
          marginBottom: '20px',
          border: '2px solid #28a745'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h3 style={{ color: '#28a745', margin: 0 }}>✓ Saved Results</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <button
                onClick={() => setShowAllSavedComments(true)}
                style={{
                  padding: '8px 16px',
                  background: '#6366F1',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '14px'
                }}
              >
                <FontAwesomeIcon icon={faComments} />
                View All Comments
              </button>
              {canPrintPreview && (
              <button
                onClick={async () => {
                  layoutSizesRef.current = null;
                  setLayoutSizes(null);
                  await ensureSavedExtras();
                  setShowPrintPreview(true);
                }}
                style={{
                  padding: '8px 16px',
                  background: '#8B5CF6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '14px'
                }}
              >
                <FontAwesomeIcon icon={faEye} />
                {printSelectionActive ? `Preview (${selectedPrintIds.size})` : 'Preview'}
              </button>
              )}
              <button
                onClick={exportToDOCX}
                style={{
                  padding: '8px 16px',
                  background: '#3B82F6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '14px'
                }}
              >
                <FontAwesomeIcon icon={faPrint} />
                {printSelectionActive ? `Print to DOCX (${selectedPrintIds.size})` : 'Print to DOCX'}
              </button>
            </div>
          </div>
          <div style={{ marginBottom: '15px', color: 'var(--text-secondary)', fontSize: '14px' }}>
            <p><strong>Total Students:</strong> {savedResults.total_students}</p>
            <p><strong>Checked Date:</strong> {savedResults.checked_date} at {savedResults.checked_time}</p>
            {savedResults.teacher_names && <p><strong>Teachers:</strong> {savedResults.teacher_names}</p>}
            <p>
              {printSelectionActive
                ? `Print uses ${selectedPrintIds.size} ticked student${selectedPrintIds.size === 1 ? '' : 's'}.`
                : 'Tick students to print only those. None ticked prints everyone.'}
            </p>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table className="students-table" style={{ tableLayout: 'auto', minWidth: 900 }}>
              <thead>
                <tr>
                  <th style={{ width: 36, textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={sortedSavedStudents.length > 0 && sortedSavedStudents.every((student) => selectedPrintIds.has(String(student.id)))}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedPrintIds(new Set(sortedSavedStudents.map((student) => String(student.id))));
                        } else {
                          setSelectedPrintIds(new Set());
                        }
                      }}
                      title="Tick all students in this list for print"
                      style={{ width: 16, height: 16, cursor: 'pointer' }}
                    />
                  </th>
                  <th style={{ width: '18%', minWidth: 140 }}>
                    <button
                      type="button"
                      onClick={() => toggleSavedSort('name')}
                      title="Sort by name"
                      style={{
                        background: 'none', border: 'none', padding: 0, margin: 0,
                        cursor: 'pointer', font: 'inherit', fontWeight: 700,
                        color: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 6
                      }}
                    >
                      Name
                      <FontAwesomeIcon
                        icon={savedSortIcon('name')}
                        style={{ opacity: savedSort.key === 'name' ? 1 : 0.45, fontSize: 12 }}
                      />
                    </button>
                  </th>
                  <th style={{ width: '8%', minWidth: 70 }}>
                    <button
                      type="button"
                      onClick={() => toggleSavedSort('marks')}
                      title="Sort by marks"
                      style={{
                        background: 'none', border: 'none', padding: 0, margin: 0,
                        cursor: 'pointer', font: 'inherit', fontWeight: 700,
                        color: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 6
                      }}
                    >
                      Marks
                      <FontAwesomeIcon
                        icon={savedSortIcon('marks')}
                        style={{ opacity: savedSort.key === 'marks' ? 1 : 0.45, fontSize: 12 }}
                      />
                    </button>
                  </th>
                  <th style={{ width: 'auto' }}>Comments</th>
                  <th style={{ width: 280, minWidth: 280 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedSavedStudents.map((student, index) => {
                  const ticked = selectedPrintIds.has(String(student.id));
                  return (
                  <tr key={student.id} style={ticked ? { background: 'rgba(139, 92, 246, 0.08)' } : undefined}>
                    <td style={{ textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={ticked}
                        onChange={() => togglePrintStudent(student.id)}
                        title="Include this student in Print and Export DOCX"
                        style={{ width: 16, height: 16, cursor: 'pointer' }}
                      />
                    </td>
                    <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{student.student_name}</td>
                    <td style={{ whiteSpace: 'nowrap', fontWeight: 700, color: '#059669' }}>{student.marks !== '' && student.marks != null ? student.marks : '-'}</td>
                    <td style={{ overflow: 'hidden', maxWidth: 0 }}>
                      <div
                        style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          color: 'var(--text-secondary)'
                        }}
                        title="Click View to read the full comment"
                      >
                        {savedCommentOneLine(student.comments) || '-'}
                      </div>
                    </td>
                    <td style={{ overflow: 'visible', whiteSpace: 'nowrap', maxHeight: 'none' }}>
                      <div style={{ display: 'inline-flex', gap: '6px', alignItems: 'center', flexWrap: 'nowrap' }}>
                        <button
                          onClick={() => previewStudentImages(student)}
                          title="Preview this student's answer sheet image(s)"
                          style={{
                            padding: '6px 10px',
                            background: 'linear-gradient(135deg, #3B82F6, #2563EB)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontWeight: 600,
                            fontSize: '13px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            whiteSpace: 'nowrap',
                            flexShrink: 0
                          }}
                        >
                          <FontAwesomeIcon icon={faEye} />
                        </button>
                        <button
                          onClick={() => openSingleSavedComment(student, index)}
                          title="View this student's comments, image, OCR and rubric"
                          style={{
                            padding: '6px 12px',
                            background: 'linear-gradient(135deg, #8B5CF6, #6D28D9)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontWeight: 600,
                            fontSize: '13px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            whiteSpace: 'nowrap',
                            flexShrink: 0
                          }}
                        >
                          <FontAwesomeIcon icon={faComments} />
                          View
                        </button>
                        {(() => {
                          const existing = recheckByDataId.get(student.id);
                          if (existing) {
                            const chip = {
                              pending:  { label: 'Pending',  bg: 'rgba(245,158,11,0.15)', fg: '#B45309' },
                              approved: { label: existing.resolution === 'marks_updated' ? 'Approved: you can update on edupage' : 'Approved', bg: 'rgba(16,185,129,0.15)', fg: '#047857' },
                              rejected: { label: 'Rejected', bg: 'rgba(239,68,68,0.15)',  fg: '#B91C1C' }
                            }[existing.status] || { label: existing.status, bg: '#eee', fg: '#555' };
                            const tip = existing.resolution === 'marks_updated'
                              ? (existing.review_note || 'Approved: you can update on edupage')
                              : (existing.review_note ? `Examiner note: ${existing.review_note}` : `Re-check ${chip.label.toLowerCase()}`);
                            return (
                              <span
                                title={tip}
                                style={{
                                  padding: '6px 10px', background: chip.bg, color: chip.fg,
                                  borderRadius: '6px', fontWeight: 700, fontSize: '12px',
                                  display: 'inline-flex', alignItems: 'center', gap: '5px',
                                  whiteSpace: 'nowrap', flexShrink: 0
                                }}
                              >
                                <FontAwesomeIcon icon={faRotateRight} />
                                {chip.label}
                              </span>
                            );
                          }
                          return (
                            <button
                              onClick={() => openRecheckModal(student)}
                              title="Request a re-check of this student's paper (attach an image + explain the problem)"
                              style={{
                                padding: '6px 12px',
                                background: 'linear-gradient(135deg, #F59E0B, #D97706)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontWeight: 600,
                                fontSize: '13px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                whiteSpace: 'nowrap',
                                flexShrink: 0
                              }}
                            >
                              <FontAwesomeIcon icon={faRotateRight} />
                              Re-check
                            </button>
                          );
                        })()}
                        {renderFeedbackButton(student, true)}
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Upload section */}
      <div className="upload-section" style={{ display: showResults ? 'none' : 'flex' }}>
        <label htmlFor="image-upload" className="upload-button">
          <FontAwesomeIcon icon={faUpload} />
          {uploading ? ' Uploading...' : ' Upload Images'}
        </label>
        <input
          id="image-upload"
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
          multiple
          onChange={handleFileUpload}
          disabled={uploading}
          style={{ display: 'none' }}
        />
        {scans.length > 0 && canDeleteScans && (
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            cursor: 'pointer',
            padding: '8px 16px',
            background: 'var(--primary-color, #3B82F6)',
            color: 'white',
            borderRadius: '6px',
            fontWeight: '600'
          }}>
            {(() => {
              // Select All checks/unchecks ONLY the currently visible
              // (search-filtered) rows. With a search active, this means
              // you can search "blank" → Select All → Delete Selected to
              // remove all blank-named uploads, without touching others.
              const q = searchQuery.trim().toLowerCase();
              const visible = q
                ? scans.filter(s => (s.file_name || '').toLowerCase().includes(q))
                : scans;
              const allVisibleSelected = visible.length > 0
                && visible.every(s => selectedScans.has(s.id));
              return (
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedScans(prev => {
                        const next = new Set(prev);
                        visible.forEach(s => next.add(s.id));
                        return next;
                      });
                    } else {
                      setSelectedScans(prev => {
                        const next = new Set(prev);
                        visible.forEach(s => next.delete(s.id));
                        return next;
                      });
                    }
                  }}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
              );
            })()}
            Select All{searchQuery.trim() ? ' (filtered)' : ''}
          </label>
        )}
        {scans.length > 0 && canDeleteScans && (
          <>
            {selectedScans.size > 0 && (
              <button
                className="clear-all-button"
                onClick={handleDeleteSelected}
                disabled={uploading}
                style={{ background: '#f44336' }}
                title="Delete selected images"
              >
                <FontAwesomeIcon icon={faTrash} />
                Delete Selected ({selectedScans.size})
              </button>
            )}
            <button
              className="clear-all-button"
              onClick={handleClearAll}
              disabled={uploading}
              title="Delete all images in this folder"
            >
              <FontAwesomeIcon icon={faTrash} />
              Clear All ({scans.length})
            </button>
          </>
        )}
        {uploading && <FontAwesomeIcon icon={faSpinner} spin className="upload-spinner" />}
      </div>

      {/* Messages */}
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {/* Loading state */}
      {loading && !showResults && <div className="loading-message">Loading images...</div>}

      {/* No scans message */}
      {!loading && !showResults && scans.length === 0 && (
        <div className="no-scans">
          <p>No images uploaded yet. Upload your first image to get started.</p>
        </div>
      )}

      {/* Search bar — only rendered when there are scans to search through.
          Filters by file_name case-insensitively. The filtered list also
          drives Select All so checking the box selects only what's
          currently visible. */}
      {!loading && !showResults && scans.length > 0 && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          margin: '8px 0',
          padding: '8px 12px',
          background: 'var(--panel-bg, white)',
          border: '1px solid var(--border-color, #e2e8f0)',
          borderRadius: 8
        }}>
          <FontAwesomeIcon icon={faSearch} style={{ color: 'var(--text-secondary, #6b7280)' }} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search images by name…"
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              background: 'transparent',
              color: 'var(--text-primary)',
              fontSize: 14,
              padding: '4px 6px'
            }}
          />
          {searchQuery && (
            <>
              <span style={{ fontSize: 12, color: 'var(--text-secondary, #6b7280)' }}>
                {scans.filter(s => (s.file_name || '').toLowerCase().includes(searchQuery.toLowerCase())).length} of {scans.length}
              </span>
              <button
                onClick={() => setSearchQuery('')}
                title="Clear search"
                style={{
                  border: 'none',
                  background: 'transparent',
                  color: 'var(--text-secondary, #6b7280)',
                  cursor: 'pointer',
                  fontSize: 14,
                  padding: '4px 8px'
                }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </>
          )}
          {/* Grid / List view toggle — segmented control at the right edge of
              the search bar. Persisted via setView so the choice sticks. */}
          <div style={{ display: 'flex', border: '1px solid var(--border-color, #e2e8f0)', borderRadius: 6, overflow: 'hidden', flexShrink: 0 }}>
            <button
              onClick={() => setView('grid')}
              title="Grid view"
              style={{
                border: 'none', cursor: 'pointer', padding: '5px 10px', lineHeight: 1,
                background: viewMode === 'grid' ? 'linear-gradient(135deg, #3b82f6, #2563eb)' : 'transparent',
                color: viewMode === 'grid' ? 'white' : 'var(--text-secondary, #6b7280)', fontSize: 13
              }}
            >
              <FontAwesomeIcon icon={faTableCells} />
            </button>
            <button
              onClick={() => setView('list')}
              title="List view"
              style={{
                border: 'none', cursor: 'pointer', padding: '5px 10px', lineHeight: 1,
                background: viewMode === 'list' ? 'linear-gradient(135deg, #3b82f6, #2563eb)' : 'transparent',
                color: viewMode === 'list' ? 'white' : 'var(--text-secondary, #6b7280)', fontSize: 13
              }}
            >
              <FontAwesomeIcon icon={faList} />
            </button>
          </div>
        </div>
      )}

      {/* Scans list */}
      {!loading && !showResults && scans.length > 0 && (() => {
        const q = searchQuery.trim().toLowerCase();
        const filteredScans = q
          ? scans.filter(s => (s.file_name || '').toLowerCase().includes(q))
          : scans;
        if (filteredScans.length === 0) {
          return (
            <div className="no-scans" style={{ padding: '20px', textAlign: 'center', color: 'var(--text-secondary)' }}>
              No images match "<strong>{searchQuery}</strong>". <button onClick={() => setSearchQuery('')} style={{ border: 'none', background: 'transparent', color: '#3B82F6', cursor: 'pointer', textDecoration: 'underline' }}>Clear search</button>
            </div>
          );
        }
        return viewMode === 'list' ? (
          /* ── LIST VIEW ── compact rows, each with a small thumbnail ── */
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', padding: '4px 0 12px' }}>
            {filteredScans.map(scan => {
              const isSel = selectedScans.has(scan.id);
              return (
                <div
                  key={scan.id}
                  className="scan-list-item"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    border: isSel ? '2px solid #2563eb' : '1px solid var(--border-color, #e5e7eb)',
                    borderRadius: '8px',
                    background: 'var(--panel-bg, white)',
                    padding: '6px 10px',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.06)'
                  }}
                >
                  {canDeleteScans && (
                    <input
                      type="checkbox"
                      checked={isSel}
                      title="Select"
                      onChange={(e) => toggleSelect(scan, e)}
                      style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#2563eb', flexShrink: 0 }}
                    />
                  )}
                  {/* Small thumbnail — click opens the full-size preview modal */}
                  <div
                    onClick={() => handlePreview(scan, scans.indexOf(scan))}
                    title={`${scan.file_name}\nClick to preview`}
                    style={{ cursor: 'pointer', width: '42px', height: '56px', flexShrink: 0, borderRadius: '4px', overflow: 'hidden', background: 'var(--panel-bg, #f3f4f6)' }}
                  >
                    <ScanThumbnail scan={scan} />
                  </div>
                  {/* Name + meta (or inline rename) */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {editingId === scan.id ? (
                      <div className="edit-name-input" style={{ display: 'flex', gap: '4px' }}>
                        <input
                          type="text"
                          value={newFileName}
                          onChange={(e) => setNewFileName(e.target.value)}
                          placeholder="New name"
                          autoFocus
                          style={{ flex: 1, minWidth: 0, fontSize: '13px' }}
                        />
                        <button className="save-rename-btn" onClick={() => handleRename(scan.id)}>Save</button>
                        <button className="cancel-rename-btn" onClick={() => { setEditingId(null); setNewFileName(''); }}>✕</button>
                      </div>
                    ) : (
                      <>
                        <div title={scan.file_name} style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {scan.file_name}
                        </div>
                        <div style={{ fontSize: '11px', color: 'var(--text-muted, #888)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {formatFileSize(scan.file_size)} · {scan.uploader_name || scan.uploaded_by} · {formatDate(scan.uploaded_at)}
                        </div>
                      </>
                    )}
                  </div>
                  {/* Actions */}
                  <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                    <button className="action-btn" onClick={() => handlePreview(scan, scans.indexOf(scan))} title="Preview" style={{ background: '#9C27B0' }}>
                      <FontAwesomeIcon icon={faEye} />
                    </button>
                    <button className="action-btn download-btn" onClick={() => handleDownload(scan)} title="Download">
                      <FontAwesomeIcon icon={faDownload} />
                    </button>
                    <button className="action-btn edit-btn" onClick={() => { setEditingId(scan.id); setNewFileName(scan.file_name); }} title="Rename">
                      <FontAwesomeIcon icon={faEdit} />
                    </button>
                    {canDeleteScans && (
                      <button className="action-btn delete-btn" onClick={() => handleDelete(scan)} title="Delete">
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
          gap: '10px',
          padding: '4px 0 12px'
        }}>
          {filteredScans.map(scan => {
            const isSel = selectedScans.has(scan.id);
            return (
            <div
              key={scan.id}
              className="scan-grid-item"
              style={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                border: isSel ? '2px solid #2563eb' : '1px solid var(--border-color, #e5e7eb)',
                borderRadius: '10px',
                overflow: 'hidden',
                background: 'var(--panel-bg, white)',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
              }}
            >
              {/* Selection checkbox — overlay, top-left */}
              {canDeleteScans && (
                <input
                  type="checkbox"
                  checked={isSel}
                  title="Select"
                  onChange={(e) => toggleSelect(scan, e)}
                  style={{
                    position: 'absolute', top: '6px', left: '6px', zIndex: 2,
                    width: '18px', height: '18px', cursor: 'pointer', accentColor: '#2563eb'
                  }}
                />
              )}

              {/* Thumbnail — click opens the full-size preview modal */}
              <div
                onClick={() => handlePreview(scan, scans.indexOf(scan))}
                title={`${scan.file_name}\n${formatFileSize(scan.file_size)} · ${scan.uploader_name || scan.uploaded_by} · ${formatDate(scan.uploaded_at)}\nClick to preview`}
                style={{ cursor: 'pointer', width: '100%', aspectRatio: '3 / 4', background: 'var(--panel-bg, #f3f4f6)' }}
              >
                <ScanThumbnail scan={scan} />
              </div>

              {/* Name + size + compact actions */}
              <div style={{ padding: '6px 8px', display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }}>
                {editingId === scan.id ? (
                  <div className="edit-name-input" style={{ display: 'flex', gap: '4px' }}>
                    <input
                      type="text"
                      value={newFileName}
                      onChange={(e) => setNewFileName(e.target.value)}
                      placeholder="New name"
                      autoFocus
                      style={{ flex: 1, minWidth: 0, fontSize: '12px' }}
                    />
                    <button className="save-rename-btn" onClick={() => handleRename(scan.id)}>Save</button>
                    <button className="cancel-rename-btn" onClick={() => { setEditingId(null); setNewFileName(''); }}>✕</button>
                  </div>
                ) : (
                  <div
                    title={scan.file_name}
                    style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {scan.file_name}
                  </div>
                )}
                <div style={{ fontSize: '10px', color: 'var(--text-muted, #888)', whiteSpace: 'nowrap' }}>
                  {formatFileSize(scan.file_size)}
                </div>
                <div style={{ display: 'flex', gap: '4px' }}>
                  <button className="action-btn" onClick={() => handlePreview(scan, scans.indexOf(scan))} title="Preview" style={{ background: '#9C27B0', flex: 1 }}>
                    <FontAwesomeIcon icon={faEye} />
                  </button>
                  <button className="action-btn download-btn" onClick={() => handleDownload(scan)} title="Download" style={{ flex: 1 }}>
                    <FontAwesomeIcon icon={faDownload} />
                  </button>
                  <button className="action-btn edit-btn" onClick={() => { setEditingId(scan.id); setNewFileName(scan.file_name); }} title="Rename" style={{ flex: 1 }}>
                    <FontAwesomeIcon icon={faEdit} />
                  </button>
                  {canDeleteScans && (
                    <button className="action-btn delete-btn" onClick={() => handleDelete(scan)} title="Delete" style={{ flex: 1 }}>
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  )}
                </div>
              </div>
            </div>
            );
          })}
        </div>
        );
      })()}

      {/* Saved results — single-student View: same 4-pane layout as
          View All Comments (comments | image | OCR | rubric). */}
      {viewingSavedComment && (
        <div
          onClick={closeSingleSavedComment}
          className={savedDetail ? 'scans-split-overlay' : undefined}
          style={savedDetail ? undefined : {
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            data-recheck-capture-pane="1"
            className={savedDetail ? 'scans-split-overlay__comments' : undefined}
            style={savedDetail ? undefined : {
              background: 'var(--panel-bg, white)',
              borderRadius: '12px',
              maxWidth: '900px',
              width: '95%',
              maxHeight: '85vh',
              display: 'flex', flexDirection: 'column',
              boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
            }}
          >
            <div className={savedDetail ? 'scans-split-overlay__comments-header' : undefined} style={savedDetail ? undefined : {
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
              padding: '16px 20px', borderBottom: '1px solid var(--border-color, #e5e7eb)', flexWrap: 'wrap'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <h3 style={{ margin: 0, color: 'var(--text-primary)' }}>
                  {viewingSavedComment.student.student_name}
                </h3>
                <span style={{
                  background: 'rgba(16,185,129,0.12)', color: '#059669',
                  fontWeight: 800, padding: '4px 10px', borderRadius: 8, fontSize: 14
                }}>
                  Marks: {viewingSavedComment.student.marks !== '' && viewingSavedComment.student.marks != null ? viewingSavedComment.student.marks : '-'}
                </span>
                {renderFirstImageChip(viewingSavedComment.student)}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                {renderScreenshotButton(viewingSavedComment.student)}
                {(() => {
                  const student = viewingSavedComment.student;
                  const existing = recheckByDataId.get(student.id);
                  if (existing) {
                    const chip = {
                      pending:  { label: 'Re-check pending',  bg: 'rgba(245,158,11,0.15)', fg: '#B45309' },
                      approved: { label: existing.resolution === 'marks_updated' ? 'Approved: you can update on edupage' : 'Re-check approved', bg: 'rgba(16,185,129,0.15)', fg: '#047857' },
                      rejected: { label: 'Re-check rejected', bg: 'rgba(239,68,68,0.15)',  fg: '#B91C1C' }
                    }[existing.status] || { label: existing.status, bg: '#eee', fg: '#555' };
                    return (
                      <span
                        title={
                          existing.resolution === 'marks_updated'
                            ? (existing.review_note || 'Approved: you can update on edupage')
                            : (existing.review_note ? `Examiner note: ${existing.review_note}` : chip.label)
                        }
                        style={{
                          padding: '7px 12px', background: chip.bg, color: chip.fg,
                          borderRadius: 8, fontWeight: 700, fontSize: 13,
                          display: 'inline-flex', alignItems: 'center', gap: 6,
                          whiteSpace: 'nowrap'
                        }}
                      >
                        <FontAwesomeIcon icon={faRotateRight} />
                        {chip.label}
                      </span>
                    );
                  }
                  const draftN = draftCountFor(student);
                  return (
                    <button
                      onClick={() => openRecheckModal(student)}
                      title={draftN
                        ? `Open re-check with ${draftN} screenshot(s) already attached`
                        : 'Request a re-check of this student\'s paper'}
                      style={{
                        padding: '7px 14px',
                        background: 'linear-gradient(135deg, #F59E0B, #D97706)',
                        color: 'white', border: 'none', borderRadius: 8,
                        cursor: 'pointer', fontWeight: 700, fontSize: 13,
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        whiteSpace: 'nowrap'
                      }}
                    >
                      <FontAwesomeIcon icon={faRotateRight} />
                      {draftN > 0 ? `Re-check (${draftN})` : 'Re-check'}
                    </button>
                  );
                })()}
                {renderFeedbackButton(viewingSavedComment.student)}
                <button
                  onClick={closeSingleSavedComment}
                  style={{
                    padding: '7px 14px', background: '#ef4444', color: 'white',
                    border: 'none', borderRadius: 8, cursor: 'pointer',
                    fontWeight: 700, fontSize: 13,
                    display: 'inline-flex', alignItems: 'center', gap: 6
                  }}
                >
                  <FontAwesomeIcon icon={faTimes} /> Close
                </button>
              </div>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
              {renderSavedCommentBody(viewingSavedComment.student)}
            </div>
          </div>
        </div>
      )}

      {/* Saved results — View All Comments modal (every student's table). */}
      {showAllSavedComments && savedResults && (
        <div
          onClick={() => { setShowAllSavedComments(false); setSavedDetail(null); }}
          // When the image/OCR/rubric detail panel is open, anchor this modal
          // to the LEFT and reserve the right side for the panel — same
          // split-view the Check tab's All Comments modal uses. Inset below
          // the header / clear of the sidebar so Re-check stays visible.
          className={savedDetail ? 'scans-split-overlay' : undefined}
          style={savedDetail ? undefined : {
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            data-recheck-capture-pane="1"
            className={savedDetail ? 'scans-split-overlay__comments' : undefined}
            style={savedDetail ? undefined : {
              background: 'var(--panel-bg, white)',
              borderRadius: '12px',
              maxWidth: '1100px',
              width: '95%',
              maxHeight: '85vh',
              display: 'flex', flexDirection: 'column',
              boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
            }}
          >
            <div className={savedDetail ? 'scans-split-overlay__comments-header' : undefined} style={savedDetail ? undefined : {
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
              padding: '16px 20px', borderBottom: '1px solid var(--border-color, #e5e7eb)', flexWrap: 'wrap'
            }}>
              <div>
                <h3 style={{ margin: 0, color: 'var(--text-primary)' }}>
                  All Comments ({(savedResults.students || []).length})
                </h3>
                <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-secondary)' }}>
                  Showing verification tables for all saved results.
                </p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {savedDetail?.student && renderScreenshotButton(savedDetail.student)}
                {savedDetail?.student && !recheckByDataId.get(savedDetail.student.id) && (
                  <button
                    onClick={() => openRecheckModal(savedDetail.student)}
                    title="Request a re-check with any screenshots already attached"
                    style={{
                      padding: '7px 14px',
                      background: 'linear-gradient(135deg, #F59E0B, #D97706)',
                      color: 'white', border: 'none', borderRadius: 8,
                      cursor: 'pointer', fontWeight: 700, fontSize: 13,
                      display: 'inline-flex', alignItems: 'center', gap: 6
                    }}
                  >
                    <FontAwesomeIcon icon={faRotateRight} />
                    {draftCountFor(savedDetail.student) > 0
                      ? `Re-check (${draftCountFor(savedDetail.student)})`
                      : 'Re-check'}
                  </button>
                )}
                {savedDetail?.student && renderFeedbackButton(savedDetail.student)}
                <button
                  onClick={() => { setShowAllSavedComments(false); setSavedDetail(null); }}
                  style={{
                    padding: '7px 14px', background: '#ef4444', color: 'white',
                    border: 'none', borderRadius: 8, cursor: 'pointer',
                    fontWeight: 700, fontSize: 13,
                    display: 'inline-flex', alignItems: 'center', gap: 6
                  }}
                >
                  <FontAwesomeIcon icon={faTimes} /> Close
                </button>
              </div>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
              {(savedResults.students || []).map((student, idx) => {
                const isActive = !!(savedDetail && savedDetail.student === student);
                return (
                <div
                  key={student.id || idx}
                  style={{
                    padding: '14px 16px',
                    marginBottom: '10px',
                    background: isActive ? 'rgba(59,130,246,0.06)' : 'var(--surface, #F9FAFB)',
                    borderRadius: '10px',
                    border: `1px solid ${isActive ? 'rgba(59,130,246,0.4)' : 'var(--border-color, #E5E7EB)'}`,
                    borderLeft: `4px solid ${isActive ? '#3B82F6' : '#10B981'}`
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
                    <span style={{
                      background: 'linear-gradient(135deg, #3B82F6, #2563EB)',
                      color: 'white', width: 26, height: 26, borderRadius: 6,
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: 700, fontSize: 13, flexShrink: 0
                    }}>
                      {idx + 1}
                    </span>
                    <strong style={{ color: 'var(--text-primary)', fontSize: 15 }}>
                      {student.student_name}
                    </strong>
                    <span style={{
                      background: 'rgba(16,185,129,0.12)', color: '#059669',
                      fontWeight: 800, padding: '2px 10px', borderRadius: 8, fontSize: 13
                    }}>
                      Marks: {student.marks !== '' && student.marks != null ? student.marks : '-'}
                    </span>
                    {/* First image only — listing every sheet here stacked the
                        comments column so the comment body was off-screen. */}
                    {renderFirstImageChip(student)}
                  </div>
                  {renderSavedCommentBody(student)}
                </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Saved results — image + OCR + rubric detail panel. Opens from the
          image badges in All Comments, or automatically from the per-row
          View button; the comments modal shifts left to make room
          (mirrors the Check tab's 4-pane All Comments layout). */}
      {(showAllSavedComments || viewingSavedComment) && savedDetail && (() => {
        const d = savedDetail;
        const curName = d.names[d.idx] || null;
        const curUrl = curName ? d.urls[curName] : null;
        const paneHeader = (label) => (
          <div style={{
            padding: '10px 14px',
            borderBottom: '1px solid var(--border-color, #e5e7eb)',
            fontWeight: 800, fontSize: 13, color: 'var(--text-primary)',
            background: 'var(--surface-alt, #f8fafc)', flexShrink: 0,
            display: 'flex', alignItems: 'center', gap: 8, minHeight: 41
          }}>
            {label}
          </div>
        );
        // Pane shell shared by the three panes — equal width (flex: 1), full
        // height, flush against each other with just a 1px divider (same as
        // the Check tab's split layout).
        const cardStyle = {
          flex: 1, minWidth: 0, height: '100%',
          display: 'flex', flexDirection: 'column',
          background: 'var(--panel-bg, white)',
          overflow: 'hidden',
          borderLeft: '1px solid var(--border-color, #e5e7eb)'
        };
        return (
          <div
            data-recheck-capture-pane="1"
            className="scans-split-panes"
          >
            {/* Image card */}
            <div style={{ ...cardStyle, pointerEvents: 'auto' }}>
              {paneHeader(
                <>
                  <FontAwesomeIcon icon={faImage} style={{ color: '#2563EB' }} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {curName || 'Image'}{d.names.length > 1 ? ` (${d.idx + 1}/${d.names.length})` : ''}
                  </span>
                  {d.names.length > 1 && (
                    <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6 }}>
                      <button
                        onClick={() => setSavedDetail(p => p ? { ...p, idx: Math.max(0, p.idx - 1) } : p)}
                        disabled={d.idx === 0}
                        style={{ border: '1px solid var(--border-color, #ddd)', background: 'var(--panel-bg, white)', borderRadius: 6, cursor: d.idx === 0 ? 'not-allowed' : 'pointer', padding: '2px 8px', color: 'var(--text-primary)' }}
                      >
                        <FontAwesomeIcon icon={faChevronLeft} />
                      </button>
                      <button
                        onClick={() => setSavedDetail(p => p ? { ...p, idx: Math.min(p.names.length - 1, p.idx + 1) } : p)}
                        disabled={d.idx >= d.names.length - 1}
                        style={{ border: '1px solid var(--border-color, #ddd)', background: 'var(--panel-bg, white)', borderRadius: 6, cursor: d.idx >= d.names.length - 1 ? 'not-allowed' : 'pointer', padding: '2px 8px', color: 'var(--text-primary)' }}
                      >
                        <FontAwesomeIcon icon={faChevronRight} />
                      </button>
                    </span>
                  )}
                </>
              )}
              <div style={{ flex: 1, position: 'relative', minHeight: 0, background: '#1f2937' }}>
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {curName ? (
                    curUrl ? (
                      // Same zoom/pan viewer as the Check tab: scroll wheel
                      // zooms, drag pans, +/− toolbar, double-click resets.
                      <ZoomableImage url={curUrl} alt={curName} width="100%" height="100%" background="#1f2937" />
                    ) : (
                      <div style={{ color: '#e5e7eb', padding: 30, display: 'flex', alignItems: 'center', gap: 10 }}>
                        <FontAwesomeIcon icon={faSpinner} spin /> Loading {curName}…
                      </div>
                    )
                  ) : (
                    <div style={{ color: '#e5e7eb', padding: 30 }}>No image found in this folder for this student.</div>
                  )}
                </div>
                {/* Big overlay arrows for multi-sheet students — same as the
                    Check tab's image preview. Vertically centered over the
                    image, hidden at either end of the sheet list. */}
                {d.names.length > 1 && d.idx > 0 && (
                  <button
                    onClick={() => setSavedDetail(p => p ? { ...p, idx: Math.max(0, p.idx - 1) } : p)}
                    title="Previous image"
                    style={{
                      position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
                      width: 44, height: 44, borderRadius: '50%',
                      background: 'rgba(0,0,0,0.55)', color: 'white', border: 'none',
                      cursor: 'pointer', fontSize: 18,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      zIndex: 2
                    }}
                    onMouseOver={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.8)'}
                    onMouseOut={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.55)'}
                  >
                    <FontAwesomeIcon icon={faChevronLeft} />
                  </button>
                )}
                {d.names.length > 1 && d.idx < d.names.length - 1 && (
                  <button
                    onClick={() => setSavedDetail(p => p ? { ...p, idx: Math.min(p.names.length - 1, p.idx + 1) } : p)}
                    title="Next image"
                    style={{
                      position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                      width: 44, height: 44, borderRadius: '50%',
                      background: 'rgba(0,0,0,0.55)', color: 'white', border: 'none',
                      cursor: 'pointer', fontSize: 18,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      zIndex: 2
                    }}
                    onMouseOver={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.8)'}
                    onMouseOut={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.55)'}
                  >
                    <FontAwesomeIcon icon={faChevronRight} />
                  </button>
                )}
                {/* Sheet counter chip — top-right so it doesn't overlap the
                    ZoomableImage toolbar (bottom-center) or hint (top-center). */}
                {d.names.length > 1 && (
                  <div style={{
                    position: 'absolute', top: 10, right: 10,
                    background: 'rgba(0,0,0,0.65)', color: 'white',
                    padding: '3px 12px', borderRadius: 12, fontSize: 12, fontWeight: 700,
                    zIndex: 2, whiteSpace: 'nowrap', pointerEvents: 'none'
                  }}>
                    {d.idx + 1} / {d.names.length}
                  </div>
                )}
              </div>
            </div>
            {/* OCR card — this student's transcript only. */}
            <div style={{ ...cardStyle, pointerEvents: 'auto' }}>
              {paneHeader(
                <>
                  <FontAwesomeIcon icon={faAlignLeft} style={{ color: '#8B5CF6' }} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    OCR{d.student && d.student.student_name ? ` — ${d.student.student_name}` : ''}
                  </span>
                  <button
                    onClick={saveOcrDraft}
                    disabled={ocrSaving || ocrDraft === (d.ocrText || '')}
                    title="Save this student's OCR transcript. Ctrl+Enter"
                    style={{
                      marginLeft: 'auto',
                      padding: '4px 10px',
                      background: ocrDraft === (d.ocrText || '') ? '#e5e7eb' : '#7c3aed',
                      color: ocrDraft === (d.ocrText || '') ? '#6b7280' : 'white',
                      border: 'none', borderRadius: 6,
                      cursor: ocrSaving || ocrDraft === (d.ocrText || '') ? 'default' : 'pointer',
                      fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap'
                    }}
                  >
                    {ocrSaving ? 'Saving…' : 'Save'}
                  </button>
                </>
              )}
              <MarkedTextarea
                textareaRef={scansOcrRef}
                mark={linkedQuestion?.ocr || null}
                scrollTick={linkedQuestion?.tick || 0}
                dir="auto"
                value={savedSnapshotRows === null ? '' : ocrDraft}
                readOnly={savedSnapshotRows === null}
                placeholder={savedSnapshotRows === null ? 'Loading…' : 'No OCR transcript saved for this student yet. Type one and save.'}
                onChange={(e) => { setOcrDraft(e.target.value); setOcrSaveNote(''); }}
                onKeyDown={(e) => {
                  if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                    e.preventDefault();
                    saveOcrDraft();
                  }
                }}
                spellCheck={false}
                style={{
                  flex: 1, width: '100%', minHeight: 0, resize: 'none',
                  border: 'none', outline: 'none', padding: '14px 16px',
                  font: 'inherit', fontSize: 14, lineHeight: 1.8,
                  background: 'transparent', color: 'var(--text-primary)'
                }}
              />
              <div style={{ flexShrink: 0, padding: '4px 14px 8px', fontSize: 12, minHeight: 22, color: !ocrSaveNote ? 'var(--text-secondary, #6b7280)' : ocrSaveNote === 'Saved' ? '#059669' : '#b91c1c' }}>
                {ocrSaveNote && ocrSaveNote !== 'Saved' ? ocrSaveNote : (ocrSaveNote === 'Saved' ? 'Saved for this student' : 'Edits apply only to this student')}
              </div>
            </div>
            {/* Rubric card — carries the Close button for the whole panel. */}
            <div style={{ ...cardStyle, pointerEvents: 'auto' }}>
              {paneHeader(
                <>
                  <FontAwesomeIcon icon={faList} style={{ color: '#059669' }} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    Rubric{folder && folder.title ? ` — ${folder.title}` : ''}
                  </span>
                  <button
                    onClick={saveRubricDraft}
                    disabled={rubricSaving || rubricDraft === stripQuestionPaperText((savedRubric && savedRubric.text) || '')}
                    title="Save the folder rubric. This changes the marking guide for every student. Ctrl+Enter"
                    style={{
                      marginLeft: 'auto',
                      padding: '4px 10px',
                      background: rubricDraft === stripQuestionPaperText((savedRubric && savedRubric.text) || '') ? '#e5e7eb' : '#059669',
                      color: rubricDraft === stripQuestionPaperText((savedRubric && savedRubric.text) || '') ? '#6b7280' : 'white',
                      border: 'none', borderRadius: 6,
                      cursor: rubricSaving || rubricDraft === stripQuestionPaperText((savedRubric && savedRubric.text) || '') ? 'default' : 'pointer',
                      fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap'
                    }}
                  >
                    {rubricSaving ? 'Saving…' : 'Save'}
                  </button>
                  <button
                    onClick={() => {
                      if (viewingSavedComment) {
                        closeSingleSavedComment();
                      } else {
                        setSavedDetail(null);
                      }
                    }}
                    title={viewingSavedComment ? 'Close' : 'Close image, OCR & rubric'}
                    style={{
                      padding: '4px 10px', background: '#ef4444', color: 'white',
                      border: 'none', borderRadius: 6, cursor: 'pointer',
                      fontWeight: 700, fontSize: 12,
                      display: 'inline-flex', alignItems: 'center', gap: 5
                    }}
                  >
                    <FontAwesomeIcon icon={faTimes} /> Close
                  </button>
                </>
              )}
              <MarkedTextarea
                textareaRef={scansRubricRef}
                mark={linkedQuestion?.rubric || null}
                scrollTick={linkedQuestion?.tick || 0}
                dir="auto"
                value={rubricDraft}
                placeholder="No rubric text saved for this folder yet."
                onChange={(e) => { setRubricDraft(e.target.value); setRubricSaveNote(''); }}
                onKeyDown={(e) => {
                  if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                    e.preventDefault();
                    saveRubricDraft();
                  }
                }}
                spellCheck={false}
                style={{
                  flex: 1, width: '100%', minHeight: 0, resize: 'none',
                  border: 'none', outline: 'none', padding: '14px 16px',
                  font: 'inherit', fontSize: 13.5, lineHeight: 1.8,
                  background: 'transparent', color: 'var(--text-primary)'
                }}
              />
              <div style={{ flexShrink: 0, padding: '4px 14px 8px', fontSize: 12, minHeight: 22, color: !rubricSaveNote ? 'var(--text-secondary, #6b7280)' : rubricSaveNote === 'Saved' ? '#059669' : '#b91c1c' }}>
                {rubricSaveNote && rubricSaveNote !== 'Saved' ? rubricSaveNote : (rubricSaveNote === 'Saved' ? 'Saved for this folder' : 'Edits apply to every student in this folder')}
              </div>
            </div>
          </div>
        );
      })()}

      <ConfirmDialog
        confirm={confirm} 
        onConfirm={handleConfirm} 
        onCancel={handleCancel} 
      />

      {/* Image Preview Modal */}
      {previewImage && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px'
          }}
          onClick={() => setPreviewImage(null)}
        >
          {/* Close button — positioned below the page's top nav (52px
              header height + 12px gap) so the app's user-profile
              dropdown doesn't sit on top of it. */}
          <button
            onClick={() => setPreviewImage(null)}
            style={{
              position: 'absolute',
              top: 'calc(var(--header-height, 52px) + 12px)',
              right: '20px',
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              borderRadius: '50%',
              width: '50px',
              height: '50px',
              color: 'white',
              fontSize: '24px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background 0.3s',
              zIndex: 11
            }}
            onMouseOver={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.3)'}
            onMouseOut={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>

          {/* Image name and counter */}
          <div
            style={{
              position: 'absolute',
              top: 'calc(var(--header-height, 52px) + 12px)',
              left: '20px',
              background: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              padding: '10px 20px',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: 'bold'
            }}
          >
            {previewImage.name} ({previewIndex + 1} / {scans.length})
          </div>

          {/* Previous button */}
          {previewIndex > 0 && (
            <button
              onClick={handlePreviousImage}
              style={{
                position: 'absolute',
                left: '20px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                borderRadius: '50%',
                width: '60px',
                height: '60px',
                color: 'white',
                fontSize: '28px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background 0.3s'
              }}
              onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)'}
              onMouseOut={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
          )}

          {/* Next button */}
          {previewIndex < scans.length - 1 && (
            <button
              onClick={handleNextImage}
              style={{
                position: 'absolute',
                right: '20px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                borderRadius: '50%',
                width: '60px',
                height: '60px',
                color: 'white',
                fontSize: '28px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background 0.3s'
              }}
              onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)'}
              onMouseOut={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          )}

          {/* Image — zoomable / pannable / annotatable. Annotations live
              in component state only (cleared on close), so the source
              image is never modified. The toolbar's "Save" button exports
              the visible viewport + annotations as a PNG download. */}
          <div onClick={(e) => e.stopPropagation()}>
            <AnnotatableImage
              url={previewImage.url}
              alt={previewImage.name}
              width="80vw"
              height="85vh"
            />
          </div>

          {/* Instructions */}
          <div
            style={{
              position: 'absolute',
              bottom: '20px',
              background: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              padding: '10px 20px',
              borderRadius: '8px',
              fontSize: '14px'
            }}
          >
            Use ← → arrow keys or buttons to navigate • Click anywhere or press Esc to close
          </div>
        </div>
      )}

      {/* Print Preview Modal */}
      {canPrintPreview && showPrintPreview && (() => {
        const ready = Array.isArray(layoutSizes);
        const groups = ready ? getSplitGroups() : [];
        const roster = [...(savedResults?.students || [])].sort(compareStudentsByRoll);
        const laterRoster = laterGradedStudents(roster, savedSnapshotRows, regradeRuns);
        const chosenCount = roster.filter((student) => selectedPrintIds.has(String(student.id))).length;
        const laterChosen = laterRoster.filter((student) => selectedPrintIds.has(String(student.id))).length;
        const totalStudents = ready
          ? groups.reduce((n, g) => n + g.length, 0)
          : (printSelectionActive ? chosenCount : roster.length);
        const totalPages = groups.length * 2;
        const overflowPageNums = Array.from(overflowInfo.pages).map((id) => {
          const match = String(id).match(/^(front|back)-(\d+)$/);
          if (!match) return 0;
          const gIdx = parseInt(match[2], 10);
          return match[1] === 'front' ? gIdx * 2 + 1 : gIdx * 2 + 2;
        }).filter((n) => n > 0).sort((a, b) => a - b);
        const headerParts = (sc) => ({
          leftText: sc.headerLeft || '',
          rightText: sc.headerRight || ''
        });
        const renderTable = (sideTable) => {
          const hasHeader = Array.isArray(sideTable.headers) && sideTable.headers.length > 0;
          // table-layout: auto with no fixed col widths = browser auto-sizes
          // each column to its widest content (the Question and Score
          // columns stay tight; Deduction takes whatever's left).
          return (
            <table style={{ borderCollapse: 'collapse', tableLayout: 'auto', fontSize: 'inherit', lineHeight: 'inherit', marginBottom: 0 }}>
              {hasHeader && (
                <thead>
                  <tr>
                    {sideTable.headers.map((h, i) => (
                      <th key={i} style={{ border: '1px solid #000', padding: '2px 6px', background: '#E5E7EB', textAlign: 'left', whiteSpace: 'nowrap', fontSize: 'inherit', lineHeight: 'inherit', letterSpacing: 'normal', fontWeight: 'inherit', textTransform: 'none' }}>
                        {h.value}
                      </th>
                    ))}
                  </tr>
                </thead>
              )}
              <tbody>
                {sideTable.rows.map((row, ri) => (
                  <tr key={ri}>
                    {row.map((cell, ci) => {
                      // Question (first col) and Score (last col, when 3-col)
                      // stay on one line; Deduction wraps freely.
                      const isNarrow = ci === 0 || (row.length === 3 && ci === 2);
                      return (
                        <td key={ci}
                          contentEditable
                          suppressContentEditableWarning
                          style={{
                          border: '1px solid #000',
                          padding: '2px 6px',
                          // Force the cell to the container's chosen pt size.
                          // The app's global `table td { font-size: 0.8125rem }`
                          // shrinks ONLY the on-screen preview (the print iframe
                          // has no such rule), so preview text was ~9.75pt while
                          // Print/DOCX use the real 12pt — that mismatch made the
                          // PDF wrap to more lines, grow taller, and overflow.
                          // Inheriting here keeps preview = Print = DOCX.
                          fontSize: 'inherit',
                          lineHeight: 'inherit',
                          letterSpacing: 'normal',
                          fontWeight: cell.bold ? 700 : 400,
                          verticalAlign: 'top',
                          whiteSpace: isNarrow ? 'nowrap' : 'normal',
                          outline: 'none'
                        }}>
                          {cell.value}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          );
        };
        const renderStudent = (sc, side, key, matchKey, sheetIdx) => (
          <div key={key} style={{ direction: sc.hasUrdu ? 'rtl' : 'ltr' }}>
            {/* Measured content block (header + before + table). Its rendered
                height is compared front-vs-back so the shorter side gets padded
                to match — see the align-measure effect. It EXCLUDES the
                alignment padding + gap below so the measurement stays stable. */}
            <div data-align-block={`${matchKey}|${side}`}>
            {side === 'front' && (() => {
              const { leftText, rightText } = headerParts(sc);
              return (
                <div style={{ fontWeight: 700, marginBottom: '2px', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
                  <span contentEditable suppressContentEditableWarning style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', outline: 'none' }}>{leftText}</span>
                  {rightText && <span contentEditable suppressContentEditableWarning style={{ flexShrink: 0, outline: 'none' }}>{rightText}</span>}
                </div>
              );
            })()}
            {sc.table ? (
              <>
                {side === 'front' && sc.table.before && (
                  <div contentEditable suppressContentEditableWarning style={{ whiteSpace: 'pre-wrap', marginBottom: '4px', outline: 'none' }}>{sc.table.before}</div>
                )}
                {(() => {
                  const sideTable = side === 'front' ? sc.tableFront : sc.tableBack;
                  if (!sideTable || sideTable.rows.length === 0) return null;
                  // Height matching is now done by measured px padding below
                  // (see the align-measure effect), not by fixed-height rows.
                  return renderTable(sideTable);
                })()}
                {/* "after" text is omitted — its image-name + sheet-count
                    info is already in the header line above. */}
              </>
            ) : (
              <div contentEditable suppressContentEditableWarning style={{ whiteSpace: 'pre-wrap', outline: 'none' }}>{side === 'front' ? sc.firstHalf : sc.secondHalf}</div>
            )}
            </div>
            {/* Measured alignment padding — pads the shorter face by the exact
                px difference so the separator below lines up with the other
                face. Replaces the old fixed-18pt padding rows for the HTML/print
                path (DOCX still uses paddingLines). */}
            {(alignPad[`${matchKey}|${side}`] || 0) > 0 && (
              <div aria-hidden="true" style={{ height: `${alignPad[`${matchKey}|${side}`]}px` }} />
            )}
            {/* Adjustable space after this student. The blue −/+ control is
                hidden from Print (data-no-print); the gap height itself is
                kept. Driven by getStudentGap so preview, Print and DOCX agree.
                A NEGATIVE gap can't be a CSS height, so it's applied as a
                negative bottom margin instead — that pulls the next student up
                (tighter than zero) rather than adding space. */}
            <div style={{ height: `${Math.max(0, getStudentGap(sc, side, sheetIdx))}px`, marginBottom: getStudentGap(sc, side, sheetIdx) < 0 ? `${getStudentGap(sc, side, sheetIdx)}px` : undefined, position: 'relative', flexShrink: 0 }}>
              <div data-no-print contentEditable={false} style={{ position: 'absolute', top: 0, right: 0, transform: `scale(${1 / A4_SCALE})`, transformOrigin: 'top right', display: 'flex', alignItems: 'center', gap: '2px', background: 'rgba(59,130,246,0.14)', border: '1px solid rgba(59,130,246,0.4)', borderRadius: '3px', padding: '0 2px', lineHeight: 1 }}>
                <button onClick={() => adjustStudentGap(sc, side, -5)} title={`Less space after this student (${side} only)`} style={{ cursor: 'pointer', border: 'none', background: 'transparent', fontWeight: 700, fontSize: '13px', padding: '0 3px', color: '#2563EB' }}>−</button>
                <span style={{ fontSize: '9px', color: '#2563EB', minWidth: '14px', textAlign: 'center' }}>{getStudentGap(sc, side, sheetIdx)}</span>
                <button onClick={() => adjustStudentGap(sc, side, 5)} title={`More space after this student (${side} only)`} style={{ cursor: 'pointer', border: 'none', background: 'transparent', fontWeight: 700, fontSize: '13px', padding: '0 3px', color: '#2563EB' }}>+</button>
              </div>
            </div>
          </div>
        );
        const pageCard = (pageId, items, side, sizePt = DEFAULT_PT, sheetIdx = 0) => (
          <div style={{ width: A4_DISP_W, height: A4_DISP_H, overflow: 'hidden', position: 'relative', borderRadius: '2px', boxShadow: '0 2px 10px rgba(0,0,0,0.2)' }}>
            <div data-print-page="true" style={{
              width: A4_FULL_W, height: A4_FULL_H, padding: A4_MARGIN_PX,
              transform: `scale(${A4_SCALE})`, transformOrigin: 'top left',
              background: 'white', overflow: 'hidden',
              // Line height scales with the chosen font size via the shared
              // PREVIEW_LINE_MULT (17.2pt at the 12pt reference). The DOCX
              // export uses the SAME multiplier for its Word line spacing, so
              // the preview and the exported document stay in lockstep.
              fontFamily: "'Jameel Noori Nastaleeq', serif",
              fontSize: `${sizePt}pt`,
              lineHeight: `${sizePt * PREVIEW_LINE_MULT}pt`,
              color: '#000'
            }}>
              <div data-page-id={pageId}>
                {items.map((sc, idx) => (
                  <div key={idx}>
                    {/* matchKey strips the front-/back- prefix so a student's
                        two faces share one key (`${gIdx}-${idx}`). */}
                    {renderStudent(sc, side, idx, `${pageId.replace(/^(front|back)-/, '')}-${idx}`, sheetIdx)}
                    {idx < items.length - 1 && <hr style={{ border: 'none', borderBottom: '1px solid #000', margin: '4px 0' }} />}
                  </div>
                ))}
              </div>
            </div>
            {!packPending && overflowInfo.pages.has(pageId) && (
              <div style={{
                position: 'absolute', bottom: 0, left: 0, right: 0, height: '24px',
                background: 'linear-gradient(transparent, rgba(239, 68, 68, 0.25))',
                borderTop: '2px dashed #EF4444',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <span style={{ fontSize: '10px', color: '#EF4444', fontWeight: '700', fontFamily: 'system-ui, sans-serif' }}>
                  ⚠ Exceeds A4
                </span>
              </div>
            )}
          </div>
        );
        return (
          <div
            style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0, 0, 0, 0.7)',
              // Anchor the panel just below the app's fixed navbar (instead of
              // centring) so a tall modal doesn't slide up behind the header.
              display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
              zIndex: 9999,
              padding: '10px',
              paddingTop: 'calc(var(--header-height, 52px) + 8px)'
            }}
            onClick={() => { setShowPrintStudentList(false); setShowPrintLaterList(false); setShowPrintPreview(false); }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: '#E5E7EB', borderRadius: '12px',
                // At least 70vw, but never narrower than two A4 pages
                // side-by-side (so small screens still fit front+back).
                width: '100%', maxWidth: `max(70vw, ${A4_DISP_W * 2 + 100}px)`,
                maxHeight: 'calc(100vh - var(--header-height, 52px) - 16px)',
                display: 'flex', flexDirection: 'column', overflow: 'hidden',
                position: 'relative'
              }}
            >
              {/* Header */}
              <div style={{ padding: '10px 16px', background: '#1F2937', color: 'white' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
                    <h3 style={{ margin: 0, fontSize: '1.05rem', whiteSpace: 'nowrap' }}>
                      Print Preview ({totalStudents} student{totalStudents === 1 ? '' : 's'}{printSelectionActive ? ' selected' : ''}{ready ? `, ${totalPages} pages` : ', fitting…'})
                    </h3>
                    <button
                      type="button"
                      onClick={() => {
                        setShowPrintStudentList((open) => !open);
                        setShowPrintLaterList(false);
                      }}
                      title="Choose which students to print"
                      style={{
                        padding: '6px 12px',
                        background: showPrintStudentList || printSelectionActive ? '#7C3AED' : 'rgba(255,255,255,0.15)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '13px',
                        flexShrink: 0
                      }}
                    >
                      <FontAwesomeIcon icon={faList} style={{ marginRight: '6px' }} />
                      Students{printSelectionActive ? ` (${chosenCount})` : ''}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowPrintLaterList((open) => !open);
                        setShowPrintStudentList(false);
                      }}
                      title="Students graded after the class run"
                      style={{
                        padding: '6px 12px',
                        background: showPrintLaterList ? '#0F766E' : 'rgba(255,255,255,0.15)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '13px',
                        flexShrink: 0
                      }}
                    >
                      <FontAwesomeIcon icon={faClock} style={{ marginRight: '6px' }} />
                      Graded later{laterRoster.length ? ` (${laterRoster.length})` : ''}
                    </button>
                    {!packPending && overflowInfo.pages.size > 0 && (
                      <div style={{
                        padding: '5px 12px',
                        background: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.45)',
                        borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '8px',
                        fontSize: '12px', color: '#FCA5A5', minWidth: 0
                      }}>
                        <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: '#F87171', flexShrink: 0 }} />
                        <span>
                          <strong>Page {overflowPageNums.join(', ')}</strong> {overflowPageNums.length === 1 ? 'exceeds' : 'exceed'} the boundary.
                        </span>
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <label style={{ fontSize: '13px', whiteSpace: 'nowrap' }}>Gap (px):</label>
                    <input
                      type="number" min="0" max="120"
                      value={tableGap}
                      onChange={(e) => {
                        const gap = Math.max(0, parseInt(e.target.value, 10) || 0);
                        setTableGap(gap);
                        runPackRef.current(0, { tableGap: gap }).then(applyPackedSizes);
                      }}
                      title="Default space between students. An odd page's Gap field overrides this for that sheet, front and back."
                      style={{
                        width: '50px', padding: '4px 6px', border: '1px solid #555',
                        borderRadius: '4px', fontSize: '13px', textAlign: 'center',
                        background: '#374151', color: 'white'
                      }}
                    />
                    <label style={{ fontSize: '13px', whiteSpace: 'nowrap' }}>Font (pt):</label>
                    <select
                      value={globalFontPt}
                      onChange={(e) => {
                        const pt = parseInt(e.target.value, 10);
                        setGlobalFontPt(pt);
                        runPackRef.current(0, { globalFontPt: pt }).then(applyPackedSizes);
                      }}
                      title="Font size for every sheet that has no per-sheet font. Unlocked sheets reflow to fit A4."
                      style={{
                        padding: '4px 6px', border: '1px solid #555',
                        borderRadius: '4px', fontSize: '13px',
                        background: '#374151', color: 'white'
                      }}
                    >
                      {FONT_SIZE_OPTIONS.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                    <button
                      onClick={() => { if (!ready) return; markFolderPrinted(); printPreviewPages(); }}
                      disabled={!ready}
                      title="Print these pages directly (or Save as PDF) — no Word needed"
                      style={{ padding: '8px 16px', background: '#10B981', color: 'white', border: 'none', borderRadius: '6px', cursor: ready ? 'pointer' : 'default', fontWeight: '600', fontSize: '13px', opacity: ready ? 1 : 0.5 }}
                    >
                      <FontAwesomeIcon icon={faPrint} style={{ marginRight: '6px' }} /> Print
                    </button>
                    <button
                      onClick={() => { setShowPrintStudentList(false); setShowPrintLaterList(false); setShowPrintPreview(false); exportToDOCX(); }}
                      style={{ padding: '8px 16px', background: '#3B82F6', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '13px' }}
                    >
                      <FontAwesomeIcon icon={faPrint} style={{ marginRight: '6px' }} /> Export DOCX
                    </button>
                    <button
                      onClick={() => { setShowPrintStudentList(false); setShowPrintLaterList(false); setShowPrintPreview(false); }}
                      style={{ padding: '8px 12px', background: 'rgba(255,255,255,0.15)', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '16px' }}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>
                </div>
                <div style={{ marginTop: '4px', fontSize: '12px', color: '#9CA3AF' }}>
                  ✏️ Tip: click any cell to edit its text, then <strong>Print</strong>. Edits are included in Print only — they are not saved, and Export DOCX regenerates from the original data.
                </div>
              </div>
              {showPrintStudentList && (
                <div
                  style={{
                    position: 'absolute',
                    top: 78,
                    left: 16,
                    width: 'max-content',
                    maxWidth: 'calc(100% - 32px)',
                    zIndex: 30,
                    background: '#fff',
                    color: '#111827',
                    borderRadius: 8,
                    boxShadow: '0 16px 40px rgba(0,0,0,0.35)',
                    border: '1px solid #E5E7EB'
                  }}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 12px',
                    borderBottom: '1px solid #E5E7EB',
                    position: 'sticky',
                    top: 0,
                    background: '#fff',
                    fontSize: 13,
                    fontWeight: 700
                  }}>
                    <input
                      type="checkbox"
                      checked={roster.length > 0 && chosenCount === roster.length}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedPrintIds(new Set(roster.map((student) => String(student.id))));
                        } else {
                          setSelectedPrintIds(new Set());
                        }
                      }}
                      title="Tick every student"
                      style={{ width: 16, height: 16, cursor: 'pointer' }}
                    />
                    <span style={{ flex: 1 }}>All students</span>
                    <span style={{ fontWeight: 600, color: '#6B7280' }}>
                      {printSelectionActive ? `${chosenCount} ticked` : 'None ticked prints all'}
                    </span>
                  </div>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(${Math.max(3, Math.ceil(roster.length / 8))}, max-content)`,
                    gap: 4,
                    padding: 8
                  }}>
                    {roster.map((student) => {
                      const ticked = selectedPrintIds.has(String(student.id));
                      return (
                        <label
                          key={student.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            padding: '6px 8px',
                            cursor: 'pointer',
                            background: ticked ? 'rgba(124, 58, 237, 0.08)' : 'transparent',
                            borderRadius: 6,
                            fontSize: 13,
                            whiteSpace: 'nowrap'
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={ticked}
                            onChange={() => togglePrintStudent(student.id)}
                            style={{ width: 16, height: 16, cursor: 'pointer', flexShrink: 0 }}
                          />
                          <span>{student.student_name}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
              {showPrintLaterList && (
                <div
                  style={{
                    position: 'absolute',
                    top: 78,
                    left: 16,
                    width: 'max-content',
                    maxWidth: 'calc(100% - 32px)',
                    zIndex: 30,
                    background: '#fff',
                    color: '#111827',
                    borderRadius: 8,
                    boxShadow: '0 16px 40px rgba(0,0,0,0.35)',
                    border: '1px solid #E5E7EB'
                  }}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 12px',
                    borderBottom: '1px solid #E5E7EB',
                    background: '#fff',
                    fontSize: 13,
                    fontWeight: 700
                  }}>
                    <input
                      type="checkbox"
                      checked={laterRoster.length > 0 && laterChosen === laterRoster.length}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedPrintIds(new Set(laterRoster.map((student) => String(student.id))));
                        } else {
                          setSelectedPrintIds((prev) => {
                            const next = new Set(prev);
                            for (const student of laterRoster) next.delete(String(student.id));
                            return next;
                          });
                        }
                      }}
                      title="Tick every student graded later"
                      style={{ width: 16, height: 16, cursor: 'pointer' }}
                    />
                    <span style={{ flex: 1 }}>Graded later</span>
                    <span style={{ fontWeight: 600, color: '#6B7280' }}>
                      {laterRoster.length
                        ? (laterChosen ? `${laterChosen} ticked` : 'None ticked prints all')
                        : 'None after the class'}
                    </span>
                  </div>
                  {laterRoster.length === 0 ? (
                    <div style={{ padding: '14px 16px', fontSize: 13, color: '#6B7280' }}>
                      No students were graded after the class.
                    </div>
                  ) : (
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: `repeat(${Math.max(1, Math.ceil(laterRoster.length / 8))}, max-content)`,
                      gap: 4,
                      padding: 8
                    }}>
                      {laterRoster.map((student) => {
                        const ticked = selectedPrintIds.has(String(student.id));
                        return (
                          <label
                            key={student.id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              padding: '6px 8px',
                              cursor: 'pointer',
                              background: ticked ? 'rgba(15, 118, 110, 0.1)' : 'transparent',
                              borderRadius: 6,
                              fontSize: 13,
                              whiteSpace: 'nowrap'
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={ticked}
                              onChange={() => togglePrintStudent(student.id)}
                              style={{ width: 16, height: 16, cursor: 'pointer', flexShrink: 0 }}
                            />
                            <span>{student.student_name}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
              {/* Pages — front & back side by side. Table rows are split in
                  half between the two sides so a double-sided print uses
                  paper efficiently. */}
              <div style={{ flex: 1, overflowY: 'auto', padding: '12px', display: 'flex', flexDirection: 'column', gap: '18px', alignItems: 'center' }}>
                {!ready && (
                  <div style={{ padding: '48px 16px', color: '#374151', fontWeight: 700, fontSize: '15px' }}>
                    Fitting pages to A4…
                  </div>
                )}
                {groups.map((group, gIdx) => {
                  const startNum = groups.slice(0, gIdx).reduce((n, g) => n + g.length, 0) + 1;
                  const endNum = Math.min(startNum + group.length - 1, totalStudents);
                  const frontId = `front-${gIdx}`;
                  const backId = `back-${gIdx}`;
                  const labelStyle = (id) => ({
                    fontSize: '11px', fontWeight: '700',
                    color: (!packPending && overflowInfo.pages.has(id)) ? '#EF4444' : '#6B7280',
                    marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'center'
                  });
                  const frontPt = getFrontPt(gIdx);
                  const backPt  = getBackPt(gIdx);
                  const fontSelStyle = {
                    width: '54px',
                    padding: '3px 4px',
                    border: '1px solid #9CA3AF',
                    borderRadius: '4px',
                    fontSize: '11px',
                    textAlign: 'center',
                    background: 'white',
                    cursor: 'pointer'
                  };
                  return (
                    <div key={gIdx} style={{ display: 'flex', gap: '24px', justifyContent: 'center', flexWrap: 'wrap' }}>
                      <div>
                        <div style={{ marginBottom: '8px', textAlign: 'center', display: 'flex', justifyContent: 'center', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                          <span style={{ ...labelStyle(frontId), marginBottom: 0 }}>
                            Page {gIdx * 2 + 1} — Front ({startNum}–{endNum})
                            {(!packPending && overflowInfo.pages.has(frontId)) && ' ⚠'}
                          </span>
                          <label style={{ fontSize: '11px', color: '#6B7280' }}>Students:</label>
                          <input
                            type="number"
                            min="1"
                            max="50"
                            value={pageOverrides[gIdx] != null ? pageOverrides[gIdx] : group.length}
                            title={pageOverrides[gIdx] != null
                              ? 'Locked count for this sheet. Students after it reflow automatically.'
                              : 'Auto-fit. Edit to lock how many students this sheet holds.'}
                            onChange={(e) => {
                              const n = Math.max(1, Math.min(50, parseInt(e.target.value, 10) || 1));
                              const next = { ...pageOverrides, [gIdx]: n };
                              setPageOverrides(next);
                              runPackRef.current(gIdx, { locks: next }).then(applyPackedSizes);
                            }}
                            style={{
                              width: '52px',
                              padding: '3px 5px',
                              border: '1px solid #9CA3AF',
                              borderRadius: '4px',
                              fontSize: '11px',
                              textAlign: 'center',
                              background: 'white'
                            }}
                          />
                          <label style={{ fontSize: '11px', color: '#6B7280' }}>Font (pt):</label>
                          <select
                            value={frontPt}
                            onChange={(e) => {
                              const pt = parseInt(e.target.value, 10);
                              const next = { ...pairFontSize, [gIdx]: pt };
                              setPairFontSize(next);
                              runPackRef.current(gIdx, { pairFontSize: next }).then(applyPackedSizes);
                            }}
                            style={fontSelStyle}
                            title="Font size for this front page (back inherits unless overridden separately)"
                          >
                            {FONT_SIZE_OPTIONS.map(opt => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                          <label style={{ fontSize: '11px', color: '#6B7280' }}>Gap (px):</label>
                          <input
                            type="number"
                            min="0"
                            max="120"
                            value={pageGap[gIdx] != null ? pageGap[gIdx] : tableGap}
                            onChange={(e) => setSheetGap(gIdx, e.target.value)}
                            title="Space after each student on this sheet. The even page uses the same gap."
                            style={{
                              width: '52px',
                              padding: '3px 5px',
                              border: '1px solid #9CA3AF',
                              borderRadius: '4px',
                              fontSize: '11px',
                              textAlign: 'center',
                              background: 'white'
                            }}
                          />
                        </div>
                        {pageCard(frontId, group, 'front', frontPt, gIdx)}
                      </div>
                      <div>
                        {/* Two font-size selects above the BACK page: the
                            LEFT one mirrors the front's size (changing it
                            updates the front pair too); the RIGHT one is
                            the back's own override (defaults to "follow
                            front" represented by the matching front size). */}
                        <div style={{ marginBottom: '8px', textAlign: 'center', display: 'flex', justifyContent: 'center', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                          <span style={{ ...labelStyle(backId), marginBottom: 0 }}>
                            Page {gIdx * 2 + 2} — Back ({startNum}–{endNum})
                            {(!packPending && overflowInfo.pages.has(backId)) && ' ⚠'}
                          </span>
                          <label style={{ fontSize: '11px', color: '#6B7280' }}>Front (pt):</label>
                          <select
                            value={frontPt}
                            onChange={(e) => {
                              const pt = parseInt(e.target.value, 10);
                              const next = { ...pairFontSize, [gIdx]: pt };
                              setPairFontSize(next);
                              runPackRef.current(gIdx, { pairFontSize: next }).then(applyPackedSizes);
                            }}
                            style={fontSelStyle}
                            title="Mirrors the front-page font size for this pair"
                          >
                            {FONT_SIZE_OPTIONS.map(opt => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                          <label style={{ fontSize: '11px', color: '#6B7280' }}>Back (pt):</label>
                          <select
                            value={backPt}
                            onChange={(e) => {
                              const v = parseInt(e.target.value, 10);
                              const next = { ...backFontOverride };
                              if (v === frontPt) delete next[gIdx];
                              else next[gIdx] = v;
                              setBackFontOverride(next);
                              runPackRef.current(gIdx, { backFontOverride: next }).then(applyPackedSizes);
                            }}
                            style={fontSelStyle}
                            title="Override font size for this back page only (set to the same value as Front to re-sync)"
                          >
                            {FONT_SIZE_OPTIONS.map(opt => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        </div>
                        {pageCard(backId, group, 'back', backPt, gIdx)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Preview Loading Indicator */}
      {previewLoading && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
        >
          <div style={{ color: 'white', fontSize: '18px' }}>
            <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '10px' }} />
            Loading preview...
          </div>
        </div>
      )}

      {/* Re-check / Feedback — full-page split: left info, right big image */}
      {recheckModal && (
        <div
          className="recheck-request-page"
          onPaste={(e) => {
            if (applyRecheckClipboardImage(e.clipboardData)) {
              e.preventDefault();
            }
          }}
        >
          <div className="recheck-request-left">
            <div className="recheck-request-left-header">
              <button type="button" className="recheck-request-back" onClick={closeRecheckModal}>
                <FontAwesomeIcon icon={faArrowLeft} />
                Cancel
              </button>
              <h2>
                {recheckModal.kind === 'feedback' ? (
                  <>
                    <FontAwesomeIcon icon={faCommentDots} style={{ color: '#0D9488' }} />
                    Feedback
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faRotateRight} style={{ color: '#D97706' }} />
                    Request Re-check
                  </>
                )}
              </h2>
            </div>

            <div className="recheck-request-scroll">
              <div className="recheck-request-student">
                <div className="recheck-request-student-name">
                  {recheckModal.student.student_name}
                </div>
                <div className="recheck-request-student-meta">
                  <span>
                    <strong>Marks:</strong>{' '}
                    {recheckModal.student.marks != null && recheckModal.student.marks !== ''
                      ? recheckModal.student.marks
                      : '-'}
                  </span>
                  {folder.class && <span><strong>Class:</strong> {folder.class}</span>}
                  {folder.subject && <span><strong>Subject:</strong> {folder.subject}</span>}
                </div>
              </div>

              {error && (
                <div
                  className="recheck-request-error"
                  onClick={() => setError('')}
                >
                  {error}
                </div>
              )}

              <label className="recheck-request-label">
                {recheckModal.kind === 'feedback' ? (
                  <>
                    Note <span className="hint">(optional — shown with the images)</span>
                  </>
                ) : (
                  <>
                    What is the problem? <span className="req">*</span>
                    <span className="hint">(type and/or record audio)</span>
                  </>
                )}
              </label>
              <textarea
                value={recheckExplanation}
                onChange={(e) => setRecheckExplanation(e.target.value)}
                placeholder={recheckModal.kind === 'feedback'
                  ? "e.g. 'Q.2 — missed the unit. Look at the boxed step.'"
                  : "Type the problem, e.g. 'Q.2 answer is correct but was marked wrong' — or record a voice note below instead..."}
                rows={4}
                className="recheck-request-textarea"
              />

              {recheckModal.kind !== 'feedback' && (
              <div className={`recheck-request-audio ${recheckAudio || recheckRecording ? 'active' : ''}`}>
                <div className="recheck-request-audio-actions">
                  {!recheckRecording ? (
                    <button
                      type="button"
                      className="recheck-btn-record"
                      onClick={startRecheckRecording}
                      disabled={recheckSubmitting || recheckMicBusy}
                    >
                      <FontAwesomeIcon icon={recheckMicBusy ? faSpinner : faMicrophone} spin={recheckMicBusy} />
                      {recheckMicBusy
                        ? 'Requesting mic…'
                        : (recheckAudio ? 'Re-record voice note' : 'Record voice note')}
                    </button>
                  ) : (
                    <button type="button" className="recheck-btn-stop" onClick={stopRecheckRecording}>
                      <FontAwesomeIcon icon={faStop} />
                      Stop ({Math.floor(recheckRecordSecs / 60)}:{String(recheckRecordSecs % 60).padStart(2, '0')})
                    </button>
                  )}
                  <label className="recheck-btn-upload">
                    <FontAwesomeIcon icon={faPaperclip} />
                    Upload audio
                    <input
                      type="file"
                      accept="audio/*,.webm,.mp3,.wav,.m4a,.ogg,.aac"
                      disabled={recheckSubmitting || recheckRecording}
                      onChange={(ev) => {
                        const f = ev.target.files && ev.target.files[0];
                        if (f) setRecheckAudioFile(f);
                        ev.target.value = '';
                      }}
                      style={{ display: 'none' }}
                    />
                  </label>
                  {recheckRecording && (
                    <span className="recheck-recording-hint">Recording… speak clearly, then press Stop</span>
                  )}
                  {recheckAudio && !recheckRecording && (
                    <button type="button" className="recheck-btn-remove" onClick={clearRecheckAudio}>
                      <FontAwesomeIcon icon={faTimes} />
                      Remove audio
                    </button>
                  )}
                </div>
                {recheckAudioError && (
                  <div className="recheck-request-error" style={{ marginTop: 10 }}>{recheckAudioError}</div>
                )}
                {recheckAudioUrl && !recheckRecording && (
                  <audio controls src={recheckAudioUrl} style={{ width: '100%', marginTop: 10 }} />
                )}
                {!recheckAudio && !recheckRecording && !recheckAudioError && (
                  <div className="recheck-muted">
                    Record live, or upload an audio file. Optional if you already typed above.
                  </div>
                )}
              </div>
              )}

              <label className="recheck-request-label" style={{ marginTop: 16 }}>
                {recheckModal.kind === 'feedback' ? 'Annotated screenshot(s)' : 'Problem screenshot(s)'} <span className="req">*</span>
              </label>
              <div className={`recheck-request-image-tools ${recheckFiles.length ? 'has-file' : ''}`}>
                <div className="recheck-request-audio-actions">
                  <button
                    type="button"
                    className="recheck-btn-upload"
                    disabled={screenshotCapturing}
                    onClick={() => captureFastGraderScreenshot(recheckModal.student)}
                    title={recheckModal.kind === 'feedback'
                      ? 'Capture FastGrader, annotate, then Save for Feedback'
                      : 'Capture FastGrader, annotate, then Save for recheck'}
                  >
                    <FontAwesomeIcon icon={screenshotCapturing ? faSpinner : faCamera} spin={screenshotCapturing} />
                    {screenshotCapturing ? 'Capturing…' : 'Screenshot'}
                  </button>
                  <label className="recheck-btn-upload">
                    <FontAwesomeIcon icon={faPaperclip} />
                    Add image…
                    <input
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                      onChange={handleRecheckFileChange}
                      style={{ display: 'none' }}
                    />
                  </label>
                  <div
                    tabIndex={0}
                    className="recheck-btn-paste"
                    title="Click here, then press Ctrl+V to paste a screenshot"
                    onClick={(e) => e.currentTarget.focus()}
                    onPaste={(e) => {
                      if (applyRecheckClipboardImage(e.clipboardData)) {
                        e.preventDefault();
                        e.stopPropagation();
                      }
                    }}
                  >
                    <FontAwesomeIcon icon={faPaste} />
                    Paste image (Ctrl+V)
                  </div>
                </div>
                <div className="recheck-muted" style={{ marginTop: 8 }}>
                  {recheckFiles.length
                    ? `${recheckFiles.length} image(s) attached — add more via Screenshot / paste / upload.`
                    : (recheckModal.kind === 'feedback'
                      ? 'Use Screenshot (annotate + Save), or paste / upload. Preview on the right.'
                      : 'Use Screenshot (annotate + Save), or paste / upload images. Preview on the right.')}
                </div>
                {recheckFiles.length > 0 && (
                  <div className="recheck-thumb-row">
                    {recheckFiles.map((item, idx) => (
                      <div key={item.previewUrl || idx} className="recheck-thumb-card">
                        <img src={item.previewUrl} alt={item.file?.name || `Image ${idx + 1}`} />
                        <button
                          type="button"
                          className="recheck-thumb-remove"
                          title="Remove this image"
                          onClick={() => removeRecheckFileAt(idx)}
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                        <span className="recheck-thumb-label">{idx + 1}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="recheck-request-footer">
              <button
                type="button"
                className="recheck-btn-cancel"
                onClick={closeRecheckModal}
                disabled={recheckSubmitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="recheck-btn-submit"
                onClick={recheckModal.kind === 'feedback' ? submitFeedback : submitRecheckRequest}
                disabled={
                  recheckSubmitting
                  || (recheckModal.kind !== 'feedback' && recheckRecording)
                  || recheckFiles.length === 0
                  || (recheckModal.kind !== 'feedback' && !recheckExplanation.trim() && !recheckAudio)
                }
                style={recheckModal.kind === 'feedback'
                  ? { background: 'linear-gradient(135deg, #14B8A6, #0D9488)' }
                  : undefined}
              >
                {recheckSubmitting ? (
                  <><FontAwesomeIcon icon={faSpinner} spin /> {recheckModal.kind === 'feedback' ? 'Sending…' : 'Submitting...'}</>
                ) : recheckModal.kind === 'feedback' ? (
                  <><FontAwesomeIcon icon={faCommentDots} /> Feedback</>
                ) : (
                  <><FontAwesomeIcon icon={faRotateRight} /> Submit Request</>
                )}
              </button>
            </div>
          </div>

          <div className="recheck-request-right">
            <div className="recheck-request-right-header">
              <FontAwesomeIcon icon={faImage} style={{ color: '#60A5FA' }} />
              <span>
                {recheckFiles.length
                  ? `${recheckFiles.length} screenshot${recheckFiles.length > 1 ? 's' : ''}`
                  : (recheckModal.kind === 'feedback' ? 'Annotated image preview' : 'Problem image preview')}
              </span>
            </div>
            <div className="recheck-request-image-stage">
              {recheckFiles.length > 0 ? (
                <div className="recheck-preview-stack">
                  {recheckFiles.map((item, idx) => (
                    <div key={item.previewUrl || idx} className="recheck-preview-stack-item">
                      <div className="recheck-preview-stack-cap">
                        <span>Image {idx + 1} of {recheckFiles.length}</span>
                        <span className="recheck-preview-stack-hint">Click to zoom</span>
                      </div>
                      <img
                        src={item.previewUrl}
                        alt={item.file?.name || `Problem ${idx + 1}`}
                        className="recheck-preview-stack-img"
                        onClick={() => setPreviewLightbox({
                          url: item.previewUrl,
                          name: item.file?.name || `Problem ${idx + 1}`
                        })}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="recheck-request-image-empty">
                  <FontAwesomeIcon icon={faImage} style={{ fontSize: 42, opacity: 0.45, marginBottom: 12 }} />
                  <div>No image yet</div>
                  <div style={{ fontSize: 13, opacity: 0.7, marginTop: 6 }}>
                    Screenshot → annotate → Save, or paste / upload — images appear here.
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {screenshotAnnotator && (
        <div
          data-recheck-annotator="1"
          data-html2canvas-ignore="true"
          className="app-annotator-overlay"
        >
          <div style={{ position: 'relative', width: '100%', height: '100%' }}>
            <AnnotatableImage
              url={screenshotAnnotator.url}
              alt={`recheck_${screenshotAnnotator.student?.id || 'shot'}`}
              width="100%"
              height="100%"
              background="#0a0a0a"
              onSaveForRecheck={saveAnnotatedForRecheck}
              saveButtonLabel="Save"
            />
            <button
              type="button"
              onClick={closeScreenshotAnnotator}
              title="Close annotator"
              aria-label="Close annotator"
              style={{
                position: 'absolute',
                top: 10,
                right: 10,
                zIndex: 20,
                width: 38,
                height: 38,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
                background: 'rgba(220,38,38,0.95)',
                color: 'white',
                border: '2px solid rgba(255,255,255,0.9)',
                borderRadius: '50%',
                boxShadow: '0 3px 12px rgba(0,0,0,0.45)',
                cursor: 'pointer',
                fontSize: 18
              }}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </div>
      )}

      {previewLightbox && (
        <div
          data-html2canvas-ignore="true"
          onClick={() => setPreviewLightbox(null)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 12500,
            background: 'rgba(0,0,0,0.88)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
            gap: 10
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 'min(96vw, 1300px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              color: 'white',
              gap: 12
            }}
          >
            <div style={{ fontWeight: 700, fontSize: 14 }}>{previewLightbox.name}</div>
            <button
              type="button"
              onClick={() => setPreviewLightbox(null)}
              style={{
                padding: '8px 14px',
                background: '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                fontWeight: 700,
                cursor: 'pointer'
              }}
            >
              <FontAwesomeIcon icon={faTimes} /> Close
            </button>
          </div>
          <div onClick={(e) => e.stopPropagation()}>
            <ZoomableImage
              url={previewLightbox.url}
              alt={previewLightbox.name}
              width="min(96vw, 1300px)"
              height="min(84vh, 860px)"
              background="#0a0a0a"
            />
          </div>
        </div>
      )}
    </div>
  );
}
